/**
 * Explorations, kept.
 *
 * Everything here is a pure codec plus localStorage I/O. It deliberately does
 * not import the store at runtime — the store reads this module while it is
 * being evaluated, and a real cycle would resolve to `undefined` in a way that
 * fails silently on the very first boot.
 *
 * The rule the whole file is built around: a document read off disk is
 * untrusted. Every enum is validated, every dangling edge is dropped, and a
 * document written by a newer build is refused rather than guessed at.
 *
 * WHERE THINGS LIVE. The documents are in IndexedDB, mirrored in memory by
 * `core/docStore.ts` so that every reader here stays synchronous; the index —
 * one slim row per exploration, what the sidebar lists — stays in localStorage
 * with the preferences and the boot pointer, because a list drawn at render
 * time has to be answerable without awaiting anything. A document goes into the
 * mirror through `core/pack.ts`, deflated and spread over fifteen-bit code units,
 * so a library of large explorations costs a fifth of its plain weight in memory
 * and on disk; the envelope is not the document, `SCHEMA` is untouched, and a
 * plain value written by an earlier build reads as it always did. There is no
 * budget and no eviction: the origin's own quota is the only bound, and
 * `hydrateStorage` is where a library an earlier build left in localStorage —
 * or in the archive tier D33 kept beside it — is carried across, once.
 */

import {
  conjectureResultsOf,
  trackRecord,
  type ConjectureResult,
  type TrackRecordRow,
} from './trackRecord'
import { selectConnectionCandidates } from './connectionCandidates'
import type { InvestigationAgenda } from './agenda'
import type { RunAssessmentWorkspace } from './runAssessment'
import type { ExperimentWorkspaceState } from './experiments'
import { textRetention, RESEARCH_ITEM_CHARS, SOURCE_QUOTE_CHARS } from './textRetention'
import { parseResearchContract, researchContractBlock } from './researchContract'
import { AUDIT_SCOPES, AUDIT_VERDICTS, parseAuditCoverage } from './auditReview'
import type {
  Bridge,
  BridgeEnd,
  ConjectureAdmission,
  ConjectureRecord,
  ConjectureRegistration,
  CorpusEvidence,
  DocNotice,
  FieldSurvey,
  Filters,
  ForkOrigin,
  ForeignRef,
  JoiningSource,
  Lens,
  LogicalOpKind,
  ModelCallJsonValue,
  ModelCallReceipt,
  ModelStamp,
  MoveReason,
  NodeElaboration,
  Op,
  Outcome,
  Audit,
  PanelCheck,
  PanelVerdict,
  PanelVote,
  PassageCite,
  PriorArt,
  PriorArtBackstopHit,
  PriorArtEntry,
  PriorArtSearch,
  FieldSweep,
  SweepItem,
  PortfolioRecord,
  NodeKind,
  NodeOrigin,
  ReasonNode,
  ReasonedEndingCounts,
  RunVerb,
  Run,
  RunMove,
  RunStop,
  Session,
  SharedCopy,
  SourceDesign,
  SourceReading,
  Spend,
  SpendDetail,
  Stance,
  Directive,
  FormerDirective,
  EvidenceRebase,
  StoredLinkage,
  SurveyPart,
  SurveyRow,
  SurveySource,
  Temper,
  UntilCondition,
  Verification,
  VerificationArchiveEntry,
  VerifyScope,
  WebSource,
  BridgeBasis,
  BridgeRung,
  IndexLinkage,
  Programme,
  ProgrammeStep,
} from './types'
import {
  addSpendRow,
  auditableByProvider,
  BRIDGE_RUNGS,
  DEFAULT_FILTERS,
  kindChannelOf,
  MODEL_CALL_RECEIPT_VERSION,
  liveChannelOf,
  OP_HINT,
  OP_LABEL,
  ownEntry,
  pairKey,
  panelLive,
  passageRef,
  READING_LABEL,
  SOURCE_DESIGNS,
  storableId,
  projectLabel,
  refutedIsEarned,
  verificationLive,
} from './types'
// TYPE-ONLY, like `RightTab` below. A stored stamp is validated as bounded text
// and never against the registry — see `asModelStamp`.
import {
  flushForgotten,
  forgottenElsewhere,
  keepWorkingFrom,
  markForgotten,
  markMoved,
  markNow,
  noteWorkingFrom,
  onForgottenElsewhere,
} from './forgotten'
import type { ProviderId } from './models'
import type { SemanticProfile } from './semanticGit'
import { sortBridges } from './rung'
import type { Weighting } from './weight'
import { makeNode } from './session'
import {
  CONJECTURE_MAX_ANCHORS,
  CONJECTURE_MAX_LEADS,
  CONJECTURE_MOVES,
  predictionDiscriminates,
} from './conjecture'
import {
  PRIOR_ART_HISTORY,
  PRIOR_ART_MAX_ENTRIES,
  PRIOR_ART_MAX_HITS,
  regatePriorArt,
} from './priorArt'
import { SWEEP_HISTORY, SWEEP_KINDS, SWEEP_MAX_ITEMS } from './sweep'
import { clampStretch } from './layout'
import { clamp } from './geometry'
import { LINK_READINGS } from './link'
import { liveOrigin } from './sources'
import { panelSplit, panelStability, type ModelStability } from './panel'
import { calibrate, type ModelCalibration } from './calibration'
import { recordDay } from './recordDigest'
import { DIRECTIVE_CHARS, MAX_BRIEF, MAX_FORMER } from './brief'
import { canonicalUrl, isHttpUrl } from './url'
import { packText, unpackText } from './pack'
import { parsePinned, serialisePinned, withPinned, withoutPinned } from './pins'
import { parseFolded } from './projects'
import type { ForgetAllOutcome, ForgetOutcome } from './forget'
import { parseOpened } from './unread'
import { nameFor } from './name'
import {
  clearDocs,
  cacheDocText,
  documentBackend,
  setStorageHydrationFailure,
  deleteDocText,
  docIds,
  docVersion,
  dropDocText,
  hydrateDocs,
  readDocText,
  refreshDoc,
  repackDocText,
  shelfOpen,
  unwrapDoc,
  writeDocText,
  type DocBackend,
} from './docStore'
import { DEFAULT_TEMPO, TEMPOS } from './tempo'
import { mergeVerificationArchive, mergeVerificationEvents, verificationEvents } from './rebase'
import {
  SURVEY_PARTS,
  SURVEY_PART_CAP,
  SURVEY_QUERY_CAP,
  SURVEY_ROW_REFS,
  SURVEY_SOURCE_CAP,
  SURVEY_TEXT_CHARS,
  mergeSurveyEvents,
} from './survey'
import {
  createResearchState,
  parseResearchStateSync,
  parseFrozenEvidenceProfile,
  pruneManualEvidenceBindingsForNodes,
} from './research'
import { canonicalSemanticJson } from './semanticGit'
// TYPE-ONLY, and it has to stay that way: `verbatimModuleSyntax` erases this
// line entirely, so there is no runtime import and no cycle.
import type { RightTab } from './store'

/**
 * The DOCUMENT's version, and only the document's. The envelope a document is
 * stored in carries its own tag — `PACK_TAG` in `core/pack.ts` — so storing a
 * value differently is not a schema change: nothing in this number says how the
 * bytes sit in the key, and a reader accepts the plain and packed forms alike.
 */
export const SCHEMA = 16
/** The pristine demo has a fixed id, so replaying it overwrites only itself. */
export const DEMO_SESSION_ID = 's_demo'
/**
 * The worked example's seed. Here beside its id rather than with the rest of the
 * demo in `engine/demo.ts`, because the codec has to recognise the copies an
 * earlier build committed under other ids (`isDemoPhantom`) and must not import
 * the engine to do it.
 */
export const DEMO_SEED = 'It is possible to build an antigravity device'

/**
 * Recent unreferenced operations retained per document. The ledger is a ledger;
 * the graph is the artefact, which is why node stamps and run records are written
 * independently of it. A completed rebase op still named by a node and the
 * source-check op it reviewed are retained in addition: their order is the
 * durable proof that an old receipt was not rebound to a later evidence event.
 * Run moves and agenda attempts also retain their named operations, including
 * attached research analyses, so preserving an account cannot erase its receipts.
 *
 * EXPORTED because the run manifest names this number in the sentence that says
 * what its `opsMayBeTruncated` flag means. A cap a document declares and a cap
 * the code applies must be one number — see `core/manifest.ts`.
 */
export const MAX_OPS = 200
/** How many explorations one scan can claim to have read. A stored count, so it
 *  is bounded like every other number off disk — there is no library ceiling to
 *  bound it by any more, and a scan reads a handful in any case. */
const MAX_READ_EXPLORATIONS = 1000
/** Produced ids one retained operation can account for, packet or sequential. */
const MAX_OP_PRODUCED = 40
/** Candidate syntheses kept per document, highest rung first — `sortBridges`, never
 *  the score. Unbounded, a long session of scans is the one part of a document that
 *  can grow without limit. */
const MAX_BRIDGES = 40
/** On-disk trace cap. The in-memory one is larger; this is a storage number. */
const DISK_THINKING_CAP = 2000
/** More than the largest current multi-call op (a three-gather link plus reasoning). */
const MAX_MODEL_CALLS_PER_OP = 12
/** Exact receipts above this boundary are omitted whole, never clipped and called exact. */
const MODEL_CALL_RECEIPT_CHARS = 600_000
const MODEL_CALL_RECEIPT_JSON_NODES = 100_000

/**
 * How old a restored node's clock is forced to be.
 *
 * The longest possible growth is the full phase total at the slowest tempo,
 * plus one pulse. A restored exploration is finished reasoning, not arriving
 * reasoning — it should be on screen, consolidated, on the first frame.
 * Exported for the demo's boot repair, whose restored nodes are the same case.
 */
export const SETTLED_MS = 20_000

interface StoredClock {
  /** This device's read time. */
  now: number
  /** How far the writing device's save clock was ahead, never negative. */
  offset: number
}

/**
 * One document-wide clock translation.
 *
 * Clamping every future field independently collapses a valid sequence such as
 * check → rebase → recheck onto one millisecond. The envelope's `savedAt` comes
 * from the same writer as those fields, so one shared offset preserves their
 * differences while still refusing to restore a time beyond this device's now.
 *
 * For EVENT times only — the clocks a document compares with each other. The
 * result is a function of `Date.now()`, so the same bytes read a millisecond
 * apart give two values whenever the writing clock was ahead of this one. That
 * is harmless inside one read and fatal for a value compared with itself across
 * reads; the one such value goes through `storedIdentity` instead.
 */
const storedTime = (value: unknown, clock: StoredClock, fallback = clock.now): number => {
  const raw = Number(value)
  if (!Number.isFinite(raw) || raw <= 0) return fallback
  return Math.min(raw - clock.offset, clock.now)
}

/**
 * A stored time that is an IDENTITY rather than an event: read as written.
 *
 * `session.createdAt` is what a payer carries to name the document it paid for,
 * and `spendGoesTo` and `recordSpendOn` compare it for equality across two
 * reads — one when the question was asked, one when the answer lands. Through
 * `storedTime` that comparison held only while the offset had not moved, which
 * on a save clock ahead of this device is one millisecond: the offset shrinks as
 * `readAt` advances, `createdAt` came back as `raw - anchor + readAt`, the two
 * reads disagreed by the time between them, and the receipt was refused as
 * another document's — the ledger reading LOW, silently. Not clamped either: a
 * clamp to `now` is a function of the clock too, and moves for the same reason.
 * Model stamps are read as written in the same spirit. See D79.
 */
const storedIdentity = (value: unknown, fallback: number): number => {
  const raw = Number(value)
  return Number.isFinite(raw) && raw > 0 ? raw : fallback
}

/* A forget observed in another tab — `core/forgotten.ts` sees the marker land
   and says which id, or null for everything — takes the mirror's copy with it.
   The shelf was that tab's to clear and it has; what this tab holds is a copy
   that would otherwise outlive the document, unreachable from the list but one
   `loadSession` away from being written back. */
onForgottenElsewhere((id) => dropDocText(id))

const K = {
  index: 'nv.index',
  prefs: 'nv.prefs',
  /**
   * The pinned explorations, as a list of ids, newest pin first. `core/pins.ts`
   * says why a pin is a fact about this device's list rather than about a
   * document. HERE, in the codec's table, and not in the store's beside the
   * keys where the declared projects live: the one thing that must take a pin
   * down is the door that takes the exploration down, and that door is in this
   * file — `forgetSession` drops the id and `forgetAll` drops the slot, so no
   * surface with a Forget on it has to remember to. Not in `nv.prefs` either:
   * that blob is discarded whole on a parse failure, and a pin is a choice
   * somebody made about one exploration, not taste.
   */
  pins: 'nv.pins',
  last: 'nv.last',
  /** The pagehide net — see `writeDoc`. One key, the open document only. */
  ckpt: 'nv.ckpt',
  /** Where an earlier build kept a document. Read at boot by `hydrateStorage`,
   *  which moves it onto the shelf and removes it; never written. */
  doc: (id: string) => `nv.s.${id}`,
}
/** The archive tier's keys (D33), all of them: its manifest rows, its tombstones
 *  and its legacy single key. Read at boot, carried across, removed. */
const ARCHIVE_PREFIX = 'nv.arch'

export interface StoredSession {
  v: number
  savedAt: number
  /** Wall-clock coordinate used by timestamps inside `session`. Unlike
   * `savedAt`, this is never advanced merely to win a version race. */
  clockAtSave?: number
  session: Session
  ui: { selectedId: string | null; focusId: string | null; lens: Lens; temper: Temper }
}

export interface RecentEntry {
  id: string
  seed: string
  nodes: number
  insights: number
  contradictions: number
  savedAt: number
  chars: number
  demo: boolean
  /** Recorded results this exploration holds. The one thing in a document that
   *  cannot be re-obtained by paying for another pass, so the budget takes it
   *  last. Optional because an index written by the previous build has none. */
  outcomes?: number
  /** Which project the document is filed under, when it has one. DERIVED — the
   *  label's home is `Session.project` — and copied here so that grouping the
   *  library never parses a document: the sidebar regroups on every save and
   *  the search sheet on every keystroke. Optional like `outcomes`, because an
   *  index written by the previous build has none. */
  project?: string
  /** The name somebody gave the exploration, when they gave one. DERIVED like
   *  `project` — the name's home is `Session.name` — and copied here for the same
   *  reason: every list that names a row reads the index, never a document. */
  name?: string
  /** Where the exploration was forked from, when it was. DERIVED like `name`, so
   *  the line under a forked row's name costs no parse either. */
  forkedFrom?: ForkOrigin
  /** When the LATEST run in the document finished, and absent when it has not
   *  — no run yet, or the last one died with the tab (`Run.finishedAt` is
   *  absent then, and the absence is the information). DERIVED like `project`,
   *  so the mark beside a row can turn without parsing a document (D103, D334). The
   *  latest run only: an exploration being run again is one whose most recent
   *  run has not finished, whatever an earlier one did. */
  finishedRunAt?: number
  /** A copy opened from a share link and not yet continued — `SharedCopy`'s
   *  mark, copied here like `project` so the sidebar can tag the row without
   *  parsing a document. Absent once the reader presses Continue working. */
  shared?: true
  /** Nodes carrying a `resolvesBy` date — promises made, kept or not. DERIVED
   *  like `outcomes`, and for the reader `outcomes` serves: the due list, mounted
   *  in the sidebar, used to parse every stored exploration on its first render
   *  to find the few that hold a date. Optional, and an absence falls through to
   *  a read, because an index written by an earlier build has none. */
  promises?: number
  /** Which `summarise` wrote this row: `INDEX_ROW_VERSION` when it was this one.
   *  A boot adopts a stored row instead of decoding its document only when this
   *  matches, so a row an earlier build wrote — fields missing, or counted under
   *  other rules — is summarised again rather than believed. */
  rowVersion?: number
}

export interface Prefs {
  v: number
  lens: Lens
  temper: Temper
  tempo: number
  filters: Filters
  railRight: boolean
  rightTab: RightTab
  /** Read only the user's own documents when they match. See the store. */
  preferCorpus: boolean
  /** Whether the splash has ever been dismissed. In `Prefs` rather than a key of
   *  its own so one "forget my preferences" gesture takes it with everything
   *  else. The SELECTED MODEL is deliberately not here — see `nv.model` in the
   *  store: `loadPrefs` returns null for the whole blob on a parse failure, which
   *  for a lens is a taste reset and for the model is a silent change in what a
   *  source check can verify. */
  splashSeen: boolean
  /** Which canvas the workspace is on. Persisted because a view is a way of
   *  working rather than a momentary state — but it defaults to the flat one on
   *  anything unrecognised, which is also what a corrupted blob resets to. */
  viewMode: '2d' | '3d'
  /** What sizes a claim on the canvas: the model's own ratings, or the records
   *  about it. A way of reading, so it persists like `viewMode`, and it defaults
   *  to the model's for the reason `viewMode` defaults to flat. See `core/weight.ts`. */
  weighting: Weighting
  /**
   * Whether the instruments are on screen: the research workspace, the several-
   * passes tier on a claim, the compiled diagnostics, and the three heaviest
   * exports. See the store's `instruments`.
   *
   * A workspace that has seen the splash keeps every instrument it already had,
   * so nobody's tools disappear on an update; a fresh one starts without them
   * and is told, in one line beside each place they would be, where the switch
   * is. That rule is in `loadPrefs` rather than here, because it reads a second
   * field to decide.
   */
  instruments: boolean
  /** The graph footer's three view switches (D369): the faint depth rings
   *  behind the graph, the dimming of every branch outside the selected
   *  claim's lineage and children, and the minimap. Taste, so in this blob;
   *  absent reads as the view the app drew before the switches existed —
   *  rings and minimap on, nothing dimmed. */
  showRings: boolean
  dimUnrelated: boolean
  showMinimap: boolean
  /** Whether the explorations sidebar is collapsed to its icon rail. Taste, so
   *  it belongs in this blob and not beside the keys: the worst a lost pref does
   *  here is open a panel the next press closes again. The profile NAME is a
   *  different class and lives in the store's own table — see `nv.profile.name`. */
  sidebarCollapsed: boolean
  /**
   * How wide the reader dragged the thread column, or null for "never said".
   *
   * The same class as `sidebarCollapsed` above and for the same reason: it is
   * TASTE. The worst a lost one does is open the column at its default width,
   * which is a width somebody can drag back in one gesture — nothing is
   * unreachable and nothing is destroyed. A number rather than a boolean is the
   * only new thing here, and NULL is load-bearing: it says "no setting" as
   * distinct from "a setting that happens to equal today's default", so a reader
   * who never touched the handle keeps following the stage-share default when
   * they move to a different monitor.
   *
   * Validated for its TYPE here and clamped for real at every read — see
   * `threadWidthFrom` in `ui/threadPane.ts`, which is where the window's own
   * limits are known. This file may not import that one (the codec knows nothing
   * about layout), which is exactly why it may not put a ceiling on the number
   * either: a ceiling here is a guess about a screen this module cannot see.
   */
  threadWidth: number | null
  /** How wide the reader dragged the explorations sidebar, or null for "never
   *  said" — `threadWidth`'s class, its decode and its clamp-on-read, for the
   *  other pane (`sidebarWidthFrom` in `ui/sidebarPane.ts`; D104). */
  sidebarWidth: number | null
  /**
   * Projects folded away under their headings in the explorations sidebar,
   * by label. Taste, in the same class as `sidebarCollapsed` and for the same
   * reason: the worst a lost write does is show a project's rows again.
   * `core/projects.ts` holds the reading and the cap (D101).
   */
  foldedProjects: string[]
  /**
   * When each exploration was last opened on this device, by id — what the
   * mark beside a row is measured against, so it can be an unread mark
   * (D103, D334). Taste, in the same class and for the same reason: the worst
   * a lost write does is show a dot filled again. `core/unread.ts` holds the
   * reading and the cap.
   */
  openedAt: Record<string, number>
}

/* ── validation ───────────────────────────────────────────────────────── */

/**
 * A stored column width, or null.
 *
 * Absence, a non-number and a non-finite one read as NO SETTING rather than as a
 * width — the same rule the rest of this file follows, where an unrecognised
 * value lands on the behaviour written against rather than on a guess.
 *
 * THERE IS NO UPPER BOUND, and the one that was here was a bug rather than
 * caution. It capped the stored number at 4000, and 4000 is reachable: a 5120
 * ultrawide with the sidebar at its rail leaves a legitimate maximum of 4648, so
 * a reader who dragged the column wide on that screen had it accepted, drawn,
 * and then rejected to null on the very next load. Written, honoured, forgotten
 * — which is precisely the "it forgets sometimes" failure the preference class
 * is written against.
 *
 * The floor stays, because a width below the pane's own is not a width anybody
 * could have set; anything above is a fact about somebody's monitor, and this
 * module has never seen one. The real bound is applied on every read, where the
 * window is known — so a number too big for today's screen is simply read as
 * today's maximum and is still there tomorrow.
 */
/** A pane's stored width — the thread column's or the sidebar's — validated
 *  for its TYPE and a band wide enough only to reject a hand-edited blob. The
 *  real clamp is a fact about the window the pane is on, applied on every read
 *  (`threadWidthFrom`, `sidebarWidthFrom`). */
function asPaneWidth(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null
  const w = Math.round(raw)
  return w >= 200 ? w : null
}

const LENS_OK = new Set<string>([
  'first-principles',
  'technical',
  'empirical',
  'economic',
  'adversarial',
  'systems',
  'historical',
])
const TEMPER_OK = new Set<string>(['grounded', 'balanced', 'speculative'])
const FILTER_KEYS = Object.keys(DEFAULT_FILTERS) as Array<keyof Filters>
const TEMPO_OK = new Set<number>(TEMPOS.map((t) => t.value))

/**
 * Filters, merged over the defaults rather than accepted or rejected whole.
 *
 * The old all-or-nothing check meant that adding one key to `Filters` silently
 * discarded every user's display preferences on their next load — the blob
 * written by the previous build lacks the new key, fails the check, and is
 * replaced by the defaults. Merging keeps what is there and defaults what is not.
 */
function readFilters(v: unknown): Filters {
  const out: Filters = { ...DEFAULT_FILTERS }
  if (!v || typeof v !== 'object') return out
  const o = v as Record<string, unknown>
  for (const k of FILTER_KEYS) if (typeof o[k] === 'boolean') out[k] = o[k]
  return out
}

const KIND_OK = new Set<string>([
  'seed',
  'mechanism',
  'evidence',
  'assumption',
  'implication',
  'contradiction',
  'gap',
  'analogy',
  'experiment',
  'insight',
  'synthesis',
  // Admitted only with its record: `asConjecture` below validates it, and
  // `makeNode` restores a `conjecture` kind with no valid record as ordinary.
  'conjecture',
])
/**
 * Derived, never hand-listed.
 *
 * A hand-written copy of this set is a double silent failure: a missed entry
 * drops the op from the ledger *and* makes `sanitizeSession` fall a node's
 * provenance back to `'manual'`, so an exported document then attributes the
 * node to nobody.
 */
const OP_OK = new Set<string>(Object.keys(OP_LABEL))
/** A portfolio is a physical pass, never a logical run move. */
const LOGICAL_OP_OK = new Set<string>([...OP_OK].filter((k) => k !== 'portfolio'))
/** Ordinary reasoning operations that may elaborate their one target. */
const DERIVE_OP_OK = new Set<string>(
  [...LOGICAL_OP_OK].filter(
    (k) =>
      k !== 'verify' &&
      k !== 'panel' &&
      k !== 'audit' &&
      k !== 'link' &&
      k !== 'research' &&
      k !== 'survey' &&
      k !== 'priorArt' &&
      k !== 'sweep' &&
      k !== 'plan' &&
      k !== 'sharpen',
  ),
)
/** Checks and the packet envelope itself cannot claim to have authored a node. */
const NODE_ORIGIN_OK = new Set<string>(
  [...LOGICAL_OP_OK].filter(
    (k) =>
      k !== 'verify' &&
      k !== 'link' &&
      k !== 'research' &&
      k !== 'survey' &&
      k !== 'priorArt' &&
      k !== 'sweep' &&
      k !== 'plan' &&
      k !== 'sharpen',
  ),
)
/** Derived from the exhaustive hint map so adding a seventh packet verb fails closed. */
const RUN_VERB_OK = new Set<string>(Object.keys(OP_HINT))
/** What a programme step may name: a run verb, or one of the three checks. */
const PROGRAMME_VERB_OK = new Set<string>([...RUN_VERB_OK, 'verify', 'panel', 'audit'])
/** Steps kept per programme. A model proposes a dozen; a document claiming more
 *  than twice that was hand-edited. */
const MAX_PROGRAMME_STEPS = 24
/** Typed edges kept per node, per relation. A person draws a few; a document
 *  claiming more was hand-edited. */
const MAX_EDGES = 8

const asLens = (v: unknown): Lens =>
  typeof v === 'string' && LENS_OK.has(v) ? (v as Lens) : 'first-principles'
const asTemper = (v: unknown): Temper =>
  typeof v === 'string' && TEMPER_OK.has(v) ? (v as Temper) : 'balanced'
const asId = (v: unknown): string | null => (typeof v === 'string' ? v : null)

/* ── the epistemic records, off disk ──────────────────────────────────────
   Each of these returns `undefined` for anything it cannot vouch for. A record
   that is half-validated is worse than no record: the UI would print a reading
   with no source behind it, which is the exact artefact the citation gate
   exists to prevent — arriving through the back door of a hand-edited
   localStorage key. */

const text = (v: unknown, max: number): string =>
  typeof v === 'string' ? v.trim().slice(0, max) : ''

const unit = (v: unknown): number | undefined => {
  const n = Number(v)
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : undefined
}

const strings = (v: unknown, cap: number, max: number): string[] =>
  Array.isArray(v)
    ? v
        .map((x) => text(x, max))
        .filter(Boolean)
        .slice(0, cap)
    : []

/**
 * The bound on a node's next steps, named because THREE readers apply it.
 *
 * `nextStepsOf` reads the elaboration's list where there is one and the base
 * list otherwise, so the two are one field to everything downstream and a bound
 * on only one of them is not a bound. The numbers are the ones `asElaboration`
 * has always used; naming them is what stops the base list and the elaboration's
 * from drifting apart under the accessor that cannot tell them apart.
 */
const NEXT_STEPS_CAP = 8
const NEXT_STEP_MAX = 500

/** `asElaboration`'s bound on the reasoning it holds, named for the same reason:
 *  `reasoningOf` reads the base kernel and the elaboration's as one body of
 *  text, so a bound on one of them is not a bound. */
const REASONING_MAX = 4000

/**
 * A string array with its ELEMENTS checked and no cap.
 *
 * For `evidence`, which the codec bounds NOWHERE — `makeNode` takes it as given
 * and nothing upstream of that trims it. The defect a door has to stop is an
 * entry that is not a string at all, because `NodeCard` renders each as a React
 * child; inventing a length here, where `label` and `claim` beside it are checked
 * for their type and nothing else, would be a bound picked to look thorough. Use
 * `strings` wherever the codec already has a number for the field.
 */
const onlyStrings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []

/**
 * An audit the claim's OWN house made is not a cross-examination.
 *
 * `asAudit` checks an audit's shape; nothing checked who made it. So a
 * hand-edited document or merge could attach `verdict: 'pass'` stamped with the
 * node's own provider to an unedited, model-authored claim, and `bridgeStanding`
 * awarded `cross-examined` — a rung whose whole sentence is "a model from
 * another house examined it and could not object". That inflates the candidate's
 * standing, its place in the tab's order, the `join` cue, the codec's cap and
 * every export, which is invariant 10 defeated with no model call at all.
 *
 * `auditableByProvider` is the rule the live path already runs on: `reason.ts`
 * filters its targets through `auditableBy` before spending anything. Both
 * halves matter — a claim a PERSON rewrote is open to any house, because the
 * words stopped being the model's.
 */
const admittedAudit = (
  audit: Audit | undefined,
  model: ModelStamp | undefined,
  editedAt: number | undefined,
): Audit | undefined =>
  audit && auditableByProvider(model, editedAt, audit.model.provider) ? audit : undefined

/**
 * A panel that ruled on wording the claim no longer carries is DROPPED, not
 * marked.
 *
 * The one evidence record with that answer, and `panelLive` has the argument:
 * `reason.ts` clears the panel in the same patch that sets `editedAt`, so a
 * stale one is a state the app never writes and there is nothing to preserve by
 * keeping it. An audit and a source check survive their rewrite and are read
 * through a liveness predicate instead, which is why `admittedAudit` above hands
 * back a record and this hands back nothing.
 *
 * Here rather than at the fifth reader, because the shelf is where the class
 * ends. The merge already dropped it (`semanticApply`), the tournament's ledger
 * and the panel collector already skipped it — and `bridgeStanding` awarded the
 * `attacked` rung off it anyway, which is the rung ordering the Breakthroughs
 * tab, the `join` cue, the codec's cap and every export. A predicate added to
 * that one reader would have left the canvas's panel-agreement weight and the
 * exports reading the same stale record; dropping it at the door answers for
 * every reader at once, including the ones nobody enumerated.
 *
 * A stricter read of stored data, so no schema bump — `refutedIsEarned` in
 * `asVerification` is the precedent, and the head it can leave dirty is the
 * accepted cost documented at `semanticWorkingTreeDirty`.
 */
const admittedPanel = (
  panel: PanelCheck | undefined,
  editedAt: number | undefined,
): PanelCheck | undefined => (panel && panelLive(panel.checkedAt, editedAt) ? panel : undefined)

/** The three ratings, on the codec's own rule: a number is clamped to the unit
 *  interval and anything else takes `makeNode`'s default. Admitted out of range
 *  they survived on the graph until the next reload and then changed under the
 *  reader, leaving the branch dirty against a head nobody had edited. */
const score = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? clamp(v) : fallback

const READING_OK = new Set<string>([
  'supported',
  'mixed',
  'refuted',
  'nothing-found',
  'not-checkable',
  'unsourced',
])
const STANCE_OK = new Set<string>(['supports', 'disputes', 'context'])
const NOTICE_OK = new Set<string>(['retracted', 'flagged'])
const RUN_STOP_OK = new Set<string>([
  'moves',
  'tokens',
  'clock',
  'size',
  'spend',
  'until',
  'protocol',
  'frontier',
  'programme',
  'pursuit',
  'stalled',
  'declined',
  'stopped',
  'unreachable',
  'replaced',
])
/** The condition kinds a stored run may carry. A record naming any other kind is
 *  dropped whole rather than half-read: an `until` with no kind is a condition
 *  the account could not describe, on the ending whose whole content is the
 *  condition. */
const UNTIL_KIND_OK = new Set<string>(['refuted', 'supported', 'split', 'objection', 'rung'])
/** The standing brief's kinds. A directive whose kind fails is dropped whole —
 *  half a directive is an instruction the prompts could not route. */
const DIRECTIVE_OK = new Set<string>(['constraint', 'given', 'exclude', 'criterion'])
/** Directives kept per document, and characters per directive — `core/brief.ts`,
 *  where the writers that add directives read the same two numbers first. */
export { MAX_BRIEF }

function asBrief(v: unknown): Directive[] {
  if (!Array.isArray(v)) return []
  const out: Directive[] = []
  for (const raw of v.slice(0, MAX_BRIEF)) {
    if (!raw || typeof raw !== 'object') continue
    const o = raw as Record<string, unknown>
    const body = text(o.text, DIRECTIVE_CHARS)
    if (!body || typeof o.kind !== 'string' || !DIRECTIVE_OK.has(o.kind)) continue
    // The gap a directive answers, when it has one (`Directive.answers`): an id,
    // clamped like one, and dropped rather than kept malformed.
    const answers = typeof o.answers === 'string' ? text(o.answers, 80) : ''
    // When it joined (`Directive.at`): a finite time or nothing — a malformed one
    // is dropped, never replaced with a guess.
    const at = typeof o.at === 'number' && Number.isFinite(o.at) && o.at > 0 ? o.at : undefined
    out.push({
      kind: o.kind as Directive['kind'],
      text: body,
      ...(answers ? { answers } : {}),
      ...(at !== undefined ? { at } : {}),
    })
  }
  return out
}

/**
 * What the brief used to say (`Session.formerDirectives`), read as defensively as
 * the brief: a kind that fails or words that are empty drop the entry, a
 * malformed date is dropped and never guessed, and the newest `MAX_FORMER` stay.
 * `left` is the one date an entry cannot do without — it is where the thread puts
 * the change — so an entry with none is dropped whole. Times are read as written,
 * like `Directive.at`: the edit join (`editedFrom`) compares one entry's `left`
 * with a directive's `at` for equality, and translating one clock without the
 * other would part them.
 */
function asFormerDirectives(v: unknown): FormerDirective[] {
  if (!Array.isArray(v)) return []
  const out: FormerDirective[] = []
  const time = (x: unknown) =>
    typeof x === 'number' && Number.isFinite(x) && x > 0 ? x : undefined
  for (const raw of v.slice(-MAX_FORMER)) {
    if (!raw || typeof raw !== 'object') continue
    const o = raw as Record<string, unknown>
    const body = text(o.text, DIRECTIVE_CHARS)
    const left = time(o.left)
    if (!body || left === undefined || typeof o.kind !== 'string' || !DIRECTIVE_OK.has(o.kind))
      continue
    const at = time(o.at)
    const replacedBy = typeof o.replacedBy === 'string' ? text(o.replacedBy, DIRECTIVE_CHARS) : ''
    out.push({
      kind: o.kind as Directive['kind'],
      text: body,
      ...(at !== undefined ? { at } : {}),
      left,
      ...(replacedBy ? { replacedBy } : {}),
    })
  }
  return out
}
// Every key of `MOVE_REASON`, and a run move naming one that is missing here is
// dropped on the next read — the move survives in the account with no reason
// beside it, which is the one thing a printed comparison exists to avoid. Pinned
// by a round-trip test over the label record rather than by review.
const MOVE_REASON_OK = new Set<string>([
  'agenda',
  'in-progress',
  'least-worked',
  'smallest-branch',
  'contradiction',
  'gap',
  'disputed',
  'stale',
  'foundation-broke',
  'oldest',
  'only-one',
  'load-bearing',
  'checkable',
  'unexamined',
  'synthesis-first',
  'cone',
  'programme',
  'whole-graph',
  'unexplained',
  'landed-unsearched',
])
const MOVE_OUTCOME_OK = new Set<string>(['nodes', 'nothing', 'refused', 'error', 'incomplete'])
const SCOPE_OK = new Set<string>(['web', 'corpus', 'both'])
const CLOSENESS_OK = new Set<string>(['same', 'overlapping', 'adjacent'])
const VERDICT_OK = new Set<string>(['holds', 'holds-weaker', 'fails', 'undecidable'])
const BEARING_OK = new Set<string>(['supported', 'refuted', 'inconclusive'])
/** A run's collected cross-examination verdicts. `asAudit` tests the same two
 *  values inline for the single record on a node; this is the set form, needed
 *  because `Run.auditVerdicts` is an array and is filtered rather than rejected. */
const AUDIT_VERDICT_OK = new Set<string>(AUDIT_VERDICTS)

// Every member of `ProviderId`, and it has to be every one. `custom` was missing
// here while being a stamp the app writes, so each record carrying it failed the
// check below and was dropped on the next read: a run on your own endpoint lost
// its whole account, its spend row left the ledger, and its nodes came back
// unattributed. The writer never filtered stamps, so the loss happened entirely
// on the way in — one reload.
const PROVIDER_OK = new Set<string>(['anthropic', 'openai', 'google', 'custom', 'typesafe'])

/**
 * A model stamp, off disk.
 *
 * Validated as bounded TEXT against the provider literals and NEVER against
 * `MODELS`: a stamp checked against the live registry would erase the provenance
 * of every record made with a model the next release delists — which is the one
 * guarantee this whole area exists to make. Same rule, same reason, as
 * `asForeignRef`. It is also why `custom` cannot be validated any harder than
 * this: its id is whatever the user typed, and no registry will ever hold it.
 */
function asModelStamp(v: unknown): ModelStamp | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const id = text(o.id, 120)
  if (!id || typeof o.provider !== 'string' || !PROVIDER_OK.has(o.provider)) return undefined
  return { provider: o.provider as ProviderId, id, name: text(o.name, 120) || id }
}

/** Copies made for one physical pass must describe the same model exactly. */
function sameModelStamp(a: ModelStamp, b: ModelStamp): boolean {
  return a.provider === b.provider && a.id === b.id && a.name === b.name
}

/**
 * A receipt's breakdown, off disk, or nothing.
 *
 * The AGGREGATES are authoritative and the breakdown is not: the estimate and
 * the spending ceiling read the aggregates, so a stored detail is never allowed
 * to change one. That decides both branches here.
 *
 * The two `unclassified` fields are DERIVED here rather than read, which is what
 * makes the identity hold by construction instead of by trust. That also buys
 * forward compatibility for free: a row written by a later build that names a
 * category this one does not know about arrives with that category missing from
 * the named sum, and the difference lands in `unclassified` — the honest reading
 * of "some of this input had a kind, and it was not one of mine".
 *
 * Where the NAMED categories exceed the aggregate they decompose, the breakdown
 * is dropped whole: fixing it would mean choosing one to shrink, and there is no
 * honest basis for that choice. The row keeps its aggregates and reads as one
 * recorded before the meter existed, which is the accurate description of a
 * record whose breakdown could not be trusted.
 *
 * No `SCHEMA` bump. Absence has always meant "no breakdown recorded" and still
 * does, so an old row is valid unchanged and an older build stripping the field
 * on save loses a diagnostic rather than a figure any account depends on.
 */
function asDetail(v: unknown, inputTokens: number, searches: number): SpendDetail | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  // Every field is clamped to the aggregate it is a part of, which is the cap:
  // no category can exceed the total it decomposes, and the bound needs no
  // constant of its own to say so.
  const tok = (x: unknown): number => Math.min(Math.max(0, Math.trunc(Number(x) || 0)), inputTokens)
  const req = (x: unknown): number => Math.min(Math.max(0, Math.trunc(Number(x) || 0)), searches)

  const inputUncached = tok(o.inputUncached)
  const cacheWrite5m = tok(o.cacheWrite5m)
  const cacheWrite30m = tok(o.cacheWrite30m)
  const cacheWrite1h = tok(o.cacheWrite1h)
  const cacheRead = tok(o.cacheRead)
  const webSearches = req(o.webSearches)
  const webFetches = req(o.webFetches)

  const named = inputUncached + cacheWrite5m + cacheWrite30m + cacheWrite1h + cacheRead
  const namedReq = webSearches + webFetches
  if (named > inputTokens || namedReq > searches) return null

  return {
    inputUncached,
    cacheWrite5m,
    cacheWrite30m,
    cacheWrite1h,
    cacheRead,
    // Whatever the named categories do not account for, however it got there —
    // a stored value, a build that reported less, or a shortfall this read had
    // to absorb. Written from the identity rather than from the field, so the
    // identity cannot be violated by what was on disk.
    inputUnclassified: inputTokens - named,
    webSearches,
    webFetches,
    serverRequestsUnclassified: searches - namedReq,
  }
}

function asSpend(v: unknown): Spend | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const model = asModelStamp(o.model)
  // An unattributed row cannot be added to any dashboard, which is the only thing
  // this ledger is for.
  if (!model) return null
  const inputTokens = Math.max(0, Math.trunc(Number(o.inputTokens) || 0))
  const outputTokens = Math.max(0, Math.trunc(Number(o.outputTokens) || 0))
  const searches = Math.max(0, Math.trunc(Number(o.searches) || 0))
  const detail = asDetail(o.detail, inputTokens, searches)
  // Kept only where it can be true: a whole number no larger than the row it
  // describes. Anything else is dropped rather than repaired, and a dropped peak
  // prices the row by its total, which only ever reprices upward (D311).
  const peak = o.peakInputTokens
  const peakInputTokens =
    typeof peak === 'number' && Number.isSafeInteger(peak) && peak >= 0 && peak <= inputTokens
      ? peak
      : undefined
  return {
    model,
    inputTokens,
    outputTokens,
    searches,
    ...(detail ? { detail } : {}),
    ...(peakInputTokens !== undefined ? { peakInputTokens } : {}),
  }
}

/**
 * Exact-call usage cannot use the forgiving dashboard codec above.
 *
 * `asSpend` deliberately repairs old aggregate rows, but doing that inside a
 * receipt whose claim is exact would turn strings, negatives or a broken
 * breakdown into a different call. Any such difference drops the whole call set.
 */
function asExactModelCallSpend(v: unknown): Spend | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const model = asModelStamp(o.model)
  const inputTokens = exactNonnegativeInteger(o.inputTokens)
  const outputTokens = exactNonnegativeInteger(o.outputTokens)
  const searches = exactNonnegativeInteger(o.searches)
  if (!model || inputTokens === null || outputTokens === null || searches === null) return null

  let detail: SpendDetail | undefined
  if (o.detail !== undefined) {
    if (!o.detail || typeof o.detail !== 'object' || Array.isArray(o.detail)) return null
    const raw = o.detail as Record<string, unknown>
    const fields = [
      'inputUncached',
      'cacheWrite5m',
      'cacheWrite30m',
      'cacheWrite1h',
      'cacheRead',
      'inputUnclassified',
      'webSearches',
      'webFetches',
      'serverRequestsUnclassified',
    ] as const
    const values = Object.fromEntries(
      fields.map((field) => [field, exactNonnegativeInteger(raw[field])]),
    ) as Record<(typeof fields)[number], number | null>
    if (fields.some((field) => values[field] === null)) return null
    detail = values as SpendDetail
    if (
      detail.inputUncached +
        detail.cacheWrite5m +
        detail.cacheWrite30m +
        detail.cacheWrite1h +
        detail.cacheRead +
        detail.inputUnclassified !==
        inputTokens ||
      detail.webSearches + detail.webFetches + detail.serverRequestsUnclassified !== searches
    ) {
      return null
    }
  }
  return { model, inputTokens, outputTokens, searches, ...(detail ? { detail } : {}) }
}

const MODEL_CALL_EFFORT_OK = new Set<string>(['low', 'medium', 'high', 'xhigh', 'max'])
const MODEL_CALL_ERROR_KIND_OK = new Set<string>([
  'storage',
  'auth',
  'rate',
  'quota',
  'network',
  'server',
  'request',
  'aborted',
  'timeout',
  'unknown',
])
const INVALID_MODEL_CALL_JSON = Symbol('invalid-model-call-json')

interface ModelCallBudget {
  chars: number
  nodes: number
}

function exactReceiptString(
  value: unknown,
  budget: ModelCallBudget,
  maximum = MODEL_CALL_RECEIPT_CHARS,
): string | null {
  if (typeof value !== 'string' || value.length > maximum || value.length > budget.chars)
    return null
  budget.chars -= value.length
  return value
}

/** JSON admission for exact receipts: any bound or shape failure drops the set. */
function exactModelCallJson(
  value: unknown,
  budget: ModelCallBudget,
  depth = 0,
): ModelCallJsonValue | typeof INVALID_MODEL_CALL_JSON {
  if (budget.nodes-- <= 0 || depth > 20) return INVALID_MODEL_CALL_JSON
  if (value === null || typeof value === 'boolean') return value
  if (typeof value === 'number')
    return Number.isFinite(value) ? (Object.is(value, -0) ? 0 : value) : INVALID_MODEL_CALL_JSON
  if (typeof value === 'string') {
    const held = exactReceiptString(value, budget)
    return held === null ? INVALID_MODEL_CALL_JSON : held
  }
  if (!value || typeof value !== 'object') return INVALID_MODEL_CALL_JSON
  if (Array.isArray(value)) {
    const out: ModelCallJsonValue[] = []
    for (const row of value) {
      const parsed = exactModelCallJson(row, budget, depth + 1)
      if (parsed === INVALID_MODEL_CALL_JSON) return INVALID_MODEL_CALL_JSON
      out.push(parsed)
    }
    return out
  }
  const out: Record<string, ModelCallJsonValue> = {}
  for (const [key, row] of Object.entries(value)) {
    if (exactReceiptString(key, budget, 1_000) === null) return INVALID_MODEL_CALL_JSON
    const parsed = exactModelCallJson(row, budget, depth + 1)
    if (parsed === INVALID_MODEL_CALL_JSON) return INVALID_MODEL_CALL_JSON
    // `out['__proto__'] = value` invokes Object.prototype's legacy setter and
    // silently loses an own JSON field. Define it so a schema carrying that
    // perfectly legal key survives byte-for-byte instead of changing under an
    // "exact" receipt.
    Object.defineProperty(out, key, {
      value: parsed,
      enumerable: true,
      configurable: true,
      writable: true,
    })
  }
  return out
}

function exactNonnegativeInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null
}

function asModelCallReceipt(
  value: unknown,
  ordinal: number,
  clock: StoredClock,
  budget: ModelCallBudget,
): ModelCallReceipt | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  if (
    row.version !== MODEL_CALL_RECEIPT_VERSION ||
    (row.kind !== 'structured' && row.kind !== 'text') ||
    row.ordinal !== ordinal
  ) {
    return null
  }
  const id = exactReceiptString(row.id, budget, 120)
  const selectedModel = asModelStamp(row.selectedModel)
  const answeredModel = row.answeredModel === null ? null : asModelStamp(row.answeredModel)
  if (!id || !selectedModel || (row.answeredModel !== null && !answeredModel)) return null

  if (!Array.isArray(row.messages) || row.messages.length !== 2) return null
  const system = row.messages[0] as Record<string, unknown> | undefined
  const user = row.messages[1] as Record<string, unknown> | undefined
  const systemContent = exactReceiptString(system?.content, budget)
  const userContent = exactReceiptString(user?.content, budget)
  if (
    system?.role !== 'system' ||
    user?.role !== 'user' ||
    systemContent === null ||
    userContent === null
  ) {
    return null
  }

  const parameters = row.parameters as Record<string, unknown> | undefined
  const rawSchema = parameters?.schema as Record<string, unknown> | undefined
  const schemaName = exactReceiptString(rawSchema?.name, budget, 240)
  const schemaValue = exactModelCallJson(rawSchema?.value, budget)
  const maxTokens = exactNonnegativeInteger(parameters?.maxTokens)
  const maxRetries = exactNonnegativeInteger(parameters?.maxRetries)
  if (
    !parameters ||
    (row.kind === 'structured' && (!schemaName || schemaValue === INVALID_MODEL_CALL_JSON)) ||
    (row.kind === 'text' && parameters.schema !== null) ||
    !MODEL_CALL_EFFORT_OK.has(String(parameters.effort)) ||
    maxTokens === null ||
    maxTokens < 1 ||
    maxRetries === null
  ) {
    return null
  }
  const rootArrayKey =
    parameters.rootArrayKey === null
      ? null
      : exactReceiptString(parameters.rootArrayKey, budget, 240)
  const cacheKey =
    parameters.cacheKey === null ? null : exactReceiptString(parameters.cacheKey, budget, 1_000)
  if (rootArrayKey === null && parameters.rootArrayKey !== null) return null
  if (cacheKey === null && parameters.cacheKey !== null) return null

  let web: ModelCallReceipt['parameters']['web'] = null
  if (parameters.web !== null) {
    if (!parameters.web || typeof parameters.web !== 'object') return null
    const rawWeb = parameters.web as Record<string, unknown>
    const maxSearches = exactNonnegativeInteger(rawWeb.maxSearches)
    const maxFetches = exactNonnegativeInteger(rawWeb.maxFetches)
    const maxContentTokens =
      rawWeb.maxContentTokens === null ? null : exactNonnegativeInteger(rawWeb.maxContentTokens)
    if (
      maxSearches === null ||
      maxFetches === null ||
      (maxContentTokens === null && rawWeb.maxContentTokens !== null)
    )
      return null
    web = { maxSearches, maxFetches, maxContentTokens }
  }

  const rawStartedAt = Number(row.startedAt)
  const rawCompletedAt = Number(row.completedAt)
  if (
    !Number.isFinite(rawStartedAt) ||
    rawStartedAt <= 0 ||
    !Number.isFinite(rawCompletedAt) ||
    rawCompletedAt < rawStartedAt
  ) {
    return null
  }
  const startedAt = storedTime(rawStartedAt, clock)
  const completedAt = storedTime(rawCompletedAt, clock)

  const rawOutcome = row.outcome as Record<string, unknown> | undefined
  let outcome: ModelCallReceipt['outcome']
  if (rawOutcome?.kind === 'completed' && typeof rawOutcome.truncated === 'boolean') {
    outcome = { kind: 'completed', truncated: rawOutcome.truncated }
  } else if (rawOutcome?.kind === 'refused' && typeof rawOutcome.truncated === 'boolean') {
    const note = rawOutcome.note === null ? null : exactReceiptString(rawOutcome.note, budget)
    if (note === null && rawOutcome.note !== null) return null
    outcome = { kind: 'refused', note, truncated: rawOutcome.truncated }
  } else if (
    rawOutcome?.kind === 'failed' &&
    typeof rawOutcome.errorKind === 'string' &&
    MODEL_CALL_ERROR_KIND_OK.has(rawOutcome.errorKind)
  ) {
    const reason = exactReceiptString(rawOutcome.reason, budget)
    if (reason === null) return null
    outcome = {
      kind: 'failed',
      errorKind: rawOutcome.errorKind as Extract<
        ModelCallReceipt['outcome'],
        { kind: 'failed' }
      >['errorKind'],
      reason,
    }
  } else {
    return null
  }

  const usage = row.usage === null ? null : asExactModelCallSpend(row.usage)
  if (row.usage !== null && !usage) return null
  if (outcome.kind === 'failed') {
    if (answeredModel !== null || (usage !== null && !sameModelStamp(selectedModel, usage.model)))
      return null
  } else if (
    !answeredModel ||
    !usage ||
    !sameModelStamp(selectedModel, answeredModel) ||
    !sameModelStamp(answeredModel, usage.model)
  ) {
    return null
  }

  const rawText =
    row.textContext === undefined ? undefined : exactModelCallJson(row.textContext, budget)
  const textContext = rawText as ModelCallReceipt['textContext']
  if (row.kind === 'text') {
    if (
      !textContext ||
      typeof textContext !== 'object' ||
      !Array.isArray(textContext.history) ||
      textContext.history.length > 100 ||
      !textContext.history.every(
        (turn) =>
          turn &&
          (turn.role === 'user' || turn.role === 'assistant') &&
          typeof turn.text === 'string',
      ) ||
      typeof textContext.output !== 'string' ||
      textContext.internalAttempts !== null ||
      (textContext.systemDynamic !== undefined && typeof textContext.systemDynamic !== 'string') ||
      (textContext.systemTail !== undefined && typeof textContext.systemTail !== 'string') ||
      rootArrayKey !== null ||
      web !== null
    )
      return null
  } else if (rawText !== undefined) return null
  const researchContract =
    row.researchContract === undefined
      ? undefined
      : parseResearchContract(exactModelCallJson(row.researchContract, budget))
  if (
    row.researchContract !== undefined &&
    (!researchContract || !userContent.startsWith(researchContractBlock(researchContract)))
  )
    return null
  return {
    version: MODEL_CALL_RECEIPT_VERSION,
    id,
    ordinal,
    kind: row.kind,
    ...(textContext ? { textContext } : {}),
    ...(researchContract ? { researchContract } : {}),
    messages: [
      { role: 'system', content: systemContent },
      { role: 'user', content: userContent },
    ],
    parameters: {
      schema:
        row.kind === 'text'
          ? null
          : { name: schemaName!, value: schemaValue as ModelCallJsonValue },
      effort: parameters.effort as ModelCallReceipt['parameters']['effort'],
      maxTokens,
      rootArrayKey,
      web,
      cacheKey,
      maxRetries,
    },
    selectedModel,
    answeredModel,
    startedAt,
    completedAt,
    outcome,
    usage,
  }
}

/**
 * An op's physical calls, in the order they were sent. Each one starts after the
 * one before it has finished — except a panel's (`overlapping`), whose seats two
 * and three are sent together while seat one is still streaming (`runPanel`).
 * For those only the op's own bounds hold them, which `modelCallReceiptsMatchOp`
 * checks for every op alike. Looser than it was for a panel alone, and a looser
 * read of stored data needs no schema bump: every set this admitted before, it
 * still admits.
 */
function asModelCallReceipts(
  value: unknown,
  clock: StoredClock,
  overlapping = false,
): ModelCallReceipt[] | null {
  if (!Array.isArray(value) || value.length < 1 || value.length > MAX_MODEL_CALLS_PER_OP)
    return null
  const budget: ModelCallBudget = {
    chars: MODEL_CALL_RECEIPT_CHARS,
    nodes: MODEL_CALL_RECEIPT_JSON_NODES,
  }
  const receipts: ModelCallReceipt[] = []
  const ids = new Set<string>()
  for (let index = 0; index < value.length; index++) {
    const receipt = asModelCallReceipt(value[index], index + 1, clock, budget)
    if (!receipt || ids.has(receipt.id)) return null
    const prior = receipts[index - 1]
    if (prior && !overlapping && receipt.startedAt < prior.completedAt) return null
    ids.add(receipt.id)
    receipts.push(receipt)
  }
  return receipts
}

function sameSpendReceipt(a: Spend, b: Spend): boolean {
  return (
    sameModelStamp(a.model, b.model) &&
    a.inputTokens === b.inputTokens &&
    a.outputTokens === b.outputTokens &&
    a.searches === b.searches &&
    JSON.stringify(a.detail ?? null) === JSON.stringify(b.detail ?? null)
  )
}

function modelCallReceiptsMatchOp(
  receipts: readonly ModelCallReceipt[],
  op: { startedAt: number; finishedAt?: number; model?: ModelStamp; spend?: Spend },
): boolean {
  if (receipts.some((receipt) => receipt.startedAt < op.startedAt)) return false
  if (
    op.finishedAt !== undefined &&
    receipts.some((receipt) => receipt.completedAt > op.finishedAt!)
  ) {
    return false
  }
  if (
    !receipts.every((receipt) => sameModelStamp(receipt.selectedModel, receipts[0]!.selectedModel))
  )
    return false
  if (!op.model || !sameModelStamp(receipts[0]!.selectedModel, op.model)) return false
  // An unfinished operation may have persisted between the receipt callback and
  // the spend write. Once it is finished, the two durable views must agree.
  if (op.finishedAt === undefined) return true
  let aggregate: Spend | null = null
  for (const receipt of receipts) {
    const row = receipt.usage
    if (!row || (!row.inputTokens && !row.outputTokens && !row.searches)) continue
    if (aggregate && !sameModelStamp(aggregate.model, row.model)) return false
    aggregate = aggregate ? addSpendRow(aggregate, row) : row
  }
  return aggregate === null
    ? op.spend === undefined
    : !!op.spend && sameSpendReceipt(aggregate, op.spend)
}

/**
 * A portfolio packet, off disk.
 *
 * The record is deliberately validated as a whole. Keeping only the valid
 * angles would make `Op.produced` and the logical run rows describe a different
 * pass from the one that actually ran.
 */
function asPortfolio(v: unknown, targetIds: string[], produced: string[]): PortfolioRecord | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (!Array.isArray(o.parts) || o.parts.length < 2 || o.parts.length > 3) return null
  if (targetIds.length !== 1) return null

  const targetId = targetIds[0]!
  const kinds = new Set<string>()
  const allProduced: string[] = []
  const parts: PortfolioRecord['parts'] = []
  for (const raw of o.parts) {
    if (!raw || typeof raw !== 'object') return null
    const p = raw as Record<string, unknown>
    if (typeof p.kind !== 'string' || !RUN_VERB_OK.has(p.kind) || kinds.has(p.kind)) return null
    if (text(p.targetId, 60) !== targetId) return null

    const ids = strings(p.produced, MAX_OP_PRODUCED, 60)
    if (!Array.isArray(p.produced) || ids.length !== p.produced.length) return null
    for (const id of ids) {
      if (allProduced.includes(id)) return null
      allProduced.push(id)
    }
    kinds.add(p.kind)
    parts.push({ kind: p.kind as RunVerb, targetId, produced: ids })
  }

  if (
    produced.length !== allProduced.length ||
    produced.some((id, index) => id !== allProduced[index])
  )
    return null

  const elaboratedTargetId = text(o.elaboratedTargetId, 60)
  if (elaboratedTargetId && elaboratedTargetId !== targetId) return null
  const rawDropped = Number(o.droppedForSize)
  if (
    Object.prototype.hasOwnProperty.call(o, 'droppedForSize') &&
    (!Number.isInteger(rawDropped) || rawDropped < 0)
  )
    return null
  const droppedForSize =
    Number.isInteger(rawDropped) && rawDropped >= 0
      ? Math.min(rawDropped, MAX_OP_PRODUCED)
      : undefined
  return {
    parts,
    ...(elaboratedTargetId ? { elaboratedTargetId } : {}),
    ...(droppedForSize !== undefined ? { droppedForSize } : {}),
  }
}

function asElaboration(v: unknown, now: number): NodeElaboration | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const retained = textRetention('persistence', o.textTruncations, o.textRetentionVersion)
  const reasoning = retained.text('reasoning', o.reasoning)
  const opId = text(o.opId, 60)
  const model = asModelStamp(o.model)
  const authoredAt = Number(o.authoredAt)
  if (!reasoning || !opId || !model || !Number.isFinite(authoredAt) || authoredAt < 0)
    return undefined
  return {
    reasoning,
    nextSteps: retained.list('nextSteps', o.nextSteps),
    model,
    opId,
    authoredAt: Math.min(authoredAt, now),
    ...retained.fields(),
  }
}

/**
 * A 64-bit artefact signature, off disk: exactly 16 lowercase hex characters.
 *
 * `similarity.ts` emits exactly that width so a stored value can be validated
 * without knowing what produced it. Longer is REFUSED rather than capped — the
 * codec's usual `text(v, n)` slice would turn a 20-character string into a
 * different, valid-looking signature, and a fabricated key here would collapse
 * two sources that are not copies of each other.
 */
const signature = (v: unknown): string | undefined => {
  if (typeof v !== 'string') return undefined
  const s = v.trim().toLowerCase()
  return /^[0-9a-f]{16}$/.test(s) ? s : undefined
}

export const nodeOrdinal = (v: unknown): number | undefined => {
  const n = Number(v)
  return Number.isSafeInteger(n) && n >= 0 ? n : undefined
}

function asWebSource(v: unknown): WebSource | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const url = text(o.url, 2000)
  // The same gate the live pass applies. A URL that is not http(s) was never a
  // source, whoever wrote it into the document.
  if (!url || !isHttpUrl(url)) return null
  const given = typeof o.stance === 'string' && STANCE_OK.has(o.stance)
  const stance = given ? (o.stance as Stance) : 'context'
  // Filled as context when the record carries no side it can read, as it always
  // was — and marked as filled, so the stance count names it "not labelled"
  // rather than as a source the check read as context. A stored mark is kept only
  // beside the fill it explains: on a source with a side, it would be the count
  // disowning a label the gates read. An optional field read defensively, so no
  // SCHEMA bump (`WebSource.stanceUnlabelled`).
  const unlabelled = !given || (o.stanceUnlabelled === true && stance === 'context')
  const hash = signature(o.textHash)
  const near = signature(o.simHash)
  const retained = textRetention('persistence', o.textTruncations, o.textRetentionVersion)
  return {
    url,
    title: text(o.title, 300) || url,
    quote: retained.text('quote', o.quote, SOURCE_QUOTE_CHARS),
    stance,
    ...(unlabelled ? { stanceUnlabelled: true as const } : {}),
    via: o.via === 'fetch' ? 'fetch' : 'search',
    // Tri-state, preserved. Coercing an absent value to `false` here would make
    // a restored check accuse the model of inventing a quote it copied correctly.
    ...(typeof o.quoteFound === 'boolean' ? { quoteFound: o.quoteFound } : {}),
    ...(typeof o.textComplete === 'boolean' ? { textComplete: o.textComplete } : {}),
    // The two artefact signatures, carried so the disk gate can reach the live
    // gate's verdict — the text they were taken over lives for one pass and is
    // never written. Dropped rather than repaired when they are not signatures:
    // absent merges nothing, which is exactly how a document written by an older
    // build reads, so a damaged field costs the same legible false negative
    // instead of a silent collapse. See `core/similarity.ts`.
    ...(hash ? { textHash: hash } : {}),
    ...(near ? { simHash: near } : {}),
    ...(asJudgedStance(o.judged) ? { judged: asJudgedStance(o.judged) } : {}),
    // What kind of evidence the check read the page as. Validated against the
    // closed set and dropped otherwise, with no SCHEMA bump: an older check simply
    // has none, and no gate reads it (`WebSource.design`).
    ...(typeof o.design === 'string' && DESIGN_OK.has(o.design)
      ? { design: o.design as SourceDesign }
      : {}),
    ...retained.fields(),
  }
}

const DESIGN_OK = new Set<string>(SOURCE_DESIGNS)

const JUDGED_STANCE_OK = new Set<string>(['supports', 'disputes', 'neither'])

/**
 * The judge's second reading of a source, off disk, or nothing.
 *
 * Whole or dropped, never repaired: a reading with a stance and no probability
 * behind it, or a probability and no model stamp, is not the record the live
 * pass writes. Absent is the ordinary state — most checks were made with no
 * judge configured — and it means nothing about the source, because the gate
 * this rides beside never reads it.
 */
function asJudgedStance(v: unknown): WebSource['judged'] | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const model = asModelStamp(o.model)
  const p =
    o.probabilities && typeof o.probabilities === 'object'
      ? (o.probabilities as Record<string, unknown>)
      : null
  if (!model || !p || typeof o.stance !== 'string' || !JUDGED_STANCE_OK.has(o.stance))
    return undefined
  const supports = unit(p.supports)
  const disputes = unit(p.disputes)
  const neither = unit(p.neither)
  if (supports === undefined || disputes === undefined || neither === undefined) return undefined
  return {
    stance: o.stance as 'supports' | 'disputes' | 'neither',
    probabilities: { supports, disputes, neither },
    model,
  }
}

function asPassageCite(v: unknown): PassageCite | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const passage = Number(o.passage)
  if (!Number.isInteger(passage) || passage < 1) return null
  const docId = text(o.docId, 60)
  if (!docId) return null
  const retained = textRetention('persistence', o.textTruncations, o.textRetentionVersion)
  // An auxiliary MACHINE field, validated against its closed set and dropped when
  // it is anything else — the `textHash` rule of D28, for the same reason: absent
  // is exactly how a cite written by an older build reads, so a damaged value
  // costs the mark rather than inventing a notice nobody looked up. Carrying it
  // needs no `SCHEMA` bump for the same reason again; see D31.
  const notice =
    typeof o.notice === 'string' && NOTICE_OK.has(o.notice) ? (o.notice as DocNotice) : undefined
  return {
    docId,
    docTitle: text(o.docTitle, 200),
    passage,
    quote: retained.text('passage', o.quote, RESEARCH_ITEM_CHARS),
    ...(notice ? { notice } : {}),
    ...retained.fields(),
  }
}

function asCorpusEvidence(v: unknown): CorpusEvidence | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const cites = Array.isArray(o.cites)
    ? (o.cites.map(asPassageCite).filter(Boolean) as PassageCite[]).slice(0, 8)
    : []
  const match = o.match === 'ok' || o.match === 'thin' ? o.match : 'none'
  return {
    cites,
    dropped: Math.max(0, Math.trunc(Number(o.dropped) || 0)),
    match,
    offered: Math.max(0, Math.trunc(Number(o.offered) || 0)),
    bestCoverage: unit(o.bestCoverage) ?? 0,
    docsAtCheck: Math.max(0, Math.trunc(Number(o.docsAtCheck) || 0)),
  }
}

/**
 * A check's record that the user's documents were searched and none offered.
 *
 * Optional and ADDITIVE, so no `SCHEMA` bump: a record written before it existed
 * simply has none, which is how every check read before it existed. Whole or not
 * at all, like the judge's fields — a count of documents that is not a positive
 * integer, or an `offered` that is not the 0 this field exists to state, is a
 * hand edit, and the sentence it would print ("none matched") would be false of
 * it. The switch reads as off unless it says true, which only drops the clause
 * that says the fallback went against the user's preference.
 */
function asCorpusSearched(v: unknown): Verification['corpusSearched'] {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const docs = Number(o.docs)
  if (!Number.isInteger(docs) || docs < 1 || o.offered !== 0) return undefined
  return { docs, offered: 0, preferCorpus: o.preferCorpus === true }
}

/** A passage ref as builds before D146 stored it: the call-local number. */
const LEGACY_PASSAGE_REF = /^p\d+$/

function asPriorArt(v: unknown, refs: Set<string>, legacy: string | undefined): PriorArt | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const stored = text(o.ref, 2000)
  const ref = LEGACY_PASSAGE_REF.test(stored) ? legacy : stored
  // A prior-art entry pointing at a source that is not in this record is a
  // citation with nothing behind it. `refs` is built from what survived above.
  if (!ref || !refs.has(ref)) return null
  const closeness =
    typeof o.closeness === 'string' && CLOSENESS_OK.has(o.closeness)
      ? (o.closeness as PriorArt['closeness'])
      : 'adjacent'
  return { ref, covers: text(o.covers, 400), closeness }
}

/**
 * A verification, off disk.
 *
 * The invariants the live reconciliation enforces are re-enforced here, because
 * a document is untrusted whatever wrote it: `refuted` still needs an opened page
 * behind it, and `measuredNovelty` still needs surviving prior art.
 */
function asVerification(v: unknown, novelty: number, clock: StoredClock): Verification | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  if (typeof o.reading !== 'string' || !READING_OK.has(o.reading)) return undefined
  let reading = o.reading as SourceReading

  const sources = Array.isArray(o.sources)
    ? (o.sources.map(asWebSource).filter(Boolean) as WebSource[]).slice(0, 6)
    : []
  const corpus = asCorpusEvidence(o.corpus)
  // A record citing one of the user's own passages cannot also say none of theirs
  // was offered. The live path never writes both, so a document that does is a
  // hand edit, and the note is the half that goes — the cite is evidence.
  const corpusSearched = corpus?.cites.some((c) => liveOrigin(c.docId) === null)
    ? undefined
    : asCorpusSearched(o.corpusSearched)

  // Preserved as a boolean or dropped, NEVER coerced. Coercing absent → false
  // would make a restored documents-only check report a model limit it never hit.
  // NOT an input to the gate below — that reads each source's own `quoteFound`,
  // because the evidence class is a property of the page and not of the pass. This
  // is carried for the prose that describes the pass: `refutedBacking` and
  // `sourceReach` both read it.
  const quotesCheckable = typeof o.quotesCheckable === 'boolean' ? o.quotesCheckable : undefined

  // The same demotion the live path applies — and now literally the same function,
  // because these were two copies of three predicates and only one of them was
  // updated when the gate got stricter. A document written by an older build
  // carries `refuted` readings backed by a single domain; restoring them as
  // `refuted` would keep exactly the forgeries the live gate refuses, reachable by
  // doing nothing more exotic than reloading the page.
  //
  // The same function means the same STORED inputs on both sides, and all of them
  // ride on the SOURCES: the artefact signatures the collapse reads and the
  // `quoteFound` each bar is chosen by. The collapse and the bar are therefore
  // re-derived here from the document rather than re-decided. A document written
  // before those fields existed carries none of them, and every absence reads as
  // the strictest thing it could mean — no artefact matches, no quote was checked
  // against any page, and a source with neither a signature nor a DOI in its URL
  // counts toward neither bar, because nothing on disk can say whether it is a
  // source of its own or the copy beside it under another masthead.
  //
  // No `SCHEMA` bump: this is a stricter READ of data already on disk, it is
  // non-destructive — the sources survive and `claimedReading` records what was
  // claimed — and it is what the live path does with the same input.
  //
  // The stored claim itself is taken as written. Before D322 the live path kept
  // its fill for a missing reading here as a claimed `nothing-found`, and that
  // record is, field for field, the one a pass that did report nothing found
  // writes. What the model returned is kept nowhere, not on the record and not in
  // the call's receipt, so no read can tell the two apart, and dropping the claim
  // from both would erase the true ones with the false.
  const claimed =
    typeof o.claimedReading === 'string' && READING_OK.has(o.claimedReading)
      ? (o.claimedReading as SourceReading)
      : undefined
  let claimedReading = claimed
  if (reading === 'refuted' && !refutedIsEarned(sources)) {
    claimedReading = 'refuted'
    reading = 'mixed'
  }

  // Rebuilt by the one function the live pass stored them with, so a passage
  // ref names the same cite on both sides of a reload (D146).
  const passageRefs = (corpus?.cites ?? []).map(passageRef)
  const refs = new Set<string>([...sources.map((s) => canonicalUrl(s.url)), ...passageRefs])
  // A ref stored before D146 is the call-local `p3`, whose table went with the
  // call. It names a passage only where the record cites exactly ONE: the live
  // gate admitted a `p3` only if it was one of this pass's cited passages, so
  // with one cite there was nothing else it could have been — the inference the
  // v2 → v3 stamp rests on. With two or more it could be any of them, and the
  // document-level numbers the codec used to read it against are the wrong
  // numbering: that read dropped the entry or re-attached it to another passage.
  // So it is dropped, with the `measuredNovelty` that rested on it. No step in
  // `MIGRATIONS` rewrites it: this read is the one place a legacy ref is decided,
  // whichever door the document came in by.
  const legacy = passageRefs.length === 1 ? passageRefs[0] : undefined
  const priorArt = Array.isArray(o.priorArt)
    ? (o.priorArt.map((x) => asPriorArt(x, refs, legacy)).filter(Boolean) as PriorArt[]).slice(0, 4)
    : []

  const rawNovelty = unit(o.measuredNovelty)
  // Down only, and only with prior art behind it. See `Verification`.
  const measuredNovelty =
    priorArt.length > 0 && rawNovelty !== undefined && rawNovelty <= novelty
      ? rawNovelty
      : undefined

  const queries = strings(o.queries, 8, 300)
  const seen = Math.max(0, Math.trunc(Number(o.seen) || 0))
  const storedScope =
    typeof o.scope === 'string' && SCOPE_OK.has(o.scope) ? (o.scope as VerifyScope) : 'web'
  // Re-derived where the record proves it, as the refuted gate above is applied. A
  // documents-only check that admitted no passage was stored as `web` by every
  // build before D321, which read the scope off the admitted passages rather than
  // off what the pass declared. The record still says what it was: passages from
  // its documents were offered (`corpus.offered`), and it carries no trace of a
  // search — no query, no result, no web source, and no `quotesCheckable`, which is
  // written on every pass that declared one. A searching pass always leaves one of
  // those, since the gate abandons a search that returned nothing unless a passage
  // was admitted, and a passage admitted beside a search is `both`.
  //
  // No `SCHEMA` bump: the shape is unchanged and nothing is dropped. Only the
  // label moves, to the one the live path now writes, so a re-check after this
  // build does not read as a change of scope against the older record.
  const scope =
    storedScope === 'web' &&
    !!corpus?.offered &&
    quotesCheckable === undefined &&
    !queries.length &&
    !seen &&
    !sources.length
      ? 'corpus'
      : storedScope

  const failures =
    Array.isArray(o.failures) && o.failures.length ? strings(o.failures, 4, 200) : undefined
  // Why a `not-checkable` is the gate's (D326). Kept only where the live gate can
  // have written it: beside that reading, on a check that declared a search, with
  // a failure on the record. And re-derived, as the scope above is, where a record
  // from before it proves it: a claimed absence under `not-checkable` is written by
  // the failed-search branch of the gate and by no other, which before D322
  // claimed every absence it demoted, given or filled. No `SCHEMA` bump: an older
  // build reads past the field, and this read only drops a marker the record cannot
  // back and adds one the record proves. The reading never moves.
  const demotedFor =
    reading === 'not-checkable' &&
    scope !== 'corpus' &&
    !!failures?.length &&
    (o.demotedFor === 'failed-search' || claimedReading === 'nothing-found')
      ? ('failed-search' as const)
      : undefined

  const model = asModelStamp(o.model)
  // Dropped rather than guessed at. A check is the strongest claim this tool
  // makes, and `refuted` is quote-backed on one provider and opened-page-backed
  // on the other, so a reading with no model behind it cannot be weighed. The
  // v2→v3 step fills every stored one, so reaching here means a hand edit.
  if (!model) return undefined

  const retained = textRetention('persistence', o.textTruncations, o.textRetentionVersion)
  const summary = reading !== 'unsourced' ? retained.text('summary', o.summary) : undefined
  return {
    ...(text(o.opId, 60) ? { opId: text(o.opId, 60) } : {}),
    ...(text(o.claimAtCheck, 1600) ? { claimAtCheck: text(o.claimAtCheck, 1600) } : {}),
    ...(signature(o.claimHashAtCheck) ? { claimHashAtCheck: signature(o.claimHashAtCheck) } : {}),
    ...(text(o.labelAtCheck, 200) ? { labelAtCheck: text(o.labelAtCheck, 200) } : {}),
    ...(nodeOrdinal(o.nodeOrdinalAtCheck) !== undefined
      ? { nodeOrdinalAtCheck: nodeOrdinal(o.nodeOrdinalAtCheck) }
      : {}),
    reading,
    ...(claimedReading && claimedReading !== reading ? { claimedReading } : {}),
    ...(demotedFor ? { demotedFor } : {}),
    // Dropped for an unsourced reading, on disk as in memory: prose whose every
    // citation was deleted is the one part of this record nobody should read.
    ...(summary ? { summary, ...retained.fields() } : {}),
    sources,
    ...(corpus ? { corpus } : {}),
    ...(corpusSearched ? { corpusSearched } : {}),
    priorArt,
    ...(measuredNovelty !== undefined ? { measuredNovelty } : {}),
    ...(measuredNovelty !== undefined ? { noveltyNote: text(o.noveltyNote, 300) } : {}),
    scope,
    queries,
    seen,
    dropped: Math.max(0, Math.trunc(Number(o.dropped) || 0)),
    ...(o.partial === true ? { partial: true } : {}),
    ...(failures ? { failures } : {}),
    // Clamped backwards, like `birth`: a future timestamp is fine for a record
    // but it would print as a date nobody has reached yet.
    // An evidence event's order is substantive: collapsing several fresh checks
    // onto the animation settle time can let an older rebase appear to cover a
    // genuinely later check. Clamp only future clocks, never age valid events.
    checkedAt: storedTime(o.checkedAt, clock),
    model,
    ...(quotesCheckable !== undefined ? { quotesCheckable } : {}),
    ...(text(o.judgeNote, 300) ? { judgeNote: text(o.judgeNote, 300) } : {}),
  }
}

/**
 * Validate a node's complete evidence ledger and restore its one canonical head.
 *
 * `verificationHistory` stores the old rows only; `verification` is the current
 * row. A hand-edited document may duplicate the head in the history or lose the
 * head while keeping older valid rows. The former is collapsed by operation id
 * (or by the legacy timestamp/model/reading tuple), and the latter promotes the
 * newest valid historical row instead of discarding every paid check.
 */
function asVerificationChain(
  history: unknown,
  current: unknown,
  novelty: number,
  clock: StoredClock,
): { verification?: Verification; verificationHistory?: Verification[] } {
  const rows = Array.isArray(history)
    ? history
        .map((row) => asVerification(row, novelty, clock))
        .filter((row): row is Verification => !!row)
    : []
  const head = asVerification(current, novelty, clock)
  const unique = mergeVerificationEvents(rows, head ? [head] : undefined)
  if (!unique.length) return {}
  const verification = unique.pop()!
  return {
    verification,
    ...(unique.length ? { verificationHistory: unique } : {}),
  }
}

/**
 * A check arriving from OUTSIDE this codec, held to exactly this codec's bar.
 *
 * `core/mergeRequest.ts` takes a file another person exported and
 * `semanticApply.ts` puts its nodes on the live graph — a second door onto the
 * document that does not pass through `readDoc`. Its validator checks the SHAPE
 * of a semantic node and never looked at `evidence.verification`, so a
 * hand-edited request could carry a `refuted` reading whose sources never met
 * `refutedIsEarned`, or citations no search returned, straight onto a claim —
 * and `taint.ts` carries a forged refutation down the whole subtree. Invariants
 * 1 and 2 with a door left open beside them.
 *
 * This is the SAME function the shelf reads through, exported rather than
 * restated. The comment on `asVerification` records what happened the last time
 * these were two copies: only one got stricter, and the weaker one was reachable
 * by reloading the page. A third spelling for imports would be that again, with
 * the loosest of three reached by opening somebody's file.
 *
 * `offset: 0` because there is no save envelope to translate against — the
 * incoming times are the writer's own, and clamping a future one to now is what
 * the stored path does with a clock it cannot trust either.
 */
export function admitVerificationChain(
  history: unknown,
  current: unknown,
  novelty: number,
  now: number = Date.now(),
): { verification?: Verification; verificationHistory?: Verification[] } {
  return asVerificationChain(history, current, novelty, { now, offset: 0 })
}

/**
 * EVERY per-node record this codec validates, held to this codec's shapes.
 *
 * Four rounds of review found this same door open one field at a time —
 * `verification`, then `verificationArchive`, then `bridges`, then the typed
 * edges — and each was fixed as the field it was. `outcome` was the fifth, and
 * the fifth is where fixing them one at a time stops being defensible: a forged
 * `{ bearing: 'refuted' }` with no observation behind it is read as a RECORDED
 * result by `liveChannelOf`, carried down the subtree by `taint.ts`, and counted
 * as the `observed` rung by `bridgeStanding` — the highest thing a candidate can
 * earn, from a line somebody typed into a file.
 *
 * So this is the whole list rather than the newest gap, taken from
 * `sanitizeSession`'s own node pass, which is the only definition of what this
 * app will believe off disk. Seven records were reaching the live graph
 * ungated: `evidenceRebases`, `panel`, `audit`, `outcome`, `foreignFrom`,
 * `model` and `elaboration`, plus `resolvesBy`'s finite-and-positive rule and
 * the `compact === true` coercion that decides whether an elaboration is read at
 * all. The verification chain is already gated by `admitVerificationChain` and
 * stays there, because its call site needs the demotion note beside it.
 *
 * `offset: 0`, like the two wrappers above: the writer's clock is not this
 * document's clock, and an import carries no envelope to translate from.
 */
export function admitNodeRecords(
  raw: Record<string, unknown>,
  now: number = Date.now(),
): Pick<
  ReasonNode,
  | 'compact'
  | 'elaboration'
  | 'parentId'
  | 'bridgeFrom'
  | 'createdOrdinal'
  | 'reasoning'
  | 'evidence'
  | 'nextSteps'
  | 'confidence'
  | 'novelty'
  | 'tension'
  | 'terminalReason'
  | 'terminalKind'
  | 'kind'
  | 'origin'
  | 'createdAt'
  | 'editedAt'
  | 'evidenceRebases'
  | 'panel'
  | 'audit'
  | 'outcome'
  | 'resolvesBy'
  | 'foreignFrom'
  | 'model'
> {
  const compact = raw.compact === true ? true : undefined
  const settled = now - SETTLED_MS
  const clock: StoredClock = { now, offset: 0 }
  // The clamped rating, computed once because `asOutcome` falls back to it for
  // a missing or unreadable `confidenceBefore`. Handed the RAW value it wrote
  // `confidenceBefore: 2` beside a node clamped to 1, and the next reload — a
  // fresh clamp over a stored 1 — moved the outcome to 1 as well, so an
  // untouched checkout came back dirty one load after the import. This was a
  // `confidence` PARAMETER until the clamp arrived — the caller's reading of a
  // field this function now reads for itself, which is exactly how the two
  // came to disagree.
  const held = score(raw.confidence, 0.6)
  return {
    compact,
    elaboration: compact ? asElaboration(raw.elaboration, now) : undefined,
    // A compact node defers every next step. The codec's words for keeping a
    // hand-edited array beside one: "two conflicting authored records".
    //
    // Through `strings` and not an `Array.isArray` cast, which asserted the
    // ELEMENT type rather than checking it: `nextSteps: [{}]` passed, landed on
    // the live node, and `NodeCard` renders each entry as a React child — a
    // thrown "objects are not valid as a React child" that takes the canvas with
    // it, while `falsification.ts` joins the same array into the text an index is
    // built from. `strings` drops what is not a string, because `text` answers
    // '' for it and the filter takes that out.
    nextSteps: compact ? [] : strings(raw.nextSteps, NEXT_STEPS_CAP, NEXT_STEP_MAX),
    // The other two the door was handing straight to a renderer. `reasoningOf`
    // returns the base kernel unchanged where there is no elaboration, and
    // `NodeCard` prints it and every `evidence` entry as a React child — so an
    // object in either throws and takes the canvas with it. Not covered by the
    // `nextSteps` fix beside them, which is the whole lesson: the sibling three
    // lines away had the same defect.
    //
    // And the three STRUCTURAL fields beside them, which nothing on this door
    // covered and which two readers dereference before anything validates them.
    //
    // `parentId` and `bridgeFrom` are looked up as property KEYS, and JavaScript
    // coerces: a numeric `1` finds the node stored under `"1"`, so
    // `validateTopology`'s "names a node that exists" check passes and the
    // number travels on into `order`, where the codec drops it and normalises
    // the child's parent to null — two roots and a tree that differs from the
    // commit it came from, a reload later. Coerced to the codec's own answer
    // instead: a non-string parent becomes null, which `validateTopology` then
    // refuses LOUDLY as a second root.
    //
    // `createdOrdinal` is arithmetic rather than a lookup, which is worse:
    // `applySemanticSnapshot` computes `(createdOrdinal ?? -1) + 1`, so a string
    // makes `"bad1"` and `Math.max` returns NaN. Every node added afterwards
    // takes a NaN ordinal and persistence renumbers the lot. `nodeOrdinal` is
    // the codec's own rule — a non-negative safe integer or nothing. This field
    // was named as a residual when the closed sets landed; this is it closed.
    parentId: typeof raw.parentId === 'string' ? raw.parentId : null,
    // `bridgeFrom` is a LIST of extra parents, so it is the elements that are
    // looked up and the elements that are filtered. Existence stays with
    // `validateTopology`, which REFUSES a name that is not on the graph — a drop
    // here would quietly turn that refusal into a missing edge.
    bridgeFrom: onlyStrings(raw.bridgeFrom).length ? onlyStrings(raw.bridgeFrom) : undefined,
    createdOrdinal: nodeOrdinal(raw.createdOrdinal),
    reasoning: text(raw.reasoning, REASONING_MAX),
    evidence: onlyStrings(raw.evidence),
    // The three ratings, which `makeNode` clamps and this door did not. A
    // `confidence: 2` landed raw on an EXISTING node — `materialiseNode` spreads
    // over the prior node rather than rebuilding it — sat there until the next
    // reload, and then became 1, so an untouched checkout came back dirty
    // against a head nobody had edited. `makeNode`'s own defaults.
    confidence: held,
    novelty: score(raw.novelty, 0.4),
    tension: score(raw.tension, 0.1),
    // `terminalReason` for the reason above it — `NodeCard` prints it too — and
    // `terminalKind` on the codec's own rule, which is stricter than a closed
    // set: the word means one thing and only on a closed path, so it needs the
    // terminal state under it. The codec has validated this since the `...n`
    // spread was found carrying junk through; the door never asked.
    terminalReason: typeof raw.terminalReason === 'string' ? raw.terminalReason : undefined,
    terminalKind:
      raw.terminalKind === 'constraint' && raw.terminal === true ? 'constraint' : undefined,
    // The two CLOSED SETS, and the codec says why in one line: both are index
    // lookup keys, so an unrecognised value renders `undefined` in the inspector
    // and in an exported document. A snapshot carrying `kind: 'bogus'` drew a
    // blank label until the next reload, where the codec quietly made it
    // `mechanism` — and the semantic head still hashed the word nobody could
    // read, so an untouched checkout came back dirty.
    kind:
      typeof raw.kind === 'string' && KIND_OK.has(raw.kind) ? (raw.kind as NodeKind) : 'mechanism',
    origin:
      typeof raw.origin === 'string' &&
      (raw.origin === 'seed' || raw.origin === 'manual' || NODE_ORIGIN_OK.has(raw.origin))
        ? (raw.origin as NodeOrigin)
        : 'manual',
    // Clamped BACKWARDS, never rewritten to now, for the reason the codec gives:
    // a future-dated birth is a node the renderer has not started growing, so
    // the canvas comes back blank while still being clickable.
    createdAt: storedTime(raw.createdAt, clock, settled),
    editedAt: Number(raw.editedAt) ? storedTime(raw.editedAt, clock) : undefined,
    evidenceRebases: asEvidenceRebases(raw.evidenceRebases, { now, offset: 0 }),
    panel: admittedPanel(
      asPanel(raw.panel, now - SETTLED_MS),
      Number(raw.editedAt) ? storedTime(raw.editedAt, clock) : undefined,
    ),
    audit: admittedAudit(
      asAudit(raw.audit, now - SETTLED_MS),
      asModelStamp(raw.model),
      Number(raw.editedAt) ? storedTime(raw.editedAt, clock) : undefined,
    ),
    outcome: asOutcome(raw.outcome, now, held),
    // A date, not a clock, and deliberately not clamped to now — an overdue
    // promise is the resolution queue's most useful row. The same rule the
    // codec applies, restated nowhere: this IS that line.
    resolvesBy:
      Number.isFinite(Number(raw.resolvesBy)) && Number(raw.resolvesBy) > 0
        ? Number(raw.resolvesBy)
        : undefined,
    foreignFrom: asForeignRef(raw.foreignFrom, now),
    model: asModelStamp(raw.model),
  }
}

/** The archive of checks on claims that are gone, held to the same bar and for
 *  the same reason: an import is a door onto this document too. */
export function admitVerificationArchive(
  v: unknown,
  now: number = Date.now(),
): VerificationArchiveEntry[] {
  return asVerificationArchive(v, { now, offset: 0 })
}

function asVerificationArchive(v: unknown, clock: StoredClock): VerificationArchiveEntry[] {
  if (!Array.isArray(v)) return []
  const rows: VerificationArchiveEntry[] = []
  for (const raw of v) {
    if (!raw || typeof raw !== 'object') continue
    const o = raw as Record<string, unknown>
    const nodeId = text(o.nodeId, 60)
    const noveltyAtCheck = unit(o.noveltyAtCheck)
    if (!nodeId || noveltyAtCheck === undefined) continue
    // Every archived row crosses the same citation/refutation gate as a row on
    // a live node. Node deletion is never a weaker route into the document.
    const verification = asVerification(o.verification, noveltyAtCheck, clock)
    if (!verification) continue
    rows.push({ nodeId, noveltyAtCheck, verification })
  }
  return mergeVerificationArchive(rows)
}

const SURVEY_PART_OK = new Set<string>(SURVEY_PARTS)

/**
 * A survey of the seed, off disk — whole, or nothing.
 *
 * The live pass's rules, re-applied to a document that is untrusted whatever
 * wrote it: a source is an http(s) URL or it is not a source, a row rests on a
 * source THIS record carries or it is set aside (an `untested` row alone may rest
 * on none), and prose with no source left behind it is dropped with the rows. A
 * survey with no model stamp is dropped outright, for `asVerification`'s reason:
 * a search whose author cannot be named cannot be weighed (invariant 3).
 *
 * Optional and ADDITIVE, so no `SCHEMA` bump — D118's `corpusSearched` rule. A
 * document written before surveys existed has none, which is exactly how every
 * seed read before this; nothing already on disk is re-read differently.
 */
function asSurvey(v: unknown, clock: StoredClock): FieldSurvey | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const model = asModelStamp(o.model)
  const opId = text(o.opId, 60)
  const questionAtSurvey = text(o.questionAtSurvey, 1600)
  const questionHashAtSurvey = signature(o.questionHashAtSurvey)
  if (!model || !opId || !questionAtSurvey || !questionHashAtSurvey) return undefined

  const sources: SurveySource[] = []
  const keys = new Set<string>()
  for (const raw of Array.isArray(o.sources) ? o.sources : []) {
    if (sources.length >= SURVEY_SOURCE_CAP) break
    if (!raw || typeof raw !== 'object') continue
    const s = raw as Record<string, unknown>
    const url = text(s.url, 2000)
    if (!url || !isHttpUrl(url) || keys.has(canonicalUrl(url))) continue
    keys.add(canonicalUrl(url))
    sources.push({
      url,
      title: text(s.title, 300) || url,
      via: s.via === 'fetch' ? 'fetch' : 'search',
    })
  }

  const retained = textRetention('persistence', o.textTruncations, o.textRetentionVersion)
  const rows: SurveyRow[] = []
  const perPart = new Map<SurveyPart, number>()
  let setAside = Math.max(0, Math.trunc(Number(o.rowsSetAside) || 0))
  for (const [i, raw] of (Array.isArray(o.rows) ? o.rows : []).entries()) {
    if (!raw || typeof raw !== 'object') continue
    const r = raw as Record<string, unknown>
    if (typeof r.part !== 'string' || !SURVEY_PART_OK.has(r.part)) continue
    const part = r.part as SurveyPart
    const count = perPart.get(part) ?? 0
    if (count >= SURVEY_PART_CAP[part]) continue
    const said = retained.text(`rows[${i}].text`, r.text, SURVEY_TEXT_CHARS.row)
    if (!said) continue
    // Only refs this record's own sources can answer. The live gate wrote no
    // other kind, so anything else is a hand edit pointing at nothing.
    const refs = [
      ...new Set(
        strings(r.refs, SURVEY_ROW_REFS, 2000)
          .map((ref) => canonicalUrl(ref))
          .filter((ref) => keys.has(ref)),
      ),
    ]
    if (part !== 'untested' && refs.length === 0) {
      setAside++
      continue
    }
    const design = text(r.design, SURVEY_TEXT_CHARS.design)
    perPart.set(part, count + 1)
    rows.push({ part, text: said, ...(design ? { design } : {}), refs })
  }
  // Prose whose every source is gone goes with them, on disk as in the pass.
  const summary = sources.length
    ? retained.text('summary', o.summary, SURVEY_TEXT_CHARS.summary)
    : ''
  if (!sources.length) {
    setAside += rows.length
    rows.length = 0
  }
  return {
    opId,
    questionAtSurvey,
    questionHashAtSurvey,
    ...(summary ? { summary } : {}),
    rows,
    sources,
    queries: strings(o.queries, SURVEY_QUERY_CAP, 300),
    searches: Math.max(0, Math.trunc(Number(o.searches) || 0)),
    seen: Math.max(0, Math.trunc(Number(o.seen) || 0)),
    dropped: Math.max(0, Math.trunc(Number(o.dropped) || 0)),
    rowsSetAside: setAside,
    ...(o.partial === true ? { partial: true } : {}),
    ...(Array.isArray(o.failures) && o.failures.length
      ? { failures: strings(o.failures, 4, 200) }
      : {}),
    surveyedAt: storedTime(o.surveyedAt, clock),
    model,
    ...retained.fields(),
  }
}

/** The seed's survey ledger: `asVerificationChain`'s rule — the newest valid row
 *  is the live one, and a lost head promotes the newest valid earlier row rather
 *  than discarding every paid survey behind it. */
function asSurveyChain(
  history: unknown,
  current: unknown,
  clock: StoredClock,
): Pick<ReasonNode, 'survey' | 'surveyHistory'> {
  const rows = Array.isArray(history)
    ? history.map((row) => asSurvey(row, clock)).filter((row): row is FieldSurvey => !!row)
    : []
  const head = asSurvey(current, clock)
  const unique = mergeSurveyEvents(rows, head ? [head] : undefined)
  if (!unique.length) return {}
  const survey = unique.pop()!
  return { survey, ...(unique.length ? { surveyHistory: unique } : {}) }
}

function asEvidenceRebase(v: unknown, clock: StoredClock): EvidenceRebase | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const opId = text(o.opId, 60)
  const hasVerificationOpId = Object.prototype.hasOwnProperty.call(o, 'verificationOpId')
  const verificationOpId = text(o.verificationOpId, 60)
  const hasVerificationEventIndex = Object.prototype.hasOwnProperty.call(
    o,
    'verificationEventIndex',
  )
  const verificationEventIndex = nodeOrdinal(o.verificationEventIndex)
  const verificationAt = Number(o.verificationAt)
  const rebasedAt = Number(o.rebasedAt)
  if (
    !opId ||
    (hasVerificationOpId && !verificationOpId) ||
    (hasVerificationEventIndex && verificationEventIndex === undefined) ||
    !Number.isFinite(verificationAt) ||
    !Number.isFinite(rebasedAt) ||
    verificationAt <= 0 ||
    rebasedAt <= 0 ||
    (!verificationOpId && verificationEventIndex === undefined && rebasedAt < verificationAt)
  )
    return null
  const affected = strings(o.affected, 260, 60)
  const produced = strings(o.produced, 5, 60)
  // The live writer reaches this record only after at least one affected claim
  // and one new head exist. Empty arrays would let a fabricated no-op suppress
  // the mechanical stale notice even though the live path refuses that state.
  if (
    !affected.length ||
    !produced.length ||
    new Set(affected).size !== affected.length ||
    new Set(produced).size !== produced.length
  )
    return null
  return {
    verificationAt: storedTime(verificationAt, clock),
    ...(verificationOpId ? { verificationOpId } : {}),
    ...(verificationEventIndex !== undefined ? { verificationEventIndex } : {}),
    opId,
    // Unlike node animation clocks, this is the displayed completion time. New
    // rows prove ordering through retained op receipts; legacy rows still use
    // the conservative temporal gate below.
    rebasedAt: storedTime(rebasedAt, clock),
    affected,
    // The admission path has the same hard ceiling. Restoring a sixth id would
    // let hand-edited bytes claim a result the live app would have discarded.
    produced,
  }
}

function asEvidenceRebases(v: unknown, clock: StoredClock): EvidenceRebase[] | undefined {
  if (!Array.isArray(v)) return undefined
  const rows = v
    .map((row) => asEvidenceRebase(row, clock))
    .filter((row): row is EvidenceRebase => !!row)
  return rows.length ? rows : undefined
}

function evidenceRebaseMatchesOp(
  nodeId: string,
  row: EvidenceRebase,
  op: Op,
  nodes: Readonly<Record<string, ReasonNode>>,
  verification: Verification | undefined,
  verificationOp: Op | undefined,
  verificationPrecedesRebase: boolean,
): boolean {
  const checkReceiptMatches = Boolean(
    row.verificationOpId &&
    verification?.opId === row.verificationOpId &&
    verificationOp?.id === row.verificationOpId &&
    verificationOp.kind === 'verify' &&
    verificationOp.targetIds.length === 1 &&
    verificationOp.targetIds[0] === nodeId &&
    verificationOp.finishedAt !== undefined &&
    !verificationOp.error &&
    !verificationOp.refused &&
    verificationOp.produced.length === 0 &&
    verificationOp.model &&
    sameModelStamp(verification.model, verificationOp.model) &&
    verificationPrecedesRebase,
  )
  const clocksMatch =
    checkReceiptMatches ||
    (!row.verificationOpId &&
      op.finishedAt !== undefined &&
      op.finishedAt >= op.startedAt &&
      row.verificationAt <= op.startedAt &&
      row.rebasedAt >= op.finishedAt)
  const packetMatches =
    op.kind === 'rebase' &&
    op.targetIds.length === 1 &&
    op.targetIds[0] === nodeId &&
    op.finishedAt !== undefined &&
    op.model !== undefined &&
    !op.error &&
    !op.refused &&
    clocksMatch &&
    row.produced.length === op.produced.length &&
    row.produced.every((id, i) => id === op.produced[i])
  if (!packetMatches || !op.model) return false

  // `ownEntry` throughout this file wherever the KEY came off disk — see its
  // header. A produced id of `"toString"` finds a prototype member, which is
  // truthy, and this would count a function as a surviving head.
  const surviving = row.produced.flatMap((id) => {
    const node = ownEntry(nodes, id)
    return node ? [node] : []
  })
  // A live rebase state always retains at least one replacement head; pruning
  // the last one removes the state and reopens “needs a second look”. Requiring
  // that witness closes the otherwise-indistinguishable forged packet whose ids
  // never existed. Every surviving head must also agree on local authorship.
  return (
    surviving.length > 0 &&
    surviving.every(
      (produced) =>
        produced.parentId === nodeId &&
        produced.origin === 'rebase' &&
        produced.model !== undefined &&
        sameModelStamp(produced.model, op.model!),
    )
  )
}

/** Keep the recent window plus the receipts required by retained accounts. */
function retainedOps(
  ops: readonly Op[],
  nodes: Readonly<Record<string, ReasonNode>>,
  runs: readonly Run[],
  agenda?: InvestigationAgenda,
  assessments?: RunAssessmentWorkspace,
  experiments?: ExperimentWorkspaceState,
  commits: readonly {
    snapshot: { nodes: Record<string, { evidence: SemanticEvidenceRows }> }
  }[] = [],
): Op[] {
  const required = new Set<string>()
  for (const attempt of assessments?.attempts ?? []) required.add(attempt.opId)
  // A conjecture's executable test is named by the pass that proposed it, and a
  // copy under a new id joins the test through that pass in its ledger (D290).
  for (const spec of experiments?.experiments ?? []) required.add(spec.id)
  for (const receipt of experiments?.receipts ?? []) required.add(receipt.experimentId)
  const need = (rows: readonly EvidenceRebase[] | undefined) => {
    for (const row of rows ?? []) {
      required.add(row.opId)
      if (row.verificationOpId) required.add(row.verificationOpId)
    }
  }
  for (const node of Object.values(nodes)) need(node.evidenceRebases)
  // And every rebase a LOCAL COMMIT holds, not only the ones on the live graph.
  //
  // A receipt was kept while its rebase was in front of you and dropped the
  // moment you switched branches — so `MAX_OPS` operations later, on the next
  // save, the op proving an inactive branch's rebuild aged out. Checking that
  // branch back out then filtered the row it could no longer prove, the repaired
  // evidence read as stale again, and the restored tree no longer hashed to the
  // head it came from: `semanticWorkingTreeDirty` called an untouched checkout
  // dirty, and the next one refused. A branch you have not visited lately is
  // still yours, and its receipts are what make its history legible.
  for (const commit of commits) {
    for (const row of Object.values(commit.snapshot?.nodes ?? {})) {
      need(row?.evidence?.evidenceRebases)
    }
  }
  const runIds = new Set(runs.map((run) => run.id))
  for (const run of runs) for (const move of run.moves) required.add(move.opId)
  for (const step of agenda?.steps ?? [])
    for (const attempt of step.attempts) if (attempt.opId) required.add(attempt.opId)
  // A paid pass may have a separate automatic research analysis. Its parentage
  // is part of the account too; retain the exact recorded join, never a guess
  // from nearby timestamps. Walking the links also works for reordered imports.
  const children = new Map<string, string[]>()
  for (const op of ops) {
    const parent = op.narration?.parentAction
    if (parent?.kind === 'run' && runIds.has(parent.id)) required.add(op.id)
    if (parent?.kind !== 'operation') continue
    const siblings = children.get(parent.id)
    if (siblings) siblings.push(op.id)
    else children.set(parent.id, [op.id])
  }
  const pending = [...required]
  for (let index = 0; index < pending.length; index++) {
    for (const id of children.get(pending[index]!) ?? []) {
      if (required.has(id)) continue
      required.add(id)
      pending.push(id)
    }
  }
  const recentFrom = Math.max(0, ops.length - MAX_OPS)
  return ops.filter((op, index) => index >= recentFrom || required.has(op.id))
}

/** Just the field `retainedOps` reads out of a commit's node rows. Structural
 *  rather than an import, so `persist.ts` does not take a runtime edge on the
 *  semantic-git module for one property name. */
interface SemanticEvidenceRows {
  evidenceRebases?: EvidenceRebase[]
}

function asPanelVote(v: unknown): PanelVote | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (typeof o.lens !== 'string' || !LENS_OK.has(o.lens)) return null
  if (typeof o.verdict !== 'string' || !VERDICT_OK.has(o.verdict)) return null
  return {
    lens: o.lens as Lens,
    verdict: o.verdict as PanelVerdict,
    because: text(o.because, 700),
    confidence: unit(o.confidence) ?? 0.5,
  }
}

/**
 * A cross-provider audit, off disk.
 *
 * The model stamp is REQUIRED here, as it is on a `Verification` and for the
 * same reason: this record's entire claim is that a DIFFERENT house examined the
 * claim, and an audit that cannot name who ran it cannot make that claim. There
 * is nothing to fall back to — dropping it is the honest outcome.
 *
 * The verdict is a closed set with no default. An unrecognised string
 * cannot be read as `pass` (that would invent a survivor and inflate the tally)
 * and cannot be read as `objection` (there would be no objection on the graph to
 * point at), so it takes the record with it.
 */
function asAudit(v: unknown, settled: number): Audit | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const model = asModelStamp(o.model)
  if (!model) return undefined
  if (!AUDIT_VERDICTS.includes(o.verdict as Audit['verdict'])) return undefined
  if (o.scope !== undefined && !AUDIT_SCOPES.includes(o.scope as NonNullable<Audit['scope']>))
    return undefined
  const status =
    o.verdict === 'objection' ? 'proposed' : o.verdict === 'pass' ? 'no-objection' : 'needs-context'
  if (o.status !== undefined && o.status !== status) return undefined
  let proofStep: Audit['proofStep']
  if (o.proofStep !== undefined) {
    const p = o.proofStep as Audit['proofStep']
    if (
      !p ||
      typeof p.obligationId !== 'string' ||
      !p.obligationId.trim() ||
      p.obligationId.length > 240 ||
      typeof p.revision !== 'string' ||
      !p.revision.trim() ||
      p.revision.length > 100 ||
      (p.stepId !== null &&
        (typeof p.stepId !== 'string' || !p.stepId.trim() || p.stepId.length > 240))
    )
      return undefined
    proofStep = { obligationId: p.obligationId, revision: p.revision, stepId: p.stepId }
  }
  return {
    model,
    verdict: o.verdict as Audit['verdict'],
    ...(o.scope !== undefined ? { scope: o.scope as Audit['scope'] } : {}),
    ...(o.status !== undefined ? { status } : {}),
    ...(proofStep ? { proofStep } : {}),
    ...(text(o.objectionNodeId, 60) ? { objectionNodeId: text(o.objectionNodeId, 60) } : {}),
    note: text(o.note, 4000),
    // Clamped backwards like a panel's `checkedAt`, so a hand-edited future date
    // cannot make `auditLive` report an audit as newer than a rewrite it predates.
    at: Math.min(Number(o.at) || settled, settled),
  }
}

function asPanel(v: unknown, settled: number): PanelCheck | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const votes = Array.isArray(o.votes)
    ? (o.votes.map(asPanelVote).filter(Boolean) as PanelVote[]).slice(0, 5)
    : []
  if (!votes.length) return undefined
  const lenses = Array.isArray(o.lenses)
    ? o.lenses.filter((x): x is Lens => typeof x === 'string' && LENS_OK.has(x)).slice(0, 5)
    : votes.map((x) => x.lens)
  return {
    lenses: lenses.length ? lenses : votes.map((x) => x.lens),
    votes,
    temper: asTemper(o.temper),
    // Clamped BACKWARDS, like `birth`. A future `checkedAt` leaves a contested
    // strand permanently un-split while still being contested — the one state
    // where the canvas would silently contradict the inspector.
    checkedAt: Math.min(Number(o.checkedAt) || settled, settled),
    // Absent on the demo's two hand-written panels, and the absence prints as
    // "read again under 3 lenses" with no model named — which is true.
    ...(asModelStamp(o.model) ? { model: asModelStamp(o.model) } : {}),
  }
}

function asOutcome(v: unknown, now: number, confidence: number): Outcome | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const observed = text(o.observed, 4000)
  if (!observed) return undefined
  const bearing =
    typeof o.bearing === 'string' && BEARING_OK.has(o.bearing)
      ? (o.bearing as Outcome['bearing'])
      : 'inconclusive'
  // Clamped forward-to-now, not backwards. `recordedAt` is what a descendant is
  // compared against to decide whether it predates the result; a future value
  // would mark every node below it as predating the result forever.
  const recordedAt = Math.min(Number(o.recordedAt) || now, now)
  return {
    observed,
    bearing,
    at: Math.min(Number(o.at) || recordedAt, now),
    recordedAt,
    ...(text(o.claimAtRecord, 4000) ? { claimAtRecord: text(o.claimAtRecord, 4000) } : {}),
    confidenceBefore: unit(o.confidenceBefore) ?? confidence,
    ...(text(o.source, 200) ? { source: text(o.source, 200) } : {}),
  }
}

const CONJECTURE_MOVE_OK = new Set<string>(CONJECTURE_MOVES)
/** Earlier registrations kept per conjecture. Each is a rewrite, and a longer
 *  history than this is a node being edited, not a prediction being pinned. */
const MAX_CONJECTURE_REGISTRATIONS = 24

function asRegistration(v: unknown, clock: StoredClock): ConjectureRegistration | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const contentHash = text(o.contentHash, 16)
  // Exactly what `textHash` writes and nothing else: a registration whose hash
  // could not have come from this app pins no wording at all.
  if (!/^[0-9a-f]{16}$/.test(contentHash)) return undefined
  return { at: storedTime(o.at, clock), contentHash }
}

/**
 * A conjecture's record, off disk — or `undefined`, which `makeNode` reads as
 * "not a conjecture" and restores the node as an ordinary one.
 *
 * All or nothing, like the source-check record: a half-restored conjecture would
 * draw the open-proposal glyph over a claim missing the prediction it is
 * supposed to be tested by. And the admission rule is applied again here, off
 * the SAME function admission uses — a stored prediction the expectation it
 * departs from would share never got past admission honestly, so it does not
 * get past the codec either. A stricter read of stored data needs no `SCHEMA`
 * bump; absence has always meant "not a conjecture".
 */
function asConjecture(
  v: unknown,
  clock: StoredClock,
  novelty: number,
): ConjectureRecord | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const move = typeof o.move === 'string' && CONJECTURE_MOVE_OK.has(o.move) ? o.move : null
  const departsFrom = text(o.departsFrom, RESEARCH_ITEM_CHARS)
  const leap = text(o.leap, RESEARCH_ITEM_CHARS)
  const killTest = text(o.killTest, RESEARCH_ITEM_CHARS)
  const p =
    o.prediction && typeof o.prediction === 'object'
      ? (o.prediction as Record<string, unknown>)
      : {}
  const prediction = {
    condition: text(p.condition, RESEARCH_ITEM_CHARS),
    observable: text(p.observable, RESEARCH_ITEM_CHARS),
    expected: text(p.expected, RESEARCH_ITEM_CHARS),
    consensusExpects: text(p.consensusExpects, RESEARCH_ITEM_CHARS),
  }
  const registered = asRegistration(o.registered, clock)
  if (
    !move ||
    !departsFrom ||
    !leap ||
    !killTest ||
    !prediction.condition ||
    !prediction.observable ||
    !registered ||
    !predictionDiscriminates(prediction)
  ) {
    return undefined
  }
  const registrations = (Array.isArray(o.registrations) ? o.registrations : [])
    .map((r) => asRegistration(r, clock))
    .filter((r): r is ConjectureRegistration => !!r)
    .slice(-MAX_CONJECTURE_REGISTRATIONS)
  const priorArt = (Array.isArray(o.priorArt) ? o.priorArt : [])
    .map((r) => asPriorArtSearch(r, clock, novelty))
    .filter((r): r is PriorArtSearch => !!r)
    .slice(-PRIOR_ART_HISTORY)
  return {
    move: move as ConjectureRecord['move'],
    departsFrom,
    leap,
    prediction,
    killTest,
    anchors: strings(o.anchors, CONJECTURE_MAX_ANCHORS, 60),
    priorArtLeads: strings(o.priorArtLeads, CONJECTURE_MAX_LEADS, RESEARCH_ITEM_CHARS),
    registered,
    ...(registrations.length ? { registrations } : {}),
    ...(priorArt.length ? { priorArt } : {}),
  }
}

const HEX16 = /^[0-9a-f]{16}$/
const PRIOR_ART_CLOSENESS_OK = new Set<string>(['same', 'overlapping', 'adjacent'])
const VIA_OK = new Set<string>(['search', 'fetch'])
const count = (v: unknown): number | undefined => {
  const n = Number(v)
  return Number.isInteger(n) && n >= 0 ? Math.min(n, 9999) : undefined
}

/** One listed page off disk, or nothing. Never repaired: a closeness this app
 *  never writes is not guessed at, because guessing low could turn `stated`
 *  into a null. The caller reads a missing entry as a record it cannot trust. */
function asPriorArtEntry(v: unknown): PriorArtEntry | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const url = text(o.url, 2000)
  if (!url || !isHttpUrl(url)) return undefined
  if (typeof o.closeness !== 'string' || !PRIOR_ART_CLOSENESS_OK.has(o.closeness)) return undefined
  if (typeof o.via !== 'string' || !VIA_OK.has(o.via)) return undefined
  const year = Number(o.year)
  return {
    url,
    title: text(o.title, 300) || url,
    quote: text(o.quote, SOURCE_QUOTE_CHARS),
    closeness: o.closeness as PriorArtEntry['closeness'],
    covers: text(o.covers, 400),
    ...(Number.isInteger(year) && year >= 1000 && year <= 3000 ? { year } : {}),
    via: o.via as PriorArtEntry['via'],
    ...(typeof o.quoteFound === 'boolean' ? { quoteFound: o.quoteFound } : {}),
  }
}

function asBackstopHit(v: unknown): PriorArtBackstopHit | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const url = text(o.url, 2000)
  const leap = unit(o.leap)
  const prediction = unit(o.prediction)
  if (!url || !isHttpUrl(url) || typeof o.via !== 'string' || !VIA_OK.has(o.via)) return undefined
  if (leap === undefined || prediction === undefined) return undefined
  return {
    url,
    title: text(o.title, 300) || url,
    via: o.via as PriorArtBackstopHit['via'],
    leap,
    prediction,
  }
}

/**
 * A prior-art search, off disk — its reading and `measuredNovelty` re-derived
 * from its own entries by `regatePriorArt`, the function the live pass uses, so a
 * document cannot carry a reading its evidence does not.
 *
 * A search with no model, or no hash of the wording it searched, is dropped: it
 * cannot say who searched or for what. A search with an entry this codec cannot
 * read is kept and marked partial, which voids its null — a record partly
 * unreadable can still show what it found, and can never read as "not found"
 * because part of it was lost.
 */
function asPriorArtSearch(
  v: unknown,
  clock: StoredClock,
  novelty: number,
): PriorArtSearch | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const model = asModelStamp(o.model)
  const contentHash = text(o.contentHash, 16)
  const instrument = text(o.instrument, 16)
  if (!model || !HEX16.test(contentHash) || !HEX16.test(instrument)) return undefined

  const rawEntries = Array.isArray(o.entries) ? o.entries.slice(0, PRIOR_ART_MAX_ENTRIES) : []
  const entries = rawEntries.map(asPriorArtEntry).filter((e): e is PriorArtEntry => !!e)
  const b =
    o.backstop && typeof o.backstop === 'object' ? (o.backstop as Record<string, unknown>) : {}
  const hits = (Array.isArray(b.hits) ? b.hits.slice(0, PRIOR_ART_MAX_HITS) : [])
    .map(asBackstopHit)
    .filter((h): h is PriorArtBackstopHit => !!h)
  const unreadable =
    entries.length < rawEntries.length ||
    count(b.pages) === undefined ||
    count(b.close) === undefined ||
    count(o.seen) === undefined
  const failures = [
    ...strings(o.failures, 8, 300),
    ...(unreadable
      ? [
          "Part of this search's stored record could not be read, so its silence is not read either.",
        ]
      : []),
  ]
  const summary = text(o.summary, 600)
  const opId = text(o.opId, 60)

  return regatePriorArt(
    {
      reading: 'unsound',
      entries,
      backstop: {
        pages: count(b.pages) ?? 0,
        close: Math.max(count(b.close) ?? 0, hits.length),
        hits,
      },
      ...(summary ? { summary } : {}),
      queries: strings(o.queries, 16, 300),
      seen: count(o.seen) ?? 0,
      dropped: count(o.dropped) ?? 0,
      ...(failures.length ? { failures: [...new Set(failures)] } : {}),
      ...(o.partial === true || unreadable ? { partial: true } : {}),
      ...(typeof o.quotesCheckable === 'boolean' ? { quotesCheckable: o.quotesCheckable } : {}),
      searchedAt: storedTime(o.searchedAt, clock),
      model,
      contentHash,
      instrument,
      ...(opId ? { opId } : {}),
    },
    novelty,
  )
}

const SWEEP_KIND_OK = new Set<string>(SWEEP_KINDS)

/**
 * One sweep item off disk, or nothing. The live rule is re-applied: an item
 * whose quote was found NOT to be on its page never survived the pass, so it
 * does not survive the codec either — an anomaly nobody said is the one thing a
 * sweep must never hand a reasoning pass.
 */
function asSweepItem(v: unknown, sweepId: string): SweepItem | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const id = text(o.id, 80)
  const url = text(o.url, 2000)
  const statement = text(o.statement, 600)
  const quote = text(o.quote, SOURCE_QUOTE_CHARS)
  if (!id.startsWith(`${sweepId}.`) || !url || !isHttpUrl(url) || !statement || !quote) {
    return undefined
  }
  if (typeof o.kind !== 'string' || !SWEEP_KIND_OK.has(o.kind)) return undefined
  if (typeof o.via !== 'string' || !VIA_OK.has(o.via)) return undefined
  if (o.quoteFound === false) return undefined
  const year = Number(o.year)
  return {
    id,
    kind: o.kind as SweepItem['kind'],
    statement,
    url,
    title: text(o.title, 300) || url,
    quote,
    ...(Number.isInteger(year) && year >= 1000 && year <= 3000 ? { year } : {}),
    via: o.via as SweepItem['via'],
    ...(o.quoteFound === true ? { quoteFound: true as const } : {}),
  }
}

/**
 * A session's field sweeps off disk. A sweep with no model, no node or no
 * instrument is dropped: it cannot say who searched, for what, or under which
 * instructions. Items that fail are dropped one by one, and a sweep that lost
 * one is marked partial, as a prior-art search is — an empty sweep reads as
 * "nothing came back", and that must never be because part of it was lost.
 */
function asSweeps(v: unknown, clock: StoredClock): FieldSweep[] {
  if (!Array.isArray(v)) return []
  const out: FieldSweep[] = []
  for (const raw of v.slice(-SWEEP_HISTORY)) {
    if (!raw || typeof raw !== 'object') continue
    const o = raw as Record<string, unknown>
    const id = text(o.id, 60)
    const nodeId = text(o.nodeId, 60)
    const model = asModelStamp(o.model)
    const instrument = text(o.instrument, 16)
    if (!id || !nodeId || !model || !HEX16.test(instrument)) continue
    const rawItems = Array.isArray(o.items) ? o.items.slice(0, SWEEP_MAX_ITEMS) : []
    const items = rawItems.map((i) => asSweepItem(i, id)).filter((i): i is SweepItem => !!i)
    const unreadable = items.length < rawItems.length || count(o.seen) === undefined
    const opId = text(o.opId, 60)
    const failures = [
      ...strings(o.failures, 8, 300),
      ...(unreadable
        ? [
            "Part of this sweep's stored record could not be read, so its silence is not read either.",
          ]
        : []),
    ]
    out.push({
      id,
      nodeId,
      subjectAtSweep: text(o.subjectAtSweep ?? o.subjectAtSurvey, 2000),
      items,
      queries: strings(o.queries, 16, 300),
      seen: count(o.seen) ?? 0,
      dropped: count(o.dropped) ?? 0,
      unquoted: count(o.unquoted) ?? 0,
      malformed: count(o.malformed) ?? 0,
      overCeiling: count(o.overCeiling) ?? 0,
      ...(failures.length ? { failures: [...new Set(failures)] } : {}),
      ...(o.partial === true || unreadable ? { partial: true } : {}),
      ...(typeof o.quotesCheckable === 'boolean' ? { quotesCheckable: o.quotesCheckable } : {}),
      searchedAt: storedTime(o.searchedAt, clock),
      model,
      instrument,
      ...(opId ? { opId } : {}),
    })
  }
  return out
}

/**
 * An op a build before the rename stored as a `survey`, read as the `sweep` it
 * was — only where a sweep on the session names it, so no other kind is ever
 * relabelled by the name alone.
 */
function legacySweepOp(v: unknown, sweepOps: ReadonlySet<string>): unknown {
  if (!v || typeof v !== 'object') return v
  const o = v as Record<string, unknown>
  return o.kind === 'survey' && typeof o.id === 'string' && sweepOps.has(o.id)
    ? { ...o, kind: 'sweep' }
    : v
}

/** The admission tally's seven counts, each a non-negative integer, or nothing. */
const ADMISSION_FIELDS = [
  'returned',
  'landed',
  'incomplete',
  'undiscriminating',
  'duplicate',
  'overCeiling',
  'anchorsDropped',
] as const satisfies readonly (keyof ConjectureAdmission)[]

function asAdmission(v: unknown): ConjectureAdmission | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const out: Partial<ConjectureAdmission> = {}
  for (const f of ADMISSION_FIELDS) {
    const n = Number(o[f])
    if (!Number.isInteger(n) || n < 0) return undefined
    out[f] = Math.min(n, 999)
  }
  return out as ConjectureAdmission
}

function asForeignRef(v: unknown, now: number): ForeignRef | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const sessionId = text(o.sessionId, 60)
  const nodeId = text(o.nodeId, 60)
  const label = text(o.label, 200)
  if (!sessionId || !nodeId || !label) return undefined
  return {
    sessionId,
    seed: text(o.seed, 400),
    nodeId,
    label,
    claim: text(o.claim, 900),
    at: Math.min(Number(o.at) || now, now),
  }
}

function asBridgeEnd(v: unknown, nodes: Record<string, ReasonNode>, now: number): BridgeEnd | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (o.where === 'foreign') {
    const ref = asForeignRef(o.ref, now)
    // A foreign end is never checked against this graph. That is the whole point
    // of it carrying its own copy.
    return ref ? { where: 'foreign', ref } : null
  }
  const nodeId = text(o.nodeId, 60)
  return nodeId && ownEntry(nodes, nodeId) ? { where: 'local', nodeId } : null
}

/**
 * A link scan's record, off disk.
 *
 * `reading` is validated against a closed list rather than passed through, for
 * the reason every other union here is: an unrecognised reading would reach the
 * card and print as nothing, and a scan whose whole content is one word would be
 * silently blank. A record whose reading does not validate is dropped entirely —
 * the candidate then reads as never scanned, which is true and is a state the UI
 * already has, where a half-record is not.
 */
const LINK_READING_OK = new Set<string>(LINK_READINGS)

function asJoiningSource(v: unknown): JoiningSource | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const url = text(o.url, 2000)
  if (!url || !isHttpUrl(url)) return null
  return { url, title: text(o.title, 300) || url, via: o.via === 'fetch' ? 'fetch' : 'search' }
}

function asLinkage(v: unknown, now: number): StoredLinkage | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  if (typeof o.reading !== 'string' || !LINK_READING_OK.has(o.reading)) return undefined
  const model = asModelStamp(o.model)
  // Same rule as `Verification.model`: a scan with no model behind it is not a
  // scan, and its counts cannot be weighed without knowing whose search produced
  // them.
  if (!model) return undefined

  const intermediates = asIntermediates(o.intermediates)

  const failures = strings(o.failures, 8, 200)
  const index = asIndexLinkage(o.index, now)
  return {
    reading: o.reading as StoredLinkage['reading'],
    intermediates,
    ...(text(o.mechanism, 1200) ? { mechanism: text(o.mechanism, 1200) } : {}),
    ...(text(o.via, 120) ? { via: text(o.via, 120) } : {}),
    joinedBy: (Array.isArray(o.joinedBy) ? o.joinedBy : [])
      .map(asJoiningSource)
      .filter((x): x is JoiningSource => !!x)
      .slice(0, 6),
    queries: strings(o.queries, 20, 300),
    aDocs: Math.max(0, Math.trunc(Number(o.aDocs) || 0)),
    cDocs: Math.max(0, Math.trunc(Number(o.cDocs) || 0)),
    depth: o.depth === 'text' ? 'text' : 'titles',
    ...(failures.length ? { failures } : {}),
    ...(index ? { index } : {}),
    scannedAt: Math.min(Number(o.scannedAt) || now, now),
    model,
  }
}

function asIntermediates(v: unknown): { term: string; aDocs: number; cDocs: number }[] {
  return (Array.isArray(v) ? v : [])
    .map((raw) => {
      if (!raw || typeof raw !== 'object') return null
      const r = raw as Record<string, unknown>
      const term = text(r.term, 120)
      if (!term) return null
      return {
        term,
        aDocs: Math.max(0, Math.trunc(Number(r.aDocs) || 0)),
        cDocs: Math.max(0, Math.trunc(Number(r.cDocs) || 0)),
      }
    })
    .filter((x): x is { term: string; aDocs: number; cDocs: number } => !!x)
    .slice(0, 12)
}

/**
 * The index scan's reading, off disk. No model stamp to require — no model ran —
 * but the `source` literal is required in its place: a record that cannot say
 * which index it read is a count of nothing in particular.
 */
function asIndexLinkage(v: unknown, now: number): IndexLinkage | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  if (typeof o.reading !== 'string' || !LINK_READING_OK.has(o.reading)) return undefined
  if (o.source !== 'openalex') return undefined
  return {
    reading: o.reading as IndexLinkage['reading'],
    intermediates: asIntermediates(o.intermediates),
    joinedBy: (Array.isArray(o.joinedBy) ? o.joinedBy : [])
      .map(asJoiningSource)
      .filter((x): x is JoiningSource => !!x)
      .slice(0, 6),
    // Wider than the web scan's cap: an index query is a whole request URL,
    // recorded so the null is auditable, and clipping it mid-parameter would
    // leave a query nobody could re-run.
    queries: strings(o.queries, 20, 800),
    aDocs: Math.max(0, Math.trunc(Number(o.aDocs) || 0)),
    cDocs: Math.max(0, Math.trunc(Number(o.cDocs) || 0)),
    source: 'openalex',
    scannedAt: Math.min(Number(o.scannedAt) || now, now),
  }
}

/** The pair arithmetic, off disk. Both numbers or neither: half a basis would
 *  print a distance beside a blank where the dissimilarity should be. */
function asBasis(v: unknown): BridgeBasis | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const distance = Math.trunc(Number(o.distance))
  const dissimilarity = unit(o.dissimilarity)
  if (!Number.isFinite(distance) || distance < 0 || dissimilarity === undefined) return undefined
  return { distance, dissimilarity }
}

/**
 * A candidate arriving from OUTSIDE this codec, held to this codec's own shape.
 *
 * The third field of a semantic snapshot, and the last one still ungated.
 * `validSemanticSnapshot` checks that `bridges` is an object and never looked
 * inside it — so a hand-edited merge request could carry a foreign end with no
 * `ref`, which `bridgeReadable` waves through (it only asks whether a LOCAL end
 * still names a node) and which five readers then dereference as `e.ref.label`.
 * A crash on the Breakthroughs tab, from a file somebody sent.
 *
 * The same function the shelf reads through, exported rather than restated, for
 * the reason `admitVerificationChain` beside it gives: a second spelling is a
 * second thing to keep in step, and the loosest of them is the one a hand-edited
 * file reaches for. A candidate that does not survive it is dropped rather than
 * refused — a malformed pair is not a claim, and losing it costs a scan hint.
 */
/**
 * The four PROFILE fields, held to the codec's rules at the import door.
 *
 * `validSemanticProfile` checks the shapes and none of the bounds, so a snapshot
 * carrying thirteen directives, a 500-character one, or a 90-character project
 * name was applied and COMMITTED as it arrived — and the next reload ran
 * `asBrief` and `projectLabel` over it and shortened the lot. An untouched
 * working tree then differed from the head the merge had just written, which is
 * what makes the following checkout refuse.
 *
 * `seed` and `frame` are passed through, and that is not an omission: the codec
 * bounds neither. `frame` is a closed set `validSemanticProfile` already
 * enforces, and the session seed has no length rule anywhere — inventing one
 * here would be this door disagreeing with the codec in the other direction.
 */
export function admitProfile(profile: SemanticProfile): SemanticProfile {
  const project = projectLabel(profile.project)
  return {
    seed: profile.seed,
    frame: profile.frame,
    brief: asBrief(profile.brief),
    project: project ?? null,
  }
}

export function admitBridges(
  rows: readonly unknown[],
  nodes: Record<string, ReasonNode>,
  now: number = Date.now(),
): Bridge[] {
  // ONE candidate per id AND one per endpoint pair, the first of each to arrive.
  //
  // Two keys because the app is held to two, and holding the door to one of them
  // was round forty-two's fix leaving round forty-four's half undone.
  //
  // The ID is the HANDLE, and every action on the Breakthroughs tab is ID-only:
  // `dropBridge` is `filter((b) => b.id !== id)` and removes BOTH of a colliding
  // pair, `patchBridge` is `findIndex((b) => b.id === id)` and writes the
  // realisation onto whichever came first however the second card was pressed,
  // and React keys the two cards the same. `asBridge` admits whatever `id` a row
  // carried and mints one only when it carried none, so two well-formed rows can
  // share one.
  //
  // `pairKey` is the MEANING, and it is `addBridges`' own rule: the live path
  // filters every scan result through a set of the pairs already on the graph,
  // so one endpoint pair is one candidate. Two branches that scanned the same
  // pair mint different ids and both survive an id check — and then the tab
  // shows the same candidate twice, can be charged twice to search or realise
  // it, counts it twice in a report, and spends a `MAX_BRIDGES` slot that would
  // otherwise hold a distinct candidate. A rule the live path enforces and the
  // door does not is a rule a merge walks around.
  //
  // Dropped on ARRIVAL order, before the sort, so which of two survives is a
  // property of the file and not of a rung that moves when the graph does.
  const byId = new Map<string, Bridge>()
  const pairs = new Set<string>()
  for (const row of rows) {
    const bridge = asBridge(row, nodes, now)
    if (!bridge || byId.has(bridge.id)) continue
    const key = pairKey(bridge)
    if (pairs.has(key)) continue
    pairs.add(key)
    byId.set(bridge.id, bridge)
  }
  // Sorted and capped, which is what makes this the shelf's rule and not a
  // second one: `sanitizeSession` reads through here, so a merge cannot commit
  // every candidate and have the first save drop all but forty — an untouched
  // working tree differing from the head that was just written, and a checkout
  // that then refuses. `sortBridges` is the rung order the cap is meaningful in:
  // truncating an unsorted list would discard by arrival rather than by
  // standing.
  return sortBridges([...byId.values()], nodes).slice(0, MAX_BRIDGES)
}

/**
 * The typed edges of ONE node, held to this codec's own shape.
 *
 * Same door, fourth field. A merge can pair one branch's deletion of node B
 * with the other branch's new `dependsOn: [B]` on node A and raise no conflict,
 * because neither side wrote the same field — and `validateTopology` checks
 * `parentId` and `bridgeFrom` but not the two edges P5 added beside them. The
 * dangling edge is then silently inert in `standingMap`, recorded in the merge
 * COMMIT, and dropped by `asEdges` on the way off disk: three copies of one
 * graph that disagree, so the working tree reads dirty against a commit nobody
 * edited and a checkout refuses.
 *
 * Exported rather than restated, for the reason `admitBridges` above gives. The
 * cap and the de-duplication come with it, so a hand-edited file cannot land
 * nine edges that the shelf will silently make eight.
 */
export function admitEdges(
  ids: unknown,
  self: string,
  nodes: Record<string, unknown>,
): string[] | undefined {
  return asEdges(ids, self, nodes)
}

function asBridge(v: unknown, nodes: Record<string, ReasonNode>, now: number): Bridge | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const a = asBridgeEnd(o.a, nodes, now)
  const b = asBridgeEnd(o.b, nodes, now)
  if (!a || !b) return null
  const title = text(o.title, 200)
  if (!title) return null
  return {
    id: text(o.id, 60) || `b_${Math.random().toString(36).slice(2, 10)}`,
    a,
    b,
    title,
    why: text(o.why, 900),
    score: unit(o.score) ?? 0.5,
    ...(asBasis(o.basis) ? { basis: asBasis(o.basis) } : {}),
    createdAt: Math.min(Number(o.createdAt) || now, now),
    ...(text(o.realizedNodeId, 60) ? { realizedNodeId: text(o.realizedNodeId, 60) } : {}),
    ...(asLinkage(o.linkage, now) ? { linkage: asLinkage(o.linkage, now) } : {}),
  }
}

/**
 * One op, off disk, field by field.
 *
 * This used to be a spread with two array filters over it, which meant
 * `thinking`, `error` and anything else a hand-edited document carried arrived
 * unvalidated and uncapped — a 4 MB `thinking` string would be read back into
 * memory and re-serialised on the next autosave.
 */
function asOp(v: unknown, clock: StoredClock): Op | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const narration = asNarration(o.narration)
  const auditCoverage = parseAuditCoverage(o.auditCoverage)
  const id = text(o.id, 60)
  if (!id || typeof o.kind !== 'string' || !OP_OK.has(o.kind)) return null
  const targetIds = strings(o.targetIds, 8, 60)
  const produced = strings(o.produced, MAX_OP_PRODUCED, 60)
  const portfolio = asPortfolio(o.portfolio, targetIds, produced)
  const model = asModelStamp(o.model)
  const spend = asSpend(o.spend)
  // Both directions fail closed. A portfolio without its packet loses the
  // account of which angle authored which node; a sequential op carrying packet
  // metadata claims consolidation that did not happen.
  if (o.kind === 'portfolio') {
    // A packet's one physical pass is the only record from which all of its
    // logical parts inherit authorship, so an anonymous one is not a packet we
    // can restore honestly.
    if (!portfolio || !model) return null
  } else if (Object.prototype.hasOwnProperty.call(o, 'portfolio')) {
    return null
  }
  // `addSpend` writes the pass receipt and its op attribution together. If the
  // two copies disagree on disk, choosing either one would silently substitute
  // a model in the provenance or the estimate ledger.
  if (spend && (!model || !sameModelStamp(model, spend.model))) return null
  const thinking = text(o.thinking, DISK_THINKING_CAP)
  // A provider's category name, kept only on a declined op (D312). Dropped
  // rather than clipped when it is too long to be one: a clipped identifier
  // names no filter, and the message would print it as though it did.
  const category = o.refused === true ? text(o.refusalCategory, 41) : ''
  const refusalCategory = category.length <= 40 ? category : ''
  const readExplorations = Math.trunc(Number(o.readExplorations))
  const startedAt = storedTime(o.startedAt, clock)
  const rawFinishedAt = Number(o.finishedAt)
  const finishedAt = rawFinishedAt ? storedTime(rawFinishedAt, clock) : undefined
  const parsedModelCalls = Object.prototype.hasOwnProperty.call(o, 'modelCalls')
    ? asModelCallReceipts(o.modelCalls, clock, o.kind === 'panel')
    : null
  const modelCalls =
    parsedModelCalls &&
    parsedModelCalls.every(
      (call) => !call.researchContract || call.researchContract.action === o.kind,
    ) &&
    modelCallReceiptsMatchOp(parsedModelCalls, {
      startedAt,
      finishedAt,
      model,
      spend: spend ?? undefined,
    })
      ? parsedModelCalls
      : null
  return {
    id,
    kind: o.kind as Op['kind'],
    targetIds,
    // Operation and evidence clocks share one clamp. A portable document from a
    // clock-ahead device must not keep a future op beside an evidence row moved
    // back to now, or the legitimate row fails its own completion ordering.
    startedAt,
    ...(finishedAt !== undefined ? { finishedAt } : {}),
    produced,
    ...(portfolio ? { portfolio } : {}),
    ...(text(o.error, 300) ? { error: text(o.error, 300) } : {}),
    // Absence has always meant "not refused", which is why these two need no
    // migration — but they must survive a reload, because a run's account reads
    // them back to say a pass was declined rather than empty.
    ...(o.refused === true ? { refused: true } : {}),
    ...(text(o.refusalNote, 300) ? { refusalNote: text(o.refusalNote, 300) } : {}),
    ...(refusalCategory ? { refusalCategory } : {}),
    ...(thinking ? { thinking } : {}),
    ...(o.thinkingClipped === true || (thinking && thinking.length >= DISK_THINKING_CAP)
      ? { thinkingClipped: true }
      : {}),
    ...(Number.isInteger(readExplorations) && readExplorations >= 0
      ? { readExplorations: Math.min(readExplorations, MAX_READ_EXPLORATIONS) }
      : {}),
    // A conjecture pass's tally of what its response offered and what landed.
    // Only on that kind: no other pass counts admissions, and a stray tally on
    // another op would claim a gate ran that never did. No SCHEMA bump —
    // absence has always meant "not counted".
    ...(o.kind === 'conjecture' && asAdmission(o.admission)
      ? { admission: asAdmission(o.admission) }
      : {}),
    // Absent on a demo op, which is the information: the ledger's Model column
    // prints an em dash rather than attributing a hand-written move to a model.
    ...(model ? { model } : {}),
    // The pass's own draw, for the step rail. No SCHEMA bump: absence has always
    // meant "recorded before per-pass rows existed" and still does, and an older
    // build stripping it on save loses a figure the per-model ledger still
    // carries in aggregate — a cache-grade loss, not the user's words. The same
    // gate as the ledger's rows: an unattributed row validates to nothing.
    ...(spend ? { spend } : {}),
    // A malformed, over-budget, temporally impossible or spend-divergent set is
    // omitted WHOLE. Keeping only its valid rows would turn a multi-call op into
    // a false claim that fewer physical requests ran.
    ...(modelCalls ? { modelCalls } : {}),
    ...(o.kind === 'research' && narration ? { narration } : {}),
    ...(o.kind === 'audit' && auditCoverage ? { auditCoverage } : {}),
  }
}

function asNarration(value: unknown): Op['narration'] | undefined {
  if (!value || typeof value !== 'object') return undefined
  const row = value as Record<string, unknown>
  const parent = row.parentAction as Record<string, unknown> | undefined
  if (
    typeof row.purpose !== 'string' ||
    !row.purpose.trim() ||
    row.purpose.length > 4000 ||
    !parent ||
    !['operation', 'run'].includes(String(parent.kind)) ||
    typeof parent.id !== 'string' ||
    !parent.id ||
    parent.id.length > 60
  )
    return undefined
  return {
    purpose: row.purpose,
    parentAction: { kind: parent.kind as 'operation' | 'run', id: parent.id },
    // The words a link carries in place of the receipt it left behind. Bounded
    // well past the voice's largest reply (a run's wrap-up, 900 tokens), and
    // dropped ALONE when malformed: the row is still a sound receipt.
    ...(typeof row.shown === 'string' && row.shown.trim() && row.shown.length <= 8000
      ? { shown: row.shown }
      : {}),
  }
}

/**
 * How a copy came by link, off disk — read defensively, every part optional.
 * Malformed parts are dropped rather than repaired: a count that is not a whole
 * number is no count, and a mark that is not exactly `true` is no mark. What
 * this reads was written here: a link's own bytes and an opened file both lose
 * the mark on the way in (`linkPayload`, `sharedExplorationCopy`), and only
 * opening a link sets it (`linkCopy`).
 */
function asSharedCopy(value: unknown): SharedCopy | undefined {
  if (!value || typeof value !== 'object') return undefined
  const row = value as Record<string, unknown>
  const count = (v: unknown): number =>
    typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 ? v : 0
  const left = row.left && typeof row.left === 'object' ? (row.left as Record<string, unknown>) : {}
  const receipts = count(left.receipts)
  const replayStates = count(left.replayStates)
  const out: SharedCopy = {
    ...(row.readFirst === true ? { readFirst: true as const } : {}),
    ...(receipts || replayStates ? { left: { receipts, replayStates } } : {}),
  }
  return out.readFirst || out.left ? out : undefined
}

/**
 * Packet membership is allowed to outlive a pruned node, just like every older
 * operation's `produced` list. A node that DOES survive, however, must agree
 * with the packet on all three pieces of authorship that can be checked locally.
 */
function portfolioMatchesNodes(op: Op, nodes: Record<string, ReasonNode>): boolean {
  if (op.kind !== 'portfolio') return true
  if (!op.portfolio || !op.model) return false
  for (const part of op.portfolio.parts) {
    for (const id of part.produced) {
      const node = ownEntry(nodes, id)
      if (!node) continue
      if (
        node.parentId !== part.targetId ||
        node.origin !== part.kind ||
        !node.model ||
        !sameModelStamp(node.model, op.model)
      )
        return false
    }
  }
  return true
}

/** An elaboration is paid output of the packet that names it, when retained. */
function elaborationMatchesOp(node: ReasonNode, op: Op): boolean {
  const detail = node.elaboration
  if (!detail || !op.model || !sameModelStamp(detail.model, op.model)) return false
  if (op.kind === 'portfolio') return op.portfolio?.elaboratedTargetId === node.id
  // A progressive packet may fall back mechanically to the ordinary one-angle
  // contract. That request can author the same detail, but checks and other
  // multi-pass records cannot, and a multi-target op is not that fallback.
  return DERIVE_OP_OK.has(op.kind) && op.targetIds.length === 1 && op.targetIds[0] === node.id
}

/**
 * One move of a run, off disk.
 *
 * The `why` is validated against the closed set rather than accepted as text: it
 * is the audit of a decision, and a hand-edited document that could put "looked
 * promising" in front of a reader would defeat the one thing the record exists to
 * prevent.
 */
function asRunMove(v: unknown): RunMove | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const opId = text(o.opId, 60)
  if (!opId || typeof o.kind !== 'string' || !LOGICAL_OP_OK.has(o.kind)) return null
  if (typeof o.why !== 'string' || !MOVE_REASON_OK.has(o.why)) return null
  if (typeof o.outcome !== 'string' || !MOVE_OUTCOME_OK.has(o.outcome)) return null
  const modelPasses = Number(o.modelPasses)
  const agendaRow =
    o.agenda && typeof o.agenda === 'object' ? (o.agenda as Record<string, unknown>) : null
  const agenda =
    agendaRow &&
    typeof agendaRow.stepId === 'string' &&
    agendaRow.stepId.length <= 200 &&
    agendaRow.stepId &&
    typeof agendaRow.attemptId === 'string' &&
    agendaRow.attemptId.length <= 200 &&
    agendaRow.attemptId &&
    typeof agendaRow.rule === 'string' &&
    agendaRow.rule.length <= 2000 &&
    agendaRow.rule
      ? { stepId: agendaRow.stepId, attemptId: agendaRow.attemptId, rule: agendaRow.rule }
      : undefined
  // Agenda reasons have always carried their exact selection rule. Retaining
  // only the label would invent a complete decision record from missing data.
  // Older move reasons predate this metadata and remain readable without it.
  if (o.why === 'agenda' && !agenda) return null
  // The judge's number on a check, whole or dropped: a probability with no stamp
  // naming who gave it is a number nobody can weigh.
  const judgeRow =
    o.judge && typeof o.judge === 'object' ? (o.judge as Record<string, unknown>) : null
  const judgeModel = judgeRow ? asModelStamp(judgeRow.model) : undefined
  const judgeP = judgeRow ? unit(judgeRow.checkability) : undefined
  const judge =
    judgeModel && judgeP !== undefined ? { checkability: judgeP, model: judgeModel } : undefined
  return {
    at: Number(o.at) || 0,
    kind: o.kind as LogicalOpKind,
    // The closing scan targets no node, so an empty id is legal here and only here.
    nodeId: text(o.nodeId, 60),
    label: text(o.label, 90),
    why: o.why as MoveReason,
    opId,
    outcome: o.outcome as RunMove['outcome'],
    produced: Math.trunc(Math.max(0, Number(o.produced) || 0)),
    // New panels record the requests that actually started; absence keeps the
    // legacy three-seat reading. No other logical move owns several requests.
    ...(agenda ? { agenda } : {}),
    ...(o.kind === 'panel' && Number.isInteger(modelPasses) && modelPasses >= 1 && modelPasses <= 3
      ? { modelPasses }
      : {}),
    ...(text(o.note, 300) ? { note: text(o.note, 300) } : {}),
    // Only a source check consults the judge; a number on any other move is a
    // hand edit and is dropped with the same silence as an unknown field.
    ...(o.kind === 'verify' && judge ? { judge } : {}),
  }
}

function asUntil(v: unknown): UntilCondition | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  if (typeof o.kind !== 'string' || !UNTIL_KIND_OK.has(o.kind)) return undefined
  const count = Math.trunc(Number(o.n))
  if (!Number.isFinite(count) || count < 1) return undefined
  if (o.kind === 'rung') {
    // A rung condition naming no rung is a condition the account could not
    // describe — the same rule as a missing kind, and dropped the same way.
    if (typeof o.rung !== 'string' || !(BRIDGE_RUNGS as readonly string[]).includes(o.rung)) {
      return undefined
    }
    return { kind: 'rung', n: count, rung: o.rung as BridgeRung }
  }
  return { kind: o.kind as UntilCondition['kind'], n: count }
}

/**
 * A programme off disk. Dropped whole when it names no model or keeps no step:
 * the list is the whole content of the record, and a run that walked an empty
 * list is not a programme run.
 */
function asProgramme(v: unknown, now: number): Programme | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const model = asModelStamp(o.model)
  if (!model) return undefined
  const steps = (Array.isArray(o.steps) ? o.steps : [])
    .map((raw): ProgrammeStep | null => {
      if (!raw || typeof raw !== 'object') return null
      const r = raw as Record<string, unknown>
      const nodeId = text(r.nodeId, 60)
      if (!nodeId || typeof r.verb !== 'string' || !PROGRAMME_VERB_OK.has(r.verb)) return null
      return { nodeId, verb: r.verb as ProgrammeStep['verb'], why: text(r.why, 300) }
    })
    .filter((x): x is ProgrammeStep => !!x)
    .slice(0, MAX_PROGRAMME_STEPS)
  if (!steps.length) return undefined
  return { steps, model, proposedAt: Math.min(Number(o.proposedAt) || now, now) }
}

/** Typed edges off disk: ids that exist in this graph, are not the node's own,
 *  deduplicated and capped. Absent rather than empty when nothing survives. */
function asEdges(
  v: unknown,
  self: string,
  // Presence only, so a SemanticNode set satisfies it as readily as a live one.
  // `admitSemanticEdges` reconciles a snapshot against its own nodes through
  // this same function, and those are not `ReasonNode`s yet.
  nodes: Record<string, unknown>,
): string[] | undefined {
  const out: string[] = []
  for (const id of strings(v, MAX_EDGES, 60)) {
    if (id === self || !ownEntry(nodes, id) || out.includes(id)) continue
    out.push(id)
  }
  return out.length ? out : undefined
}

/** A run's stored ending tally, keeping only the endings a model can reason to. */
function asEndingCounts(
  o: Record<string, unknown>,
  n: (x: unknown) => number,
): ReasonedEndingCounts {
  const out: ReasonedEndingCounts = {}
  for (const e of ['objection-stood', 'path-failed', 'open'] as const) {
    if (o[e] !== undefined) out[e] = n(o[e])
  }
  return out
}

function asRun(v: unknown, now: number): Run | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const agendaRow =
    o.agenda && typeof o.agenda === 'object' ? (o.agenda as Record<string, unknown>) : null
  const agendaIds = agendaRow?.stepIds
  const agenda =
    Array.isArray(agendaIds) &&
    agendaIds.length <= 64 &&
    agendaIds.every((id) => typeof id === 'string' && id.length > 0 && id.length <= 200) &&
    new Set(agendaIds).size === agendaIds.length
      ? { stepIds: agendaIds as string[] }
      : undefined
  const id = text(o.id, 60)
  const model = asModelStamp(o.model)
  // A run that names no model did not run. The one required stamp outside
  // `Verification`, and required for the same reason.
  if (!id || !model) return null
  const b = (o.budget ?? {}) as Record<string, unknown>
  const op = (o.open ?? {}) as Record<string, unknown>
  const sb = (o.spendBefore ?? {}) as Record<string, unknown>
  const ep =
    o.evidencePolicy && typeof o.evidencePolicy === 'object'
      ? (o.evidencePolicy as Record<string, unknown>)
      : null
  const frozenProfile = parseFrozenEvidenceProfile(ep?.profile)
  const n = (x: unknown) => Math.trunc(Math.max(0, Number(x) || 0))
  // Present-or-absent, like `auditVerdicts`: absent means no judge was
  // configured, and a block with no stamp is not the account the loop writes.
  const jr = o.judge && typeof o.judge === 'object' ? (o.judge as Record<string, unknown>) : null
  const judgeModel = jr ? asModelStamp(jr.model) : undefined
  return {
    id,
    // Clamped FORWARD to now rather than backwards: these drive no animation, and
    // a node's `createdAt` is compared against them — the `recordedAt` rule, not
    // the `birth` rule.
    startedAt: Math.min(Number(o.startedAt) || now, now),
    ...(Number(o.finishedAt) ? { finishedAt: Math.min(Number(o.finishedAt), now) } : {}),
    ...(typeof o.stop === 'string' && RUN_STOP_OK.has(o.stop) ? { stop: o.stop as RunStop } : {}),
    budget: {
      moves: n(b.moves),
      outputTokens: n(b.outputTokens),
      // Present-or-absent, like `panels` below: a run recorded under the old
      // whole-run clock keeps the limit it actually ran under, and a newer run
      // never had one — flooring the absence to 0 would state a limit of nothing.
      ...(b.ms !== undefined ? { ms: n(b.ms) } : {}),
      nodes: n(b.nodes),
      checks: n(b.checks),
      // No SCHEMA bump for this one, and the reason is that the absence carries
      // the same meaning it will always carry: a run stored before ceilings
      // existed had no ceiling. A migration would have to invent a number to fill
      // in, and every number including zero would be a claim about a run nobody
      // set a limit on. `n()` is deliberately not used — it floors to 0, and 0
      // would read as a ceiling of nothing rather than as no ceiling at all.
      ...(Number.isFinite(Number(b.usd)) && Number(b.usd) > 0 ? { usd: Number(b.usd) } : {}),
      // Present-or-absent, the way `scanned` below is read and NOT the way `usd`
      // above is. The distinction is real: a ceiling of zero is not a ceiling, so
      // `usd` has to reject 0, whereas a run genuinely allowed no panels is a run
      // whose budget said zero — and a reader is owed the difference between "this
      // run was offered none" and "this build could not offer any".
      ...(b.panels !== undefined ? { panels: n(b.panels) } : {}),
      ...(b.audits !== undefined ? { audits: n(b.audits) } : {}),
      ...(b.conjectures !== undefined ? { conjectures: n(b.conjectures) } : {}),
      ...(asUntil(b.until) ? { until: asUntil(b.until) } : {}),
    },
    // Portfolio passes can contribute several move rows, and an agenda may use
    // every move for a check. Display-era caps lost valid accounting on reload.
    // Keep the validated rows; enclosing import byte limits remain unchanged.
    ...(agenda ? { agenda } : {}),
    moves: (Array.isArray(o.moves) ? o.moves : []).map(asRunMove).filter((m): m is RunMove => !!m),
    produced: n(o.produced),
    dead: n(o.dead),
    // Present-or-absent, like `auditVerdicts`: absent is a run stored before its
    // endings were counted apart, which the account prints as `dead` alone rather
    // than splitting it by a guess. Only the three reasoned endings, as whole
    // counts. No SCHEMA bump — an optional field read defensively.
    ...(o.endings && typeof o.endings === 'object' && !Array.isArray(o.endings)
      ? { endings: asEndingCounts(o.endings as Record<string, unknown>, n) }
      : {}),
    checkReadings: (Array.isArray(o.checkReadings) ? o.checkReadings : []).filter(
      (r): r is SourceReading => typeof r === 'string' && READING_OK.has(r),
    ),
    // Present-or-absent, because absent means no audit ran and an empty array means
    // one ran and examined nothing — two different facts, and the account says a
    // different sentence for each. Several audits can cover more claims than
    // a single audit's allowance, so their valid verdicts remain a complete list.
    ...(Array.isArray(o.auditVerdicts)
      ? {
          auditVerdicts: (o.auditVerdicts as unknown[]).filter(
            (r): r is Audit['verdict'] => typeof r === 'string' && AUDIT_VERDICT_OK.has(r),
          ),
        }
      : {}),
    ...(o.scanned !== undefined ? { scanned: n(o.scanned) } : {}),
    // Both present-or-absent: absent means an ordinary run, and neither has a
    // value that could stand in for that.
    ...(text(o.pursues, 60) ? { pursues: text(o.pursues, 60) } : {}),
    ...(asProgramme(o.programme, now) ? { programme: asProgramme(o.programme, now) } : {}),
    // Present-or-absent and whole, or not at all: a count with no threshold, or a
    // threshold of nothing, is not the account the loop writes. No SCHEMA bump —
    // an absent block is what every run that skipped nothing already is.
    ...(() => {
      const sk =
        o.checksSkipped && typeof o.checksSkipped === 'object'
          ? (o.checksSkipped as Record<string, unknown>)
          : null
      const usd = Number(sk?.usd)
      return sk && n(sk.count) > 0 && Number.isFinite(usd) && usd > 0
        ? { checksSkipped: { count: n(sk.count), usd } }
        : {}
    })(),
    // The same rule for the other reason checks go off: a count with no model, or a
    // model with nothing skipped, is not the account the loop writes (D293).
    ...(() => {
      const su =
        o.checksUnread && typeof o.checksUnread === 'object'
          ? (o.checksUnread as Record<string, unknown>)
          : null
      const model = text(su?.model, 120)
      return su && n(su.count) > 0 && model ? { checksUnread: { count: n(su.count), model } } : {}
    })(),
    declined: n(o.declined),
    open: {
      reachable: n(op.reachable),
      pastDepth: n(op.pastDepth),
      folded: n(op.folded),
      modelClosed: n(op.modelClosed),
    },
    spendBefore: {
      inputTokens: n(sb.inputTokens),
      outputTokens: n(sb.outputTokens),
      searches: n(sb.searches),
    },
    // `>= 0` rather than `> 0`, unlike the ceiling: a run that was stopped before
    // its first pass genuinely drew nothing, and zero is the true figure there. It
    // is the ABSENCE that means "no estimate exists", which is why `n()` is again
    // not used — flooring a missing value to 0 would state one.
    ...(Number.isFinite(Number(o.estimatedUsd)) && Number(o.estimatedUsd) >= 0
      ? { estimatedUsd: Number(o.estimatedUsd) }
      : {}),
    ...(jr && judgeModel
      ? {
          judge: {
            model: judgeModel,
            asked: n(jr.asked),
            failed: n(jr.failed),
            ...(text(jr.note, 300) ? { note: text(jr.note, 300) } : {}),
          },
        }
      : {}),
    ...(ep && text(ep.profileId, 120) && text(ep.profileName, 120)
      ? {
          evidencePolicy: {
            ...(frozenProfile ? { profile: frozenProfile } : {}),
            profileId: text(ep.profileId, 120)!,
            profileName: text(ep.profileName, 120)!,
            profileVersion: Math.max(1, n(ep.profileVersion)),
            maxMoves:
              ep.maxMoves === null || ep.maxMoves === undefined
                ? null
                : Math.max(1, n(ep.maxMoves)),
            maxEstimatedUsd:
              ep.maxEstimatedUsd === null || ep.maxEstimatedUsd === undefined
                ? null
                : Math.max(0, Number(ep.maxEstimatedUsd) || 0),
            maxElapsedMinutes:
              ep.maxElapsedMinutes === null || ep.maxElapsedMinutes === undefined
                ? null
                : Math.max(0, Number(ep.maxElapsedMinutes) || 0),
            minSupported:
              ep.minSupported === null || ep.minSupported === undefined
                ? null
                : Math.max(1, n(ep.minSupported)),
            minRefuted:
              ep.minRefuted === null || ep.minRefuted === undefined
                ? null
                : Math.max(1, n(ep.minRefuted)),
            stopOnDecisionCriterion: ep.stopOnDecisionCriterion === true,
          },
        }
      : {}),
    model,
  }
}

/* ── raw storage ──────────────────────────────────────────────────────── */

function get(k: string): string | null {
  try {
    return localStorage.getItem(k)
  } catch {
    return null
  }
}
function put(k: string, v: string): boolean {
  try {
    localStorage.setItem(k, v)
    return true
  } catch {
    return false
  }
}
function drop(k: string) {
  try {
    localStorage.removeItem(k)
  } catch {
    /* nothing to do */
  }
}

/* ── the session codec ────────────────────────────────────────────────── */

/**
 * Migrations, one step per version.
 *
 * A step that returns null discards the document, and `parse` returns null when a
 * step is MISSING — so a `SCHEMA` bump with no corresponding entry here throws
 * away every stored exploration on the next load, in silence.
 */
const MIGRATIONS: Record<number, (d: Record<string, unknown>) => Record<string, unknown> | null> = {
  /**
   * v1 → v2: candidate syntheses gain typed ends.
   *
   * This is emphatically NOT an identity step. Every v1 bridge is `{aId, bId}`,
   * and the v2 validator reads `{a, b}` — so without the lift below, `asBridge`
   * returns null for every stored candidate and a user's whole scan history
   * disappears with nothing on screen to explain it. Every v1 end was local by
   * construction, which is what makes the lift total.
   */
  1: (d) => {
    const s = d.session as Record<string, unknown> | undefined
    if (s && Array.isArray(s.bridges)) {
      s.bridges = s.bridges.map((raw) => {
        const b = (raw ?? {}) as Record<string, unknown>
        if (b.a && b.b) return b
        return {
          ...b,
          a: { where: 'local', nodeId: b.aId },
          b: { where: 'local', nodeId: b.bId },
        }
      })
    }
    return { ...d, v: 2 }
  },
  /**
   * v2 → v3: the ledger becomes per-model and the records learn their author.
   *
   * Emphatically NOT an identity step. A v2 document was written by a build that
   * could reach exactly one model, so the stamp below is not a guess — it is the
   * only thing that could have produced it, and it is the same inference the
   * `nv.apiKey` lift rests on. Without this, every stored check comes back
   * unattributed, `asVerification` drops it, and a paid source check is discarded
   * on load with nothing on screen to explain it.
   *
   * `Op` and `PanelCheck` are stamped only OUTSIDE the demo document: the demo
   * authors two panels and two ops by hand, and stamping them would make a
   * hand-written branch claim a pass nobody paid for. `Verification` is stamped
   * unconditionally, because the demo has none by design and permanently.
   *
   * `ReasonNode.model` is deliberately left ABSENT: a v2 document cannot tell a
   * demo node from a paid one, and the export's em dash is the honest answer.
   */
  2: (d) => {
    const s = d.session as Record<string, unknown> | undefined
    if (s) {
      const stamp = { provider: 'anthropic', id: 'claude-opus-5', name: 'Claude Opus 5' }
      const paid = s.id !== DEMO_SESSION_ID
      const inTok = Math.max(0, Math.trunc(Number(s.tokensIn) || 0))
      const outTok = Math.max(0, Math.trunc(Number(s.tokensOut) || 0))
      const searches = Math.max(0, Math.trunc(Number(s.searches) || 0))
      s.spend =
        inTok || outTok || searches
          ? [{ model: stamp, inputTokens: inTok, outputTokens: outTok, searches }]
          : []
      if (paid && Array.isArray(s.ops)) {
        s.ops = s.ops.map((o) => ({ ...(o as object), model: stamp }))
      }
      const nodes = s.nodes as Record<string, Record<string, unknown>> | undefined
      for (const n of Object.values(nodes ?? {})) {
        const v = n?.verification
        // `quotesCheckable: true` is correct and load-bearing: those checks really
        // did run on a provider that returns an opened page's own text.
        if (v && typeof v === 'object') Object.assign(v, { model: stamp, quotesCheckable: true })
        const p = n?.panel
        if (paid && p && typeof p === 'object') Object.assign(p, { model: stamp })
      }
    }
    return { ...d, v: 3 }
  },
  /**
   * v3 → v4: a session learns what has run on it unattended.
   *
   * NOT an identity step, and the difference is not cosmetic: `sanitizeSession`
   * reads `r.runs`, and without this lift a v3 document restores with `undefined`
   * where the type promises an array. `parse` returns null when a step is MISSING,
   * so the absence of this entry would discard every stored exploration on the
   * next load in silence — which is why it ships in the same commit as the bump.
   *
   * An empty array is the only honest lift. A v3 document was written by a build
   * that had no runs, so nothing in it ran on its own.
   */
  3: (d) => {
    const s = d.session as Record<string, unknown> | undefined
    if (s) s.runs = []
    return { ...d, v: 4 }
  },
  /**
   * v4 → v5: the session gains USER-AUTHORED fields — the frame and the standing
   * brief — and this step is deliberately an identity.
   *
   * The bump is protective, not transformative. A v4 document needs no lifting:
   * absence of a frame is `claim` and absence of a brief is no directives, both
   * true of everything a v4 build wrote. What the bump buys is the refusal at the
   * top of `parse` — a v4 BUILD handed a v5 document declines it whole, rather
   * than passing the session through its enumerating codec, which would strip the
   * frame and every directive and then SAVE the stripped copy. Losing a cache
   * silently is survivable; silently losing words the user wrote is not.
   */
  4: (d) => ({ ...d, v: 5 }),
  /**
   * v5 → v6: portfolio passes and compact-node elaborations become durable.
   *
   * Identity-shaped but protective. A v5 document contains neither field and
   * needs no invented value. The version boundary makes a v5 BUILD refuse a v6
   * document instead of enumerating it, stripping paid packet/elaboration
   * provenance, and saving the degraded copy.
   */
  5: (d) => ({ ...d, v: 6 }),
  /**
   * v6 → v7: an exploration learns which project it is filed under.
   *
   * Identity-shaped and protective, like `4:` and `5:`, and shipped in the same
   * edit as the bump — a missing step here discards every stored exploration on
   * the next load, in silence. A v6 document needs no lifting and no invented
   * value: it has no label, and unfiled is a TRUE state that the sidebar draws
   * as its own group. What the bump buys is the refusal at the top of `parse`, so
   * that a v6 BUILD declines a v7 document whole rather than enumerating it,
   * stripping a name somebody typed, and saving the stripped copy back.
   *
   * It re-versions `Prefs` too, whose `v` is pinned to `SCHEMA` — an older build
   * refuses that blob outright and boots on the shipped defaults. Accepted: a
   * taste reset in a build the user has already moved off, against the silent
   * loss of words they wrote, which is the trade this whole entry exists for.
   */
  6: (d) => ({ ...d, v: 7 }),
  /**
   * v7 → v8: source checks become an append-only evidence ledger.
   *
   * Identity-shaped and protective. A v7 node's one `verification` is already
   * the truthful head of a one-event history, so inventing a second copy during
   * migration would waste storage and create two latest records. The version
   * boundary makes a v7 build refuse a v8 document instead of enumerating it,
   * stripping every earlier check, and saving the lossy copy back.
   */
  7: (d) => ({ ...d, v: 8 }),
  /**
   * v8 → v9: checks survive removal of the claim card that used to own them.
   *
   * Existing documents have no removed-node rows to recover, so the migration
   * is identity-shaped. The boundary is still required: a v8 build would read a
   * v9 document, enumerate the session, and silently strip its evidence archive.
   */
  8: (d) => ({ ...d, v: 9 }),
  /**
   * v9 → v10: the exploration gains a durable research workspace.
   *
   * Existing documents have no profile, genealogy decisions, structured tests,
   * semantic history or audit receipts to recover, so this lift is identity-
   * shaped. The boundary is still required: a v9 build enumerates Session and
   * would silently strip all of those user-authored records on its next save.
   */
  9: (d) => ({ ...d, v: 10 }),
  /**
   * v10 → v11: future operations retain exact physical model-call receipts.
   *
   * Historical operations have no exact prompts or request parameters to lift,
   * so this is intentionally an identity migration. The boundary is protective:
   * a v10 build would enumerate an op, strip these paid provenance records and
   * save the lossy copy back.
   */
  10: (d) => ({ ...d, v: 11 }),
  // Protect paid contracts, scoped reviews and narration receipts from older
  // codecs that would strip them. Historical records remain unchanged.
  11: (d) => ({ ...d, v: 12 }),
  // Older readers clip argument detail and source passages, and discard the
  // associated loss notices. No missing historical suffix can be recovered.
  12: (d) => ({ ...d, v: 13 }),
  // Reviews retain supplied artifacts and paid attempt identities. A v13
  // reader would drop this ledger when enumerating the research workspace.
  13: (d) => ({ ...d, v: 14 }),
  /**
   * v14 → v15: a prior-art entry names a passage by `passageRef` — its document
   * and its document-level number — instead of the call-local `p3` (D146).
   *
   * Identity-shaped and protective. A v14 document needs no lift here, because
   * `asVerification` is where a legacy `p3` is decided, on every read. What the
   * bump buys is the refusal at the top of `readStoredSessionJson`: a v14 build
   * rebuilds its admitted refs as `p<document-level number>`, finds no new-form
   * ref among them, and would strip every passage prior-art entry — and the
   * `measuredNovelty` that rests on it, a paid check's one measurement — then
   * save the stripped copy back.
   */
  14: (d) => ({ ...d, v: 15 }),
  /**
   * v15 → v16: typed edges, resolves-by dates, index linkages, pair bases and
   * programmes.
   *
   * Nothing to lift — no earlier document carries any of them — so identity-
   * shaped, and the boundary is protective for the reason v8's was: `dependsOn`,
   * `attacks` and `resolvesBy` are things a person DREW or TYPED, and a v15 build
   * enumerating a node would strip all three and save the lossy copy back.
   */
  15: (d) => ({ ...d, v: 16 }),
}

/**
 * Is this rebuild record PROVEN by the operations the document retains?
 *
 * Lifted out of `sanitizeSession` unchanged so that the import door can ask the
 * same question rather than a looser cousin of it — the rule this codebase keeps
 * learning. A rebase suppresses "needs a second look" on a claim whose sources
 * moved, which is a mechanical control state, so it is believed only where the
 * completed op it names is on this device.
 */
function rebaseIsProven(
  id: string,
  row: EvidenceRebase,
  nodes: Record<string, ReasonNode>,
  opsById: Map<string, Op[]>,
  rawOpIdCounts: Map<string, number>,
  consistentOps: Op[],
): boolean {
  const events = verificationEvents(nodes[id]!)
  // A rebuild cannot truthfully name a source record that this claim does
  // not retain. It would otherwise suppress the stale state after reload.
  const eventAtIndex =
    row.verificationEventIndex !== undefined ? events[row.verificationEventIndex] : undefined
  const eventsForOp = row.verificationOpId
    ? events.filter((event) => event.opId === row.verificationOpId)
    : []
  if (row.verificationEventIndex !== undefined && !eventAtIndex) return false
  if (row.verificationOpId && eventsForOp.length !== 1) return false
  if (eventAtIndex && row.verificationOpId && eventAtIndex.opId !== row.verificationOpId)
    return false
  const namedEvent = eventAtIndex ?? eventsForOp[0]
  if (
    namedEvent
      ? namedEvent.checkedAt !== row.verificationAt
      : !events.some((event) => event.checkedAt === row.verificationAt)
  )
    return false
  const rawCount = rawOpIdCounts.get(row.opId)
  // Unlike a compact elaboration, this row changes a mechanical control
  // state: it suppresses “needs a second look”. Without the completed op it
  // names, fail closed and let that notice return rather than accepting a
  // self-authored assertion that a rebuild happened.
  if (rawCount === undefined) return false
  const matching = opsById.get(row.opId) ?? []
  const verificationMatching = row.verificationOpId ? (opsById.get(row.verificationOpId) ?? []) : []
  const verificationRawCount = row.verificationOpId
    ? rawOpIdCounts.get(row.verificationOpId)
    : undefined
  if (row.verificationOpId && (verificationRawCount !== 1 || verificationMatching.length !== 1))
    return false
  const verificationOp = verificationMatching[0]
  const rebaseOp = matching[0]
  return (
    rawCount === 1 &&
    matching.length === 1 &&
    evidenceRebaseMatchesOp(
      id,
      row,
      rebaseOp!,
      nodes,
      namedEvent,
      verificationOp,
      verificationOp !== undefined &&
        consistentOps.indexOf(verificationOp) < consistentOps.indexOf(rebaseOp!),
    )
  )
}

/**
 * The same question, asked of a LIVE session rather than of stored bytes.
 *
 * `applySemanticSnapshot` needs it because a semantic snapshot carries rebase
 * rows and no ops: a snapshot from a colleague names operations that are not on
 * this device and never will be, while a snapshot of a LOCAL commit names ones
 * that are — and dropping both was the over-reach. Checking out a branch of your
 * own would delete a receipt this device can prove, show the repaired evidence
 * as stale again, and leave the working tree differing from the head it was just
 * restored from, so the next checkout refuses.
 *
 * The ops of a live session have already been through `asOp`, so the duplicate
 * count this takes is one per id by construction; it is passed anyway rather
 * than assumed, because the predicate is the codec's and its inputs should mean
 * what they mean there.
 */
export function admitEvidenceRebases(
  nodes: Record<string, ReasonNode>,
  ops: readonly Op[],
): Record<string, ReasonNode> {
  const opsById = new Map<string, Op[]>()
  const counts = new Map<string, number>()
  for (const op of ops) {
    const sameId = opsById.get(op.id) ?? []
    sameId.push(op)
    opsById.set(op.id, sameId)
    counts.set(op.id, (counts.get(op.id) ?? 0) + 1)
  }
  const out: Record<string, ReasonNode> = {}
  for (const [id, node] of Object.entries(nodes)) {
    if (!node.evidenceRebases) {
      out[id] = node
      continue
    }
    const kept = node.evidenceRebases.filter((row) =>
      rebaseIsProven(id, row, nodes, opsById, counts, [...ops]),
    )
    out[id] = { ...node, evidenceRebases: kept.length ? kept : undefined }
  }
  return out
}

/**
 * Where a fork came from, off disk — or undefined for anything this cannot vouch
 * for. Display only (`Session.forkedFrom`), so a record that fails any clause is
 * dropped whole rather than half-kept: a subtitle naming a step nobody branched
 * at is worse than no subtitle. No `SCHEMA` bump — absence always meant "not a
 * fork", and an older build stripping it loses only the line under a row's name.
 *
 * The date is read AS WRITTEN, like `createdAt` (`storedIdentity`): it is printed,
 * never compared with the document's event clocks, and a value that moved with
 * the reading device's clock would print a different day on two reads.
 */
function asForkOrigin(raw: unknown): ForkOrigin | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const o = raw as Record<string, unknown>
  const from = text(o.from, 80)
  const at = Number(o.at)
  if (!from || (o.kind !== 'branch' && o.kind !== 'copy') || !Number.isFinite(at) || at <= 0)
    return undefined
  // A step belongs only to a branch, and only as a whole step the rail could show.
  const step = o.kind === 'branch' && Number.isInteger(o.step) && Number(o.step) >= 1
  return { from, kind: o.kind, ...(step ? { step: Number(o.step) } : {}), at }
}

function sanitizeSession(raw: unknown, clock: StoredClock): Session | null {
  if (!raw || typeof raw !== 'object') return null
  const now = clock.now
  const r = raw as Record<string, unknown>
  const src = (r.nodes ?? {}) as Record<string, Partial<ReasonNode> | undefined>
  const order = Array.isArray(r.order)
    ? (r.order as unknown[]).filter((x): x is string => typeof x === 'string')
    : []
  if (!order.length) return null

  const settled = now - SETTLED_MS
  const nodes: Record<string, ReasonNode> = {}
  const kept: string[] = []
  let lastNodeOrdinal = -1

  // One forward pass. Safe because a parent is always inserted before its
  // children, and every path that rebuilds `order` preserves that.
  for (const id of order) {
    // An id that cannot be a key is not an id — `storableId` has the argument.
    // `nodes[id] = …` for `"__proto__"` writes nothing, so the node would join
    // `kept` and never reach the map, and its children would then fail the
    // dangling-parent test below and go too. Dropped by RULE here, where the
    // shelf's answer to a malformed record already is to drop.
    if (!storableId(id)) continue
    const n = ownEntry(src, id)
    if (!n || typeof n.label !== 'string' || typeof n.claim !== 'string') continue
    const parentId = typeof n.parentId === 'string' ? n.parentId : null
    // The same rule `addNodes` enforces: never restore a dangling edge.
    // The forward pass's whole rule is that a dangling parent is never restored,
    // and `nodes[parentId]` did restore one: `"toString"` finds a prototype
    // member and this test passed, admitting a claim whose parent is not on the
    // graph. `layout` and `taintMap` both walk down from the root, so it landed
    // where nothing draws it and nothing labels it.
    if (parentId !== null && !ownEntry(nodes, parentId)) continue
    const raw = n as Record<string, unknown>
    const retained = textRetention('persistence', raw.textTruncations, raw.textRetentionVersion)
    const retention = retained.fields()
    // Clamped, because `makeNode` clamps what lands on the node and `asOutcome`
    // falls back to this for an unreadable `confidenceBefore`. Raw, the two
    // disagreed for exactly one read: the node came back 1 and the outcome said
    // the confidence before it was 2.
    const confidence = clamp(typeof n.confidence === 'number' ? n.confidence : 0.6)
    const novelty = clamp(typeof n.novelty === 'number' ? n.novelty : 0.4)
    const storedOrdinal = nodeOrdinal(raw.createdOrdinal)
    const createdOrdinal =
      storedOrdinal !== undefined && storedOrdinal > lastNodeOrdinal
        ? storedOrdinal
        : lastNodeOrdinal + 1
    lastNodeOrdinal = createdOrdinal
    const verificationChain = asVerificationChain(
      raw.verificationHistory,
      raw.verification,
      novelty,
      clock,
    )
    nodes[id] = makeNode({
      ...n,
      id,
      parentId,
      label: n.label,
      claim: n.claim,
      textTruncations: retention.textTruncations,
      textRetentionVersion: retention.textRetentionVersion,
      compact: raw.compact === true ? true : undefined,
      elaboration: raw.compact === true ? asElaboration(raw.elaboration, now) : undefined,
      // Compact nodes defer every next step. Keeping a hand-edited base array
      // would make `nextStepsOf` expose two conflicting authored records.
      //
      // Validated HERE as well as at the import door, and that is the point:
      // `makeNode` takes this array as given, so a bound applied only to what
      // arrives through a merge is a bound reloading the page walks around —
      // invariant 2's lesson, in the one file that learned it. One validator,
      // two doors, the way `asPanel` and `asOutcome` are already called from
      // both. A stricter read of stored data needs no schema bump.
      // The same reader, for the same reason: a next step cut mid-sentence is a
      // step nobody can take, and the loss is recorded rather than swallowed.
      nextSteps: raw.compact === true ? [] : retained.list('nextSteps', raw.nextSteps),
      // The same two, at the same door's twin. `makeNode` names both and takes
      // each as given, so the `...n` spread carried whatever was on disk — the
      // rule the `terminalKind` line below has followed all along, applied to
      // the fields beside it.
      // Through the retention reader, not a hard cap: a closing argument cut at
      // 4,000 characters loses the line the argument was for, and loses it
      // silently. `retained.text` keeps the app's whole budget and RECORDS what
      // it could not keep, which is what `textTruncations` is for.
      reasoning: retained.text('reasoning', raw.reasoning),
      evidence: onlyStrings(raw.evidence),
      terminalReason: typeof raw.terminalReason === 'string' ? raw.terminalReason : undefined,
      // The epistemic fields, in the order the type declares them and
      // `makeNode` enumerates them. Each validator returns undefined for anything
      // it cannot vouch for — a half-validated record would print a reading with
      // no source behind it, which is the citation gate defeated by a hand-edited
      // localStorage key.
      //
      // Named as absent FIRST, because `...n` above has already copied the raw
      // stored record onto this object and a chain that validated nothing returns
      // `{}`. Without these two lines a check whose model stamp was deleted
      // skipped every gate in `asVerification` — a one-domain `refuted` included
      // — by being left exactly where the spread put it.
      verification: undefined,
      verificationHistory: undefined,
      ...verificationChain,
      evidenceRebases: asEvidenceRebases(raw.evidenceRebases, clock),
      // The seed's alone: `makeNode` drops it anywhere else, and reading it only
      // here means a hand-edited survey on a claim is never even validated.
      // Named absent first, for the reason the verification chain above is.
      survey: undefined,
      surveyHistory: undefined,
      ...(parentId === null ? asSurveyChain(raw.surveyHistory, raw.survey, clock) : {}),
      // Through the same gate as the import door, and for the reason the
      // `nextSteps` pair above gives: a rule only the merge applies is a rule
      // reloading the page walks around. That was literally true of this field:
      // `semanticApply` dropped a stale panel and the shelf kept it.
      panel: admittedPanel(
        asPanel(raw.panel, settled),
        Number(n.editedAt) ? storedTime(n.editedAt, clock) : undefined,
      ),
      audit: admittedAudit(
        asAudit(raw.audit, settled),
        asModelStamp(raw.model),
        Number(n.editedAt) ? storedTime(n.editedAt, clock) : undefined,
      ),
      outcome: asOutcome(raw.outcome, now, confidence),
      // A date, not a clock: deliberately NOT clamped to now. A resolves-by in
      // the past is an overdue promise, which is the resolution queue's most
      // useful row, and a future one is the normal case.
      resolvesBy:
        Number.isFinite(Number(raw.resolvesBy)) && Number(raw.resolvesBy) > 0
          ? Number(raw.resolvesBy)
          : undefined,
      // Cleared here and filled in by the second pass below: an edge may point
      // FORWARD in `order`, at a premise added after the claim that rests on it,
      // so the forward pass cannot check it against a node it has not reached.
      dependsOn: undefined,
      attacks: undefined,
      foreignFrom: asForeignRef(raw.foreignFrom, now),
      conjecture: asConjecture(raw.conjecture, clock, novelty),
      // An id and nothing else. The `...n` spread would carry any junk through,
      // and the field is what tells every rating reader to skip this node.
      carriedFrom: text(raw.carriedFrom, 60) || undefined,
      model: asModelStamp(raw.model),
      // Validated here although `makeNode` names it, because the `...n` spread
      // would otherwise carry any junk string through: the field means one thing,
      // and only on a closed path.
      terminalKind:
        raw.terminalKind === 'constraint' && n.terminal === true ? 'constraint' : undefined,
      // Validated for the same reason, and with more force: this one is read
      // straight into a coordinate. The `...n` spread would carry a hand-edited
      // 1e9 through to `radius`, and a graph whose bounds are a billion units
      // wide frames to a blank canvas with no way back short of clearing
      // storage. `clampStretch` also normalises anything below the snap to 0.
      stretch: clampStretch(Number(raw.stretch) || 0) || undefined,
      // Both index lookup tables keyed on these would render `undefined` in the
      // inspector and in an exported document.
      kind: typeof n.kind === 'string' && KIND_OK.has(n.kind) ? n.kind : 'mechanism',
      origin:
        typeof n.origin === 'string' &&
        (n.origin === 'seed' || n.origin === 'manual' || NODE_ORIGIN_OK.has(n.origin))
          ? n.origin
          : 'manual',
      // Clamped *backwards*, never rewritten to now: a future-dated birth is a
      // node the renderer has not started growing yet, so the canvas would come
      // back blank while still being clickable.
      createdAt: storedTime(n.createdAt, clock, settled),
      createdOrdinal,
      editedAt: Number(n.editedAt) ? storedTime(n.editedAt, clock) : undefined,
      // Through the same clock as `editedAt`, because the one thing it is read for
      // is which of the two came later (`reasoningPredatesRewrite`).
      reasoningEditedAt: Number(n.reasoningEditedAt)
        ? storedTime(n.reasoningEditedAt, clock)
        : undefined,
      birth: Math.min(Number(n.birth) || settled, settled),
      lastPulse: Math.min(Number(n.lastPulse) || settled, settled),
    })
    kept.push(id)
  }

  if (!kept.length) return null
  // The layout needs a root, and it takes the first parentless node in order.
  if (nodes[kept[0]!]!.parentId !== null) return null

  // The typed edges, once every node that will exist does. A node is mutated
  // here rather than rebuilt because nothing else holds it yet.
  for (const id of kept) {
    const raw = src[id] as Record<string, unknown>
    const dependsOn = asEdges(raw.dependsOn, id, nodes)
    const attacks = asEdges(raw.attacks, id, nodes)
    if (dependsOn) nodes[id]!.dependsOn = dependsOn
    if (attacks) nodes[id]!.attacks = attacks
  }
  // A build before the sweep was named stored it as a "field survey": `surveys` on
  // the session, `subjectAtSurvey` on each record and `survey` as its op's kind.
  // Read under both names, so an exploration from that build keeps its sweeps.
  const sweeps = asSweeps(r.sweeps ?? r.surveys, clock)
  const sweepOps = new Set(sweeps.map((w) => w.opId).filter((id): id is string => !!id))

  // Ordered by rung so the cap keeps the candidates that have EARNED the most,
  // and never by score — see `core/rung.ts`. Through `admitBridges` rather than
  // inline, because the import door reads through the same function and the two
  // spellings had already drifted once: whatever this one learns — the cap, the
  // rung order, one candidate per id — the merge learns at the same moment.
  const bridges = admitBridges(Array.isArray(r.bridges) ? r.bridges : [], nodes, now)
  const rawOps = Array.isArray(r.ops) ? r.ops : []
  const rawOpIdCounts = new Map<string, number>()
  for (const raw of rawOps) {
    if (!raw || typeof raw !== 'object') continue
    const id = text((raw as Record<string, unknown>).id, 60)
    if (id) rawOpIdCounts.set(id, (rawOpIdCounts.get(id) ?? 0) + 1)
  }
  // Validate cross-record packet provenance before applying the storage cap.
  // That lets a detail still be checked against an older op which is about to
  // age out, while an already-pruned id in a packet remains legal.
  const consistentOps = rawOps
    .map((o) => asOp(legacySweepOp(o, sweepOps), clock))
    .filter((o): o is Op => !!o)
    .filter((o) => portfolioMatchesNodes(o, nodes))
  const opsById = new Map<string, Op[]>()
  for (const op of consistentOps) {
    const sameId = opsById.get(op.id) ?? []
    sameId.push(op)
    opsById.set(op.id, sameId)
  }
  for (const id of kept) {
    const node = nodes[id]!
    const detail = node.elaboration
    if (!detail) continue
    const rawCount = rawOpIdCounts.get(detail.opId)
    // An op can have aged out before this document was written. In that case
    // absence is not evidence against the self-contained elaboration record.
    if (rawCount === undefined) continue
    const matching = opsById.get(detail.opId) ?? []
    if (rawCount !== 1 || matching.length !== 1 || !elaborationMatchesOp(node, matching[0]!))
      delete nodes[id]!.elaboration
  }
  for (const id of kept) {
    const node = nodes[id]!
    if (!node.evidenceRebases) continue
    const evidenceRebases = node.evidenceRebases.filter((row) =>
      rebaseIsProven(id, row, nodes, opsById, rawOpIdCounts, consistentOps),
    )
    nodes[id] = {
      ...node,
      evidenceRebases: evidenceRebases.length ? evidenceRebases : undefined,
    }
  }
  const provenanceIssues: NonNullable<Session['provenanceIssues']> = []
  const disclose = (opId: unknown, header: unknown, recorded: unknown[]) => {
    const headerModel = asModelStamp(header)
    const recordedModels = recorded
      .map(asModelStamp)
      .filter((m): m is ModelStamp => !!m)
      .filter((m, i, all) => all.findIndex((other) => sameModelStamp(m, other)) === i)
    if (
      !text(opId, 60) ||
      !headerModel ||
      !recordedModels.some((m) => !sameModelStamp(m, headerModel))
    )
      return
    if (provenanceIssues.some((issue) => issue.opId === opId)) return
    provenanceIssues.push({
      opId: text(opId, 60),
      issue: 'model-stamp-mismatch',
      headerModel,
      recordedModels: recordedModels.slice(0, 12),
    })
  }
  for (const raw of Array.isArray(r.provenanceIssues) ? r.provenanceIssues.slice(-100) : []) {
    if (
      raw &&
      typeof raw === 'object' &&
      raw.issue === 'model-stamp-mismatch' &&
      Array.isArray(raw.recordedModels)
    )
      disclose(raw.opId, raw.headerModel, raw.recordedModels)
  }
  for (const raw of rawOps) {
    if (!raw || typeof raw !== 'object') continue
    const row = raw as Record<string, unknown>
    const spend = row.spend as Record<string, unknown> | undefined
    const calls = Array.isArray(row.modelCalls) ? row.modelCalls.slice(0, 12) : []
    disclose(row.id, row.model, [
      spend?.model,
      ...calls.flatMap((c) =>
        c && typeof c === 'object' ? [c.selectedModel, c.answeredModel] : [],
      ),
    ])
  }

  const id = typeof r.id === 'string' ? r.id : `s_${Math.random().toString(36).slice(2, 10)}`
  // Read as written, NOT through `storedTime`: this is the document's identity,
  // and two reads of the same bytes have to agree on it — see `storedIdentity`.
  // The node and op clocks above keep the translation because they are compared
  // with each other inside this one read, never with this.
  const createdAt = storedIdentity(r.createdAt, settled)
  const parsedResearch = parseResearchStateSync(r.research, {
    fallbackId: `research_${id}`,
    now: createdAt,
  })
  // An oversized predecessor chain is a refused input, not an empty research
  // state. In particular, a metadata-only save must not overwrite that history
  // with the parser's fallback. Its original stored bytes remain recoverable.
  if (
    !parsedResearch.accepted &&
    parsedResearch.diagnostics.some(
      (row) =>
        (row.code === 'limit-applied' && row.path === 'knowledgeHistory.revisions') ||
        row.path === 'assessments',
    )
  )
    return null
  const research = parsedResearch.accepted
    ? pruneManualEvidenceBindingsForNodes(parsedResearch.state, new Set(kept))
    : createResearchState({ id: `research_${id}`, now: createdAt })
  const runs = (Array.isArray(r.runs) ? r.runs : [])
    .map((x) => asRun(x, now))
    .filter((x): x is Run => !!x)
  // AFTER the research, because the commits are part of what a receipt is needed
  // for. See `retainedOps`.
  const ops = retainedOps(
    consistentOps,
    nodes,
    runs,
    research.agenda,
    research.assessments,
    research.experiments,
    research.semanticCommits,
  )

  const seed = typeof r.seed === 'string' ? r.seed : (nodes[kept[0]!]!.label ?? '')
  // Through `nameFor`, the one door every writer of a name uses, so a stored name
  // that is spaces — or the seed's own words — reads back as the absence it means.
  const name = nameFor(r.name, seed)
  const forkedFrom = asForkOrigin(r.forkedFrom)
  return {
    id,
    seed,
    // Only the two values that mean something: `claim` and every unrecognised
    // value normalise to absence, which IS claim — see `Session.frame`.
    ...(r.frame === 'question' || r.frame === 'goal' ? { frame: r.frame } : {}),
    // Present only when something validated survives: an empty brief and no brief
    // are the same fact, and the prompts print neither.
    ...(asBrief(r.brief).length ? { brief: asBrief(r.brief) } : {}),
    // What the brief used to say, under the same rule: present only when some of
    // it survives.
    ...(asFormerDirectives(r.formerDirectives).length
      ? { formerDirectives: asFormerDirectives(r.formerDirectives) }
      : {}),
    // The same rule for the project label, through the ONE clamp `setProject` and
    // both fork doors also use: an empty label and no label are one fact, and only
    // absence survives a reload as itself.
    ...(projectLabel(r.project) ? { project: projectLabel(r.project) } : {}),
    ...(name ? { name } : {}),
    ...(forkedFrom ? { forkedFrom } : {}),
    // `settled`, not `now`: a document with no `createdAt` is an old document,
    // and dating it to this second makes the export's "created" line a lie.
    research,
    createdAt,
    nodes,
    order: kept,
    bridges,
    ops,
    // Present only when one validated: a session with no sweep and a session
    // with an empty list are one fact.
    ...(sweeps.length ? { sweeps } : {}),
    ...(provenanceIssues.length ? { provenanceIssues: provenanceIssues.slice(-100) } : {}),
    nextNodeOrdinal: Math.max(nodeOrdinal(r.nextNodeOrdinal) ?? 0, lastNodeOrdinal + 1),
    verificationArchive: asVerificationArchive(r.verificationArchive, clock),
    // Model ids are not a fixed-size menu: custom endpoints and later model
    // revisions can each own a spend row. Dropping the ninth loses paid usage.
    spend: (Array.isArray(r.spend) ? r.spend : []).map(asSpend).filter((x): x is Spend => !!x),
    // Run budgets, stop rules and agenda joins are durable accounting, not a
    // recent-items cache. A seventh run must not erase the first one's account.
    runs,
    // A copy opened from a link, and what that link left behind. No `SCHEMA`
    // bump: absence reads as an ordinary exploration, which is what it was.
    ...(asSharedCopy(r.shared) ? { shared: asSharedCopy(r.shared) } : {}),
  }
}

/**
 * Read one complete exploration through the same boundary used for localStorage.
 *
 * Exported for portable documents such as share links. Those documents are no
 * more trusted than bytes read off disk, so giving them a second parser would
 * eventually give one path a weaker citation or provenance gate than the other.
 */
export function readStoredSessionJson(rawText: string): StoredSession | null {
  let d: Record<string, unknown>
  try {
    d = JSON.parse(rawText) as Record<string, unknown>
  } catch {
    return null
  }
  if (!d || typeof d.v !== 'number') return null
  // A newer build wrote this. Refuse rather than guess at what changed.
  if (d.v > SCHEMA) return null
  while ((d.v as number) < SCHEMA) {
    const step = MIGRATIONS[d.v as number]
    if (!step) return null
    const next = step(d)
    if (!next) return null
    d = next
  }
  const readAt = Date.now()
  const rawSavedAt = Number(d.savedAt)
  const rawClockAtSave = Number(d.clockAtSave)
  const clockAnchor =
    Number.isFinite(rawClockAtSave) && rawClockAtSave > 0 ? rawClockAtSave : rawSavedAt
  const clock: StoredClock = {
    now: readAt,
    offset: Number.isFinite(clockAnchor) ? Math.max(0, clockAnchor - readAt) : 0,
  }
  const session = sanitizeSession(d.session, clock)
  if (!session) return null
  const ui = (d.ui ?? {}) as Record<string, unknown>
  return {
    v: SCHEMA,
    savedAt: rawSavedAt || readAt,
    // The session's event clocks have just been translated into this device's
    // coordinate — every time but `createdAt`, which is an identity and is read
    // as written; see `storedIdentity`.
    // A metadata-only rewrite may advance `savedAt` as a logical version, but it
    // must carry this independent anchor or the next read shifts every event a
    // second time.
    clockAtSave: readAt,
    session,
    ui: {
      selectedId: asId(ui.selectedId),
      focusId: asId(ui.focusId),
      lens: asLens(ui.lens),
      temper: asTemper(ui.temper),
    },
  }
}

/** Decoded consumers remain synchronous after boot hydration. This cache is a
 * working copy; only the awaited IndexedDB commit may publish durable success.
 * The writer is installed from autosave, keeping this codec out of the store's
 * runtime import cycle. */
let durableWriter: ((doc: StoredSession, keepPlace?: boolean) => Promise<boolean>) | null = null
export const durableWritesEnabled = (): boolean => durableWriter !== null
export function setDurableWriter(
  writer: ((doc: StoredSession, keepPlace?: boolean) => Promise<boolean>) | null,
): void {
  durableWriter = writer
}
/** `packed`, when given, is `packText(text)` already built by the caller — the
 * autosave's one build per queued document (see `buildOf` there). While it is
 * still being packed off the main thread the mirror holds the plain `text`, which
 * every reader passes through `unpackText` unchanged, and takes the packed form
 * when it lands, if the mirror still holds that text. */
export function cacheDurableDocument(
  doc: StoredSession,
  text = storedSessionJson(doc),
  packed: string | Promise<string> = packText(text),
): void {
  const id = doc.session.id
  noteWorkingFrom(id)
  if (typeof packed === 'string') {
    cacheDocText(id, packed, doc.savedAt)
    return
  }
  cacheDocText(id, text, doc.savedAt)
  void packed.then((stored) => repackDocText(id, text, stored))
}
export function publishDurableDocument(
  doc: StoredSession,
  text: string,
  version = doc.savedAt,
  built?: string,
): boolean {
  const id = doc.session.id
  const held = readDocText(id)
  const packed = docVersion(id) === version && held !== null ? held : (built ?? packText(text))
  if ((docVersion(id) ?? 0) <= version) {
    noteWorkingFrom(id)
    cacheDocText(id, packed, version)
  }
  const row = summarise(doc, packed.length)
  mirrorRows.set(id, { text: packed, row })
  writeIndex([...readIndex().filter((item) => item.id !== row.id), row])
  return true
}

export function readStoredDocText(id: string): string | null {
  const raw = readDocText(id) ?? get(K.doc(id))
  return raw === null ? null : unpackText(raw)
}
export function legacyDocumentIds(): string[] {
  return legacyIndex().map((row) => row.id)
}
export function activeDocumentId(): string | null {
  return get(K.last)
}
export function removeMigratedLegacyDocument(id: string, expectedText: string): void {
  // A crash before this point leaves the original copy available for another
  // migration. Never remove bytes changed by another tab during the await.
  if (get(K.doc(id)) !== expectedText) return
  drop(K.doc(id))
  writeIndex(legacyIndex().filter((row) => row.id !== id))
}

/* ── reading ──────────────────────────────────────────────────────────── */

export function loadSession(id: string): StoredSession | null {
  // BEFORE the read, both of them. The baseline is the moment this tab's copy
  // stopped being re-read from disk, and taking it afterwards left a window
  // where a forget landing mid-read was recorded as the starting point — a tab
  // then held deleted bytes with a baseline that already said "forgotten", and
  // every check downstream saw nothing move.
  const was = markNow(id)
  noteWorkingFrom(id)
  // A forget that has ALREADY landed next door. The document was in localStorage
  // once, shared, and simply gone by now; it is in this tab's mirror today, where
  // nothing but this check removes it — so the bytes are dropped here rather than
  // handed out to a caller who would write them straight back.
  if (forgottenElsewhere(id)) {
    dropDocText(id)
    return null
  }
  const raw = readDocText(id)
  if (raw === null) return null
  // Out of the envelope first. Null here is a packed value that does not read —
  // damaged in storage, or written under an envelope this build does not know —
  // and it is handed on as the same answer the codec gives a document it cannot
  // read, because that is what it is.
  const text = unpackText(raw)
  if (text === null) return null
  // ...and asked again afterwards, because the bytes and the record that
  // invalidates them are two keys and no read spans both. A record that moved
  // while we were reading means these bytes belong to a document being deleted,
  // so they are not handed out at all: the caller sees what it would have seen a
  // moment later anyway.
  //
  // AFTER the parse, not before it. `readStoredSessionJson` is the slowest thing
  // here — a `JSON.parse` of the whole document and a walk of every node — and
  // asking before it left exactly that span uncovered, which is the same mistake
  // `writeDoc` made by asking before serialising. It is pure, so running it and
  // discarding the result costs only the work.
  const doc = readStoredSessionJson(text)
  if (markMoved(id, was)) return null
  return doc
}

/**
 * What the OPEN session's project label should become, given that some other tab
 * has just written some document. Null is "this event is not about you".
 *
 * THE TRAP THIS EXISTS FOR, because it is not obvious. A tab may only refuse to
 * file the exploration IT has open — that is `fileStored`'s guard, and it is
 * per-tab by construction, because no tab can know what another tab is holding
 * on screen. So tab B files exploration X while tab A has X open, the label
 * lands on disk correctly, and then tab A's next autosave — or merely its
 * `pagehide` flush, i.e. closing the tab — writes A's in-memory session, which
 * never carried a project, straight over it. The label is erased everywhere, in
 * silence, by a tab that did nothing but be open.
 *
 * A GUARD CANNOT CLOSE THAT; ADOPTION CAN. There is no question tab A could ask
 * before its write that would help — the write is legitimate and the document is
 * A's own. What is wrong is A's copy of one field, so the fix is to bring that
 * field forward the moment it changes underneath: `storage` fires in every OTHER
 * tab on the origin, which is exactly the audience, and it is the same channel
 * `core/forgotten.ts` uses to be timely about a forget. Every tab holding X
 * converges on the last label written for X, whichever tab wrote it, and the
 * autosave that follows agrees with disk instead of fighting it.
 *
 * ONE FIELD AND ONE DOCUMENT. Not the nodes, not the ops, not the ledger: those
 * belong to whichever tab is working in them, and a tab that adopted them would
 * throw away work nobody asked it to. The project is the one field this app lets
 * a second surface write about a document a first surface is holding.
 *
 * PURE, and it takes the open session's own label rather than reading a store it
 * must not import — so the no-op case is decided here, where it can be tested,
 * rather than in a listener that cannot be. A REMOVED key answers null and never
 * "unfiled": a document that is gone was forgotten, and `core/forgotten.ts` owns
 * that event. Bytes that do not parse answer null for the same reason absence
 * does — there is nothing to adopt, and guessing is worse than waiting.
 */
export function projectAdoption(
  e: { key: string | null; newValue: string | null },
  open: { id: string; project?: string },
): { project: string | undefined } | null {
  // The INDEX ROW, not the document: the document is on the shelf, which fires
  // no event across tabs, and the row carries the label. A null key is the whole
  // origin being cleared, and a removed index is a forget, which
  // `core/forgotten.ts` owns — neither is a label change.
  if (e.key !== K.index || e.newValue === null) return null
  let rows: unknown
  try {
    rows = JSON.parse(e.newValue)
  } catch {
    return null
  }
  if (!Array.isArray(rows)) return null
  const row = (rows as Array<{ id?: unknown; project?: unknown }>).find(
    (r) => r && typeof r === 'object' && r.id === open.id,
  )
  if (!row) return null
  // Through the ONE clamp on the way in, like every other door that can name a
  // project, so a label of spaces adopts as unfiled rather than as a blank group.
  const next = projectLabel(typeof row.project === 'string' ? row.project : undefined)
  const have = projectLabel(open.project)
  if (next === have) return null
  return { project: next ?? undefined }
}

/**
 * What the OPEN session's name should become, given that some other tab has just
 * written its index. Null is "this event is not about you".
 *
 * `projectAdoption`'s trap, for the second field a surface may write about a
 * document another surface is holding: a Rename pressed on a row in tab B, while
 * tab A has that exploration open, lands on disk through `nameStored` — and A's
 * next save, or merely A closing, wrote A's unnamed copy back over it. The same
 * answer, for the same reasons, read the same way: the index row, through the one
 * normalisation every writer of a name uses (`nameFor`, which needs the seed to
 * tell a name from the seed's own words). ONE FIELD, still — the name is a label
 * over the seed, and nothing else in the document is adopted with it.
 */
export function nameAdoption(
  e: { key: string | null; newValue: string | null },
  open: { id: string; seed: string; name?: string },
): { name: string | undefined } | null {
  if (e.key !== K.index || e.newValue === null) return null
  let rows: unknown
  try {
    rows = JSON.parse(e.newValue)
  } catch {
    return null
  }
  if (!Array.isArray(rows)) return null
  const row = (rows as Array<{ id?: unknown; name?: unknown }>).find(
    (r) => r && typeof r === 'object' && r.id === open.id,
  )
  if (!row) return null
  const next = nameFor(row.name, open.seed)
  if (next === nameFor(open.name, open.seed)) return null
  return { name: next }
}

export function loadBootSession(): StoredSession | null {
  const id = get(K.last)
  return id ? loadSession(id) : null
}

export function loadPrefs(): Prefs | null {
  const raw = get(K.prefs)
  if (!raw) return null
  try {
    const d = JSON.parse(raw) as Record<string, unknown>
    if (typeof d.v !== 'number' || d.v > SCHEMA) return null
    const tempo = Number(d.tempo)
    return {
      v: SCHEMA,
      lens: asLens(d.lens),
      temper: asTemper(d.temper),
      // A tempo with no matching <option> renders the pace select blank.
      tempo: TEMPO_OK.has(tempo) ? tempo : DEFAULT_TEMPO,
      filters: readFilters(d.filters),
      railRight: d.railRight !== false,
      rightTab: d.rightTab === 'breakthroughs' ? 'breakthroughs' : 'node',
      preferCorpus: d.preferCorpus === true,
      splashSeen: d.splashSeen === true,
      // Explicitly opt-in rather than `!== '2d'`: an unknown string has to land on
      // the flat view, which is the one every other surface is written against.
      viewMode: d.viewMode === '3d' ? '3d' : '2d',
      weighting: d.weighting === 'evidence' ? 'evidence' : 'model',
      // Explicit either way once somebody has chosen. Absent, an existing
      // workspace — one that has dismissed the splash — keeps its instruments,
      // and a fresh one starts calm. See `Prefs.instruments`.
      instruments: typeof d.instruments === 'boolean' ? d.instruments : d.splashSeen === true,
      // Absent is the view as it was drawn before the footer's switches.
      showRings: d.showRings !== false,
      dimUnrelated: d.dimUnrelated === true,
      showMinimap: d.showMinimap !== false,
      // Opt-in like `splashSeen`, and never `!== false`: absence has to read as
      // OPEN. The list is the only way to reach a stored exploration, so an
      // unrecognised value that hid it would hide the library behind a rail the
      // user never chose.
      sidebarCollapsed: d.sidebarCollapsed === true,
      // Absent reads as null — "never said" — which is what keeps a reader who
      // has not touched the handle on the stage-share default.
      threadWidth: asPaneWidth(d.threadWidth),
      sidebarWidth: asPaneWidth(d.sidebarWidth),
      // Anything but an array of labels reads as nothing folded: a fold is
      // taste, and a corrupted list is a list of projects shown in full.
      foldedProjects: parseFolded(d.foldedProjects),
      // Anything but an object of times by id reads as nothing opened, which
      // is the quiet state: a corrupted map is a list with no row marked unread.
      openedAt: parseOpened(d.openedAt),
    }
  } catch {
    return null
  }
}

/**
 * The rows a reader can act on. With no shelf, only what this page holds can be
 * opened, so the rows for documents on a shelf this page could not read are
 * left out of every list — and left IN the index, for a boot that can read it.
 */
function openableRows(): RecentEntry[] {
  const rows = readIndex()
  return shelfOpen() ? rows : rows.filter((e) => readDocText(e.id) !== null)
}

export function listRecent(): RecentEntry[] {
  return openableRows().sort((a, b) => b.savedAt - a.savedAt)
}

/* ── pins ─────────────────────────────────────────────────────────────── */

/** The pinned ids, newest pin first. Read fresh on every call, like the index:
 *  two tabs must not clobber each other's list. Ids only — the sidebar joins
 *  them against the rows it has, and an id with no row draws nothing. */
export function listPinned(): string[] {
  return parsePinned(get(K.pins))
}

/**
 * Pin one exploration, or take the pin down. Both answer whether the slot now
 * says what was asked — true for a write that landed and for a state that
 * already held, false only for a write the browser refused. Idempotent, so a
 * press that arrives twice is one pin. Written synchronously: a pin is one
 * press on one row, and a debounced write that never fired would lose it
 * without saying so.
 */
export function pinSession(id: string): boolean {
  if (!id) return false
  const cur = listPinned()
  if (cur.includes(id)) return true
  return put(K.pins, serialisePinned(withPinned(cur, id)))
}

export function unpinSession(id: string): boolean {
  const cur = listPinned()
  if (!cur.includes(id)) return true
  return put(K.pins, serialisePinned(withoutPinned(cur, id)))
}

/**
 * The pool one cross-exploration scan draws from, pure over the rows it is
 * given and keeping their order — callers hand it `listRecent`'s newest-first.
 * Never the open exploration, never an empty one, and cut to `limit` AFTER the
 * filters, so a picked row is never crowded out by one the scan would have
 * refused anyway. `among` narrows to a hand-picked set of ids; absent means
 * recency decides, which is also what absent has always meant. The pick does
 * not reorder: two people ticking the same set read the same corpus in the
 * same order, whatever order they ticked it in.
 */
export function scanPool(
  entries: RecentEntry[],
  currentId: string,
  limit: number,
  among?: readonly string[],
): RecentEntry[] {
  const pick = among ? new Set(among) : null
  return entries
    .filter((e) => e.id !== currentId && e.nodes > 0 && (pick === null || pick.has(e.id)))
    .slice(0, limit)
}

/** The explorations a cross-exploration scan could read, newest first, excluding
 *  the one that is open. `among` is the user's pick — see `scanPool`. */
export function listScanCandidates(
  currentId: string,
  limit: number,
  among?: readonly string[],
): RecentEntry[] {
  return scanPool(listRecent(), currentId, limit, among)
}

/**
 * One string naming what a scan read beyond its own graph, for `ScanNote`.
 *
 * Sorted, so the same set picked in any order is the same reading — and each id
 * carries the save it was read at, because a foreign exploration saved since the
 * last scan is NEW material: "same graph, same reading" must not claim a re-scan
 * would change nothing when what changed is on the other end. One builder,
 * deliberately: the engine keys it from the digests a pass actually printed and
 * the scan control from the index rows it offers, and two copies of this join
 * would drift into a dead button or a phantom offer.
 */
export function scanPoolKey(reads: ReadonlyArray<{ id: string; savedAt: number }>): string {
  return reads
    .map((r) => `${r.id}:${r.savedAt}`)
    .sort()
    .join(' ')
}

/** One project's rows. `null` is the unfiled bucket, which is a real group and
 *  not a missing one — an exploration nobody has filed yet is a normal state. */
export interface RecentGroup {
  project: string | null
  entries: RecentEntry[]
}

/**
 * The recent list, gathered into projects.
 *
 * Named groups first, ordered by their most recent save; the unfiled bucket LAST
 * however recently it was written. That is the one rule here that is not
 * recency: unfiled is where an exploration sits before anyone has decided
 * anything about it, and a new scratch graph must not push the groups a user
 * built down the panel every time it saves. Rows inside a group are newest
 * first, which is also the order `listRecent` hands over.
 *
 * Pure, and over the index alone — it never parses a document. The sidebar
 * regroups on every save, and grouping that read the library would parse a
 * megabyte to draw a header. The demo is an
 * ordinary row here and files like any other: nothing branches on its id.
 */
export function groupRecent(entries: RecentEntry[]): RecentGroup[] {
  const byProject = new Map<string | null, RecentEntry[]>()
  for (const e of entries) {
    // Clamped on the way out too. The index is bytes off disk like everything
    // else in this file, and a hand-edited row must not open a group of its own
    // that no writer in the app could ever have produced.
    const key = projectLabel(e.project) ?? null
    const rows = byProject.get(key)
    if (rows) rows.push(e)
    else byProject.set(key, [e])
  }
  return [...byProject]
    .map(([project, rows]) => ({ project, entries: rows.sort((a, b) => b.savedAt - a.savedAt) }))
    .sort((a, b) =>
      // A group always holds at least one row, and its rows are already newest
      // first, so the head of each is the group's own recency.
      a.project === null || b.project === null
        ? Number(a.project === null) - Number(b.project === null)
        : b.entries[0]!.savedAt - a.entries[0]!.savedAt,
    )
}

/**
 * The recent list, filtered by what somebody typed.
 *
 * Matches the seed, the name somebody gave the row, and the project label, and
 * nothing else. The name is the seed's tier: it is what the row prints, so a
 * reader who named a fork will look for it by that name. This is the INDEX: a
 * full-text search over node claims would parse every stored document on every
 * keystroke, and that is a different feature with a different cost.
 *
 * Scored the way the command palette scores — an earlier match ranks higher, and
 * a seed match beats a project match, because the seed is what one row is about
 * while a label is what a whole group of rows shares. The palette expresses
 * "beats" as a fixed penalty added to the index; this splits the two into a
 * tier, because a seed is not clamped by the codec and a long one's late match
 * would otherwise outrank a project's very first character. Equal scores keep
 * the order they arrived in, which from `listRecent` is newest first.
 *
 * A blank query is the identity and returns the same array: not searching is not
 * a search that happens to match everything.
 *
 * GENERIC over the row, not because a second kind of row was anticipated but
 * because one arrived: the archive's manifest rows carry a seed and a project
 * and nothing else this function reads. Two lists answering one search
 * differently is the drift a second copy of this would produce.
 */
export function searchRecent<T extends { seed: string; project?: string; name?: string }>(
  entries: T[],
  q: string,
): T[] {
  const needle = q.trim().toLowerCase()
  if (!needle) return entries
  type Hit = { entry: T; tier: number; at: number }
  return entries
    .map((entry): Hit | null => {
      const inSeed = (typeof entry.seed === 'string' ? entry.seed : '')
        .toLowerCase()
        .indexOf(needle)
      const inName = (projectLabel(entry.name) ?? '').toLowerCase().indexOf(needle)
      // The earlier of the two, where both match: one tier, one row.
      const seed = inName < 0 ? inSeed : inSeed < 0 ? inName : Math.min(inSeed, inName)
      const project = (projectLabel(entry.project) ?? '').toLowerCase().indexOf(needle)
      if (seed < 0 && project < 0) return null
      return { entry, tier: seed >= 0 ? 0 : 1, at: seed >= 0 ? seed : project }
    })
    .filter((h): h is Hit => !!h)
    .sort((a, b) => a.tier - b.tier || a.at - b.at)
    .map((h) => h.entry)
}

/**
 * A compressed view of another exploration, for the scan prompt.
 *
 * Read from the stored document each time rather than cached in a second key.
 * A digest cache would be a second source of truth that can go stale against the
 * document it summarises — and a scan reads at most a handful of documents once, so
 * the parse is not worth a staleness class of bug.
 */
export interface DigestNode {
  id: string
  label: string
  claim: string
  kind: string
  /** Panels that disagreed are KEPT and marked: an unsettled question is good
   *  material to fuse with. Nodes a source disputed are left out entirely.
   *
   *  `panelSplit`, never the panel's mere presence: the corpus prints this as
   *  "panel disagreed", and a unanimous panel is the opposite finding. */
  contested: boolean
  /** The live check's reading and its UTC day, the two halves `readingMark`
   *  prints; absent when no check describes the claim's words now. The graph's
   *  own lines carry theirs, and a foreign line without one beside them would
   *  read as unchecked. Computed at read time and never stored, so `SCHEMA` has
   *  nothing to migrate. */
  reading?: { label: string; day: string }
}

export interface Digest {
  id: string
  seed: string
  savedAt: number
  /** Live nodes in the whole exploration, so the prompt can say what it left out. */
  live: number
  nodes: DigestNode[]
}

export function loadDigest(id: string, take: number): Digest | null {
  const doc = loadSession(id)
  if (!doc) return null
  const s = doc.session
  const live = s.order
    .map((nid) => s.nodes[nid])
    .filter(
      (n): n is ReasonNode =>
        !!n &&
        n.parentId !== null &&
        !n.terminal &&
        // A claim a source states the opposite of is not material to fuse with —
        // while the records still describe the claim. A rewrite is never re-checked,
        // so a refutation of the old words says nothing about the new ones, and
        // filtering on a stale check would hold a claim revised BECAUSE of its
        // refutation out of every scan for good.
        //
        // Through `liveChannelOf`, which is exactly this question and already asks
        // BOTH records through their own liveness rules — `outcomeLive` for the
        // outcome, `verificationLive` for the check. Spelled out here it was two raw
        // field reads, and both were wrong in the same direction: a check of wording
        // since replaced, or an outcome naming a proposition this claim no longer
        // makes, removed the live claim from every across-exploration pool.
        liveChannelOf(n) !== 'refuted',
    )
  const nodes = selectConnectionCandidates(s, live, take).map((n): DigestNode => {
    const v = verificationLive(n) ? n.verification! : null
    return {
      id: n.id,
      label: n.label,
      claim: n.claim,
      kind: n.kind as string,
      contested: panelSplit(n.panel),
      ...(v && { reading: { label: READING_LABEL[v.reading], day: recordDay(v.checkedAt) } }),
    }
  })
  return { id: s.id, seed: s.seed, savedAt: doc.savedAt, live: live.length, nodes }
}

/**
 * One record per EVENT, however many copies of the exploration the shelf holds.
 *
 * The three collectors below walk `listRecent()`, and the library is MEANT to
 * hold copies. `sharedExplorationCopy` says so at itself — _"a received graph is
 * a fork, not the sender's document id installed locally"_, so opening one shared
 * link twice is two explorations on purpose — and every other copy door lands in
 * the same place: the library's Duplicate is minted by that same function
 * (`duplicateStored`), the open exploration's Duplicate copies `nodes` by
 * reference (`duplicateSession`), and `forkAtStep` carries the steps it keeps.
 * They all leave the same NODE IDS in two documents, with the same outcome, the
 * same verification and the same panel on them, under a new session id. The
 * bridge is not one of them: a foreign end travels as a `ForeignRef` — session id,
 * seed, node id, label, claim — and the node its realisation lands is a new node a
 * model wrote, so nothing carrying a record reaches a second document that way.
 *
 * Counting those copies let a FILING ACTION manufacture evidence. `MIN_SCORED` is
 * five, so five copies of one scored session crossed the floor on a single
 * observation and then scored the model on that observation weighted five times;
 * panel counts inflated by the same multiplier, and `explorations` — a set of
 * session ids in both aggregates — counted the shelf rather than the work. A
 * number that moves when somebody presses Duplicate is not a measurement, which is
 * the whole of what the exclusions on each collector below are for. The answer is
 * at READ time and nowhere else: the copies are legitimate, they are already on
 * disk, and re-minting node ids at the copy doors would rewrite documents to fix
 * an aggregate — `duplicateStored`'s own header rules that everything in a copy
 * travels verbatim, so no verification is re-gated and no node re-stamped.
 *
 * So a record's identity is the EVENT: the node, and the millisecond its result
 * was written. A RE-RUN on a fork stamps a new millisecond and therefore counts —
 * two real passes are two observations — and only a verbatim copy collapses.
 *
 * FIRST SEEN WINS, and `better` is the single exception (see `loadCheckRecords`,
 * the one collector where two copies of one event can disagree). The surviving
 * `sessionId` is then arbitrary among the copies, which is acceptable because
 * every copy genuinely holds that node: anything that follows the id lands
 * somewhere the record really is.
 *
 * A false merge would need two DIFFERENT nodes to share an id, which `uid` makes
 * impossible within a session — `prefix_<ms base36><counter base36>` — and across
 * two sessions needs the same creation millisecond AND the same counter AND a
 * result written in the same millisecond as well. Should it ever happen the error
 * is an undercount of one, which is the direction this codebase requires of an
 * ambiguous case (`registrableHost` prices the same asymmetry): the cost lands
 * where a reader can see it — a score withheld one row longer — never as
 * inflation.
 */
function oncePerEvent<T extends { nodeId: string }>(
  rows: readonly T[],
  at: (row: T) => number,
  better?: (candidate: T, held: T) => boolean,
): T[] {
  const kept = new Map<string, T>()
  for (const row of rows) {
    const key = `${row.nodeId} ${at(row)}`
    const held = kept.get(key)
    // Replacing a held row keeps its place in the Map, so the surviving order is
    // the order the shelf was walked in.
    if (!held || better?.(row, held)) kept.set(key, row)
  }
  return [...kept.values()]
}

/**
 * One claim a model rated and the world later answered.
 *
 * The pair the whole calibration ledger is built from: what the model said its
 * confidence was BEFORE any result existed, and what was actually observed.
 */
export interface OutcomeRecord {
  sessionId: string
  nodeId: string
  label: string
  /** The model that produced the claim, and therefore the rating being graded. */
  model: ModelStamp
  /** `Outcome.confidenceBefore` — write-once, and the reason this is measurable
   *  at all. The live `confidence` would be whatever the number is now. */
  confidence: number
  bearing: Outcome['bearing']
  recordedAt: number
}

/**
 * Every scorable result across the explorations the LIBRARY holds.
 *
 * The shelf is the exclusion nobody chose, and every surface that prints these
 * numbers now says so: this walks `listRecent()`, which is the localStorage
 * index, so a document the budget moved to the archive takes its recorded results
 * out of the ledger with it and hands them back, unchanged, the moment it is
 * opened again. Reaching the archived documents would mean awaiting IndexedDB,
 * and every caller of this renders synchronously — see D33 for why that boundary
 * is where it is, and for the cost.
 *
 * Five exclusions on top of that, and each one is the difference between a
 * measurement and a number that merely looks like one:
 *
 * - **The demo.** Its recorded result was authored, not obtained, and it carries
 *   no model stamp precisely so it can never be mistaken for one. Excluded twice
 *   over — by the flag here and by the stamp requirement below — because a
 *   pre-authored outcome scoring a real model would be the exact artefact the
 *   rest of this app is built to make impossible.
 * - **Nodes with no model stamp.** The seed is the user's words, and anything
 *   stored before stamps existed cannot be attributed. A rating with no rateable
 *   author is not evidence about any model.
 * - **Nodes the user rewrote** (`origin: 'manual'`, or any `editedAt`). After a
 *   rewrite the claim is the user's and so is the number on it; grading a model
 *   against a confidence a person typed would be scoring the wrong author.
 * - **Nodes carried from a conjecture** (`carriedFrom`). The sentence is a
 *   conjecture's "departs from", written by its model; the ratings on it are
 *   placeholders nobody gave, and grading them would grade a number no model
 *   wrote.
 * - **Nothing here reads `confidence`.** Only `confidenceBefore`, which is
 *   written once and never touched by an edit — the live value moves, and a
 *   ledger built on it would grade the model against a number it never gave.
 *
 * And one row per RESULT rather than per document carrying it: the shelf holds
 * deliberate copies, so these go out through `oncePerEvent`.
 *
 * Parses every stored document, like `loadDigest` does. One per exploration in
 * the library, and this runs when someone opens the ledger, not per frame.
 */
export function loadOutcomeRecords(live?: Session): OutcomeRecord[] {
  const out: OutcomeRecord[] = []
  for (const entry of listRecent()) {
    if (entry.demo) continue
    // Read live below instead: the shelf's copy is up to a debounce behind.
    if (live && entry.id === live.id) continue
    // Written by an older build, which counted no outcomes: fall through and
    // read the document rather than trusting an absence.
    if (entry.outcomes === 0) continue
    const doc = loadSession(entry.id)
    if (!doc) continue
    out.push(...outcomeRowsOf(doc.session))
  }
  if (live && live.id !== DEMO_SESSION_ID) out.push(...outcomeRowsOf(live))
  return oncePerEvent(out, (r) => r.recordedAt)
}

/**
 * The scorable results on ONE exploration, under the exclusions above.
 *
 * Lifted out of the walk so the OPEN exploration can be read from memory. A
 * result recorded a second ago is on the node and not yet on the shelf — the
 * autosave waits out a debounce — so a line pointing at the ledger right after
 * the press, and the ledger opened from it, read the shelf and missed the one
 * result the person had just recorded. `loadOutcomeRecords(live)` puts the live
 * copy in the stored one's place; the demo's authored result is still never read.
 */
export function outcomeRowsOf(session: Session): OutcomeRecord[] {
  const out: OutcomeRecord[] = []
  for (const id of session.order) {
    const n = session.nodes[id]
    if (!n?.outcome || !n.model) continue
    if (n.origin === 'manual' || n.editedAt !== undefined) continue
    // Carried from a conjecture: the words are a model's, the ratings nobody's.
    if (n.carriedFrom !== undefined) continue
    out.push({
      sessionId: session.id,
      nodeId: n.id,
      label: n.label,
      model: n.model,
      confidence: n.outcome.confidenceBefore,
      bearing: n.outcome.bearing,
      recordedAt: n.outcome.recordedAt,
    })
  }
  return out
}

/**
 * Every conjecture with a recorded result across the explorations the LIBRARY
 * holds — the rows the conjecture track record counts (`core/trackRecord.ts`).
 *
 * The same walk, the same shelf and the same caveats as `loadOutcomeRecords`,
 * and for the same reasons: the demo is excluded, an unstamped node names no
 * model, and a document's deliberate copies are one result, not several. It does
 * NOT drop conjectures you rewrote; the track record counts those apart, as
 * yours, and says how many there were rather than silently shrinking.
 */
export function loadConjectureResults(live?: Session): ConjectureResult[] {
  const out: ConjectureResult[] = []
  for (const entry of listRecent()) {
    if (entry.demo) continue
    // Read live below instead, as `loadOutcomeRecords` does, and for its reason.
    if (live && entry.id === live.id) continue
    if (entry.outcomes === 0) continue
    const doc = loadSession(entry.id)
    if (!doc) continue
    out.push(...conjectureResultsOf(doc.session))
  }
  if (live && live.id !== DEMO_SESSION_ID) out.push(...conjectureResultsOf(live))
  return oncePerEvent(out, (r) => r.recordedAt)
}

/**
 * One claim a model rated and a source check later read on.
 *
 * The other thing that answers a rating from outside a reasoning pass. It exists
 * because `OutcomeRecord` is structurally almost always empty: recording one means
 * actually running an experiment, `MIN_SCORED` is five, and a nineteen-minute
 * exploration produces none — so the app's most distinctive honesty mechanism sat
 * dormant in every session that had not been to a lab.
 *
 * A reading is admissible for the same reason `/until 3 refuted` may end a paid run
 * while `/until novelty` is refused: it is gated on retrieved sources, and the citation
 * gate deletes every source the search did not actually return. If a reading is solid
 * enough to stop a run that costs money, it is solid enough to score a confidence
 * against.
 *
 * Kept in its own table and never merged with the recorded results — see
 * `CalibrationRow`.
 */
export interface CheckRecord {
  sessionId: string
  nodeId: string
  label: string
  /** The model that produced the claim, and therefore the rating being graded. NOT
   *  `verification.model`, which is whichever model ran the search. */
  model: ModelStamp
  confidence: number
  /** `supported` held up, `refuted` did not, and the two readings that settled
   *  nothing are counted and never scored. */
  bearing: 'supported' | 'refuted' | 'inconclusive'
  /** When the check ran. Names the newest stamp for a renamed model, like the
   *  recorded-results table. */
  recordedAt: number
}

/**
 * Which readings can grade a rating, and which are counted without grading it.
 *
 * `not-checkable` and `unsourced` are absent entirely, and that absence is the
 * `checkedAgainstSources` rule rather than a separate judgement: one means sources
 * cannot settle the claim, or that its absence stood beside a failed search (D326),
 * and the other means every citation was fabricated and deleted. Neither pass
 * checked anything, so admitting them as "counted but unscored"
 * would inflate that column with passes that never looked — the exact confusion
 * `checkedAgainstSources` exists to prevent.
 */
const READING_BEARING: Partial<Record<SourceReading, CheckRecord['bearing']>> = {
  supported: 'supported',
  refuted: 'refuted',
  // A search that came back divided, and one that came back with nothing, are both
  // results — and neither says the rating was too high or too low.
  mixed: 'inconclusive',
  'nothing-found': 'inconclusive',
}

/**
 * Every check reading that can grade a confidence, across the explorations the
 * LIBRARY holds.
 *
 * The shelf boundary ports first: this walks the same `listRecent()`, so readings
 * inside an archived exploration leave this table until it is restored, for the
 * reason `loadOutcomeRecords` states above.
 *
 * The five exclusions of `loadOutcomeRecords` port unchanged too, and for the same reasons:
 * the demo (authored, never obtained), nodes with no model stamp (no rateable author),
 * nodes the user rewrote (the claim and the number on it are theirs now), nodes carried
 * from a conjecture (ratings nobody gave), and never the live `confidence` where a
 * write-once value exists.
 *
 * That last one is the subtle case here. A source check writes only `verification`, so
 * `node.confidence` IS the model's pre-check rating — but RECORDING A RESULT overwrites
 * it with the user's own dial. So `outcome.confidenceBefore` is preferred wherever it
 * exists, which is exactly what `recordOutcome` captured it for: the model's number,
 * frozen before anything answered it.
 *
 * And that is also the one place `oncePerEvent` needs a preference. The shelf's copies
 * collapse here as they do above, but a fork can be the copy where the result was
 * recorded while another copy of the same check is not — so of two rows for one check
 * event, the one whose rating came from the frozen `confidenceBefore` wins, for the
 * reason the paragraph above gives. Which one that is cannot be read back off the
 * number, so it is tracked as the row is built.
 *
 * The OPEN exploration is read live, as `loadOutcomeRecords(live)` reads it: a
 * check that landed a second ago is on the node and not yet on the shelf.
 */
export function loadCheckRecords(live?: Session): CheckRecord[] {
  const out: CheckRecord[] = []
  const frozen = new Set<CheckRecord>()
  for (const entry of listRecent()) {
    if (entry.demo) continue
    if (live && entry.id === live.id) continue
    const doc = loadSession(entry.id)
    if (!doc) continue
    out.push(...checkRowsOf(doc.session, frozen))
  }
  if (live && live.id !== DEMO_SESSION_ID) out.push(...checkRowsOf(live, frozen))
  return oncePerEvent(
    out,
    (r) => r.recordedAt,
    (candidate, held) => frozen.has(candidate) && !frozen.has(held),
  )
}

/** The gradable readings on ONE exploration, under the exclusions above; each
 *  row whose rating came from the frozen `confidenceBefore` joins `frozen`. */
function checkRowsOf(session: Session, frozen: Set<CheckRecord>): CheckRecord[] {
  const out: CheckRecord[] = []
  for (const id of session.order) {
    const n = session.nodes[id]
    if (!n?.verification || !n.model) continue
    if (n.origin === 'manual' || n.editedAt !== undefined) continue
    if (n.carriedFrom !== undefined) continue
    const bearing = READING_BEARING[n.verification.reading]
    if (!bearing) continue
    const before = n.outcome?.confidenceBefore
    const row: CheckRecord = {
      sessionId: session.id,
      nodeId: n.id,
      label: n.label,
      model: n.model,
      confidence: before ?? n.confidence,
      bearing,
      recordedAt: n.verification.checkedAt,
    }
    // By identity, off the same expression that chose the number: two copies of
    // one event can carry the same figure by coincidence, so comparing the
    // floats would be guessing at which of them is the model's.
    if (before !== undefined) frozen.add(row)
    out.push(row)
  }
  return out
}

/**
 * One panel a model ran against itself.
 *
 * The third collector and the one that grades nothing: a panel says how far three
 * blind passes of ONE model landed apart, which is a fact about that model's own
 * steadiness and not about whether any claim is true. It is carried in its own
 * table, never joined to either calibration ledger and never handed to
 * `calibrate` — see `panelStability`, which counts these.
 *
 * Verdicts and no confidences. `PanelVote.confidence` is each pass's rating of its
 * own verdict, and pooling self-ratings is precisely the defect that got the
 * `echo` cue declined (D24). A verdict is a position, and counting positions is
 * arithmetic over records.
 */
export interface PanelRecord {
  sessionId: string
  nodeId: string
  label: string
  /** The model all three seats ran on. Off the CHECK, which stamps it once before
   *  seat one — not off the node, which names whoever reasoned the claim and may
   *  be a different model entirely. */
  model: ModelStamp
  /** How many lenses were asked. Longer than `verdicts` when a pass failed. */
  asked: number
  verdicts: PanelVerdict[]
  checkedAt: number
}

/**
 * Every panel that can be attributed, across the explorations the LIBRARY holds.
 *
 * The same shelf as the two loaders above and the same reason: `listRecent()` is
 * the localStorage index, so panels inside an archived exploration are not
 * counted until it is opened again.
 *
 * The idiom of those two as well, with the same first exclusion and a second
 * that lands in the same place for a different reason:
 *
 * - **The demo.** Its two panels were written by hand, and a hand-written
 *   disagreement counted as a model's own would be inventing a pass nobody ran.
 *   Excluded twice over, as a recorded result is: by the flag here, and by the
 *   stamp requirement below, which those two panels deliberately fail.
 * - **Panels with no model stamp.** `PanelCheck.model` is optional for exactly one
 *   case — those two demo panels — so its absence anywhere else is a document
 *   older than the stamp, and a panel that cannot name whose passes disagreed
 *   cannot say anything about a model.
 *
 * The other two exclusions of `loadOutcomeRecords` do NOT port, and the difference
 * is worth stating. They exist because a rating a person typed is not the model's
 * to be graded on — but nothing here grades a rating, and three passes over a
 * claim the user wrote are still three passes of one model. What DOES port is
 * staleness: `reviseNode` clears the panel on a rewrite, so a panel found beside a
 * later `editedAt` came off a build that did not, and it ruled on words that are
 * gone. That is the `verificationLive` rule, applied to a panel.
 *
 * One row per panel RUN rather than per document holding it, through the same
 * `oncePerEvent` as the two loaders above: three passes copied onto a second shelf
 * row are still the three passes that ran.
 *
 * The open exploration is read live, for the reason the two loaders above give.
 */
export function loadPanelRecords(live?: Session): PanelRecord[] {
  const out: PanelRecord[] = []
  for (const entry of listRecent()) {
    if (entry.demo) continue
    if (live && entry.id === live.id) continue
    const doc = loadSession(entry.id)
    if (!doc) continue
    out.push(...panelRowsOf(doc.session))
  }
  if (live && live.id !== DEMO_SESSION_ID) out.push(...panelRowsOf(live))
  return oncePerEvent(out, (r) => r.checkedAt)
}

/** The attributable panels on ONE exploration, under the exclusions above. */
function panelRowsOf(session: Session): PanelRecord[] {
  const out: PanelRecord[] = []
  for (const id of session.order) {
    const n = session.nodes[id]
    const p = n?.panel
    if (!p?.model) continue
    // `asPanel` drops a panel with no vote, so this cannot arrive off disk —
    // and a hand-edited document that carried one would be counted as a panel
    // nobody could reach rather than as the non-record it is.
    if (!p.votes.length) continue
    if (n.editedAt !== undefined && p.checkedAt < n.editedAt) continue
    out.push({
      sessionId: session.id,
      nodeId: n.id,
      label: n.label,
      model: p.model,
      asked: p.lenses.length,
      verdicts: p.votes.map((v) => v.verdict),
      checkedAt: p.checkedAt,
    })
  }
  return out
}

/**
 * The library's three tables — results, readings, steadiness — read the one way
 * every surface that prints them reads them: the shelf, with the OPEN exploration
 * read live in its stored copy's place.
 *
 * One call because three surfaces print these and they were reading two ways.
 * The Library's calibration table read the open exploration live (D197), so a
 * result recorded a second ago counted there, while the export and the
 * Overview's appendix read only the shelf, up to an autosave's debounce behind —
 * and a person who recorded a result, saw it counted in the Library, and
 * exported, got a report whose table said otherwise. Now the Library, the
 * report, the PDF, the manifest and the Overview all ask this.
 *
 * Kept as three tables, never merged: a recorded result outranks a reading, and
 * steadiness is not accuracy (`CalibrationRow`, `panelStability`).
 */
export function libraryLedger(live?: Session): {
  calibration: ModelCalibration[]
  checkCalibration: ModelCalibration[]
  stability: ModelStability[]
  trackRecord: TrackRecordRow[]
} {
  return {
    calibration: calibrate(loadOutcomeRecords(live)),
    checkCalibration: calibrate(loadCheckRecords(live)),
    stability: panelStability(loadPanelRecords(live)),
    // The fourth, and a count rather than a score (`core/trackRecord.ts`): how a
    // model's conjectures fared where somebody tested them. Never merged.
    trackRecord: trackRecord(loadConjectureResults(live)),
  }
}

/**
 * What the open exploration puts into those three tables, as one string: a memo
 * keyed on it reads the library again exactly when a result, a check or a panel
 * lands on — or leaves — the exploration on screen, and not on every edit. The
 * rows themselves, so anything they carry that the tables count moves the key.
 */
export function liveLedgerKey(live: Session): string {
  if (live.id === DEMO_SESSION_ID) return ''
  return JSON.stringify([
    outcomeRowsOf(live),
    checkRowsOf(live, new Set()),
    panelRowsOf(live),
    conjectureResultsOf(live),
  ])
}

/** One node out of a stored exploration, for realising a foreign bridge. */
export function loadForeignNode(sessionId: string, nodeId: string): ReasonNode | null {
  const doc = loadSession(sessionId)
  return doc?.session.nodes[nodeId] ?? null
}

/**
 * Forget ONE exploration, and say what became of it — ACKNOWLEDGED (D331).
 *
 * Separate from `forgetAll` because the library offers both and they are
 * different acts: one is tidying, the other is emptying.
 *
 * It resolves `forgotten` only once the shelf has committed the deletion, and the
 * library row, the pin, the closing-tab checkpoint and the boot pointer go only
 * then. It used to remove all of them first, fire the deletion without waiting
 * and return whether anything had been listed; the doors then threw the
 * transcript away and said "Forgotten." A refusal arrived later as one toast
 * that named nothing, and the exploration came back on the next boot without its
 * transcript, its pin or its checkpoint. Now a refusal leaves everything here
 * exactly as it was, and says so.
 *
 * Doors do not call this directly: `forgetExploration` in `core/forget.ts`
 * settles this tab's queued saves first, clears the canvas when it is the one
 * open, and removes the transcripts only on acceptance.
 */
export async function forgetSession(id: string): Promise<ForgetOutcome> {
  if (!shelfOpen()) {
    // The user saw only a working copy. No failed read authorizes deleting the
    // unseen native document, its legacy input, or a retained checkpoint — nor
    // its pin, which follows the document: the boot that reads the shelf again
    // brings the exploration back, and it comes back where it was. So this is
    // not a deletion and does not say it is one (`page-only`).
    const had = readDocText(id) !== null
    mirrorRows.delete(id)
    dropDocText(id)
    return { kind: 'page-only', had }
  }
  const had = readDocText(id) !== null || readIndex().some((e) => e.id === id)
  // Said DURABLY, before anything is removed. Another tab may be holding this
  // exploration in memory with an autosave pending, and removing the document
  // without recording the forget is what let that tab write it straight back —
  // index row and all — merely by being closed. See `core/forgotten.ts`.
  //
  // It stays first now that the removal can be refused, and that is a cost
  // accepted rather than missed (D331). The tombstone records the generation a
  // replayed worked example is admitted against, so the record has to exist
  // when the tombstone is written; and a record never moves backward (D39). A
  // refused deletion therefore still tells other tabs it was forgotten, when the
  // record landed — they put it down until they reload, and it is back when they
  // do — while this tab goes on working from it (`keepWorkingFrom`, which also
  // abandons a record the origin refused at quota rather than owing it).
  markForgotten(id)
  try {
    await deleteDocText(id)
  } catch (error) {
    // Kept: and a record still owed for it is a forget that did not happen.
    keepWorkingFrom(id)
    return { kind: 'kept', reason: 'storage', detail: messageOf(error) }
  }
  try {
    if (JSON.parse(get(K.ckpt) ?? 'null')?.id === id) {
      drop(K.ckpt)
      checkpointRecovery = null
    }
  } catch {
    /* Preserve unreadable checkpoint bytes. */
  }
  mirrorRows.delete(id)
  drop(K.doc(id))
  writeIndex(readIndex().filter((e) => e.id !== id))
  // Its pin goes with it: a pin is the sidebar's mark on a row, and a row that
  // is gone has no head of the list to stand at. Here rather than at each
  // Forget — the sidebar has one, the library another — so a surface that
  // forgets cannot forget to. See `core/pins.ts`.
  const pins = listPinned()
  if (pins.includes(id)) put(K.pins, serialisePinned(withoutPinned(pins, id)))
  // The record is written FIRST so no window exists where the document is gone
  // and nothing says so — but at quota that write is refused, and the row's
  // removal above is what frees the room it needed. Retried here rather than
  // swallowed: a forget nobody can see is a forget another tab will undo.
  flushForgotten()
  // The boot pointer may name the document just removed, and a pointer at a
  // deleted document makes the next load come up empty with no explanation.
  if (get(K.last) === id) drop(K.last)
  return { kind: 'forgotten', had }
}

/** What a refusal said, kept for the sentence a door prints under its own. */
const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

/**
 * What the stored explorations weigh, as stored, and how many there are.
 *
 * Exported because the library prints it. What it no longer carries is a
 * budget: there is none, and the figure to print beside this is the browser's
 * own estimate of the origin's room, from `storageEstimate` in `core/docStore.ts`.
 */
export function storageReport(): { used: number; count: number } {
  const items = openableRows()
  return { used: items.reduce((n, e) => n + e.chars, 0), count: items.length }
}

/**
 * Forget every stored exploration, and say which ones — acknowledged, as
 * `forgetSession` is (D331). On acceptance the ids are what a caller hands to
 * `forgetAllConversations`, which keeps its own keyspace and applies the same rule
 * to it; on a refusal nothing local has moved.
 */
export async function forgetAll(): Promise<ForgetAllOutcome> {
  // With no shelf, "all" is what the page holds — the rows it was shown — and
  // never the index: the rows for documents on a shelf this page could not read
  // are left for a boot that can, and so are the net, the boot pointer and the
  // counter that would invalidate every one of them. Dropping the index here
  // would make the next boot that reads the shelf sweep its documents as orphans.
  if (!shelfOpen()) {
    const shown = openableRows().map((e) => e.id)
    for (const id of shown) {
      mirrorRows.delete(id)
      dropDocText(id)
    }
    return { kind: 'page-only', ids: shown }
  }
  const all = readIndex().map((e) => e.id)
  // One counter for the door that promises everything, so explorations this tab
  // has never read are invalidated too. First, for `forgetSession`'s reasons.
  markForgotten(null)
  try {
    await clearDocs()
  } catch (error) {
    keepWorkingFrom(null)
    return { kind: 'kept', reason: 'storage', detail: messageOf(error) }
  }
  mirrorRows.clear()
  checkpointRecovery = null
  for (const e of readIndex()) drop(K.doc(e.id))
  drop(K.index)
  drop(K.last)
  drop(K.ckpt)
  // And every pin, because every id they name was just swept. Unlike the
  // declared projects — labels, which outlive the documents that carried them
  // — a pin names one exploration and is nothing once it is gone.
  drop(K.pins)
  // Same reason as `forgetSession`: everything just removed is the room the
  // record may have been refused for.
  flushForgotten()
  return { kind: 'forgotten', ids: all }
}

/* ── the index ────────────────────────────────────────────────────────── */

// Never cached in a module variable: two tabs on two explorations must not
// clobber each other's list.
function legacyIndex(): RecentEntry[] {
  const raw = get(K.index)
  if (!raw) return []
  try {
    const d = JSON.parse(raw)
    return Array.isArray(d) ? (d as RecentEntry[]).filter((e) => e && typeof e.id === 'string') : []
  } catch {
    return []
  }
}

function readIndex(): RecentEntry[] {
  const rows = new Map(legacyIndex().map((row) => [row.id, row]))
  for (const id of docIds()) {
    if (forgottenElsewhere(id)) continue
    const row = rowFromMirror(id)
    if (row && (rows.get(id)?.savedAt ?? 0) <= row.savedAt) rows.set(id, row)
  }
  return [...rows.values()]
}

function writeIndex(items: RecentEntry[]) {
  put(K.index, JSON.stringify(items))
}

/**
 * The version of `summarise`, written on every row it builds as `rowVersion`.
 *
 * Bump it whenever `summarise` changes what it writes or how it counts. A boot
 * adopts a stored row in place of decoding its document only when the row
 * carries this number (`adoptIndexRows`), so a row from before the change is
 * summarised again; without the bump, rows counted under the old rules would be
 * believed until each exploration happened to be saved again. The persistence
 * tests hold the field list to this number.
 */
export const INDEX_ROW_VERSION = 1

/**
 * A stored row this build's `summarise` wrote, re-checked field by field, or null.
 *
 * The index is localStorage — off disk, so untrusted like a document — and an
 * adopted row is handed to every list in the app in place of one summarised from
 * the bytes. So every field `summarise` writes is required in its type, every
 * optional one is either absent or well-formed, and the row is rebuilt from what
 * was checked rather than passed through.
 */
function asCurrentIndexRow(v: unknown): RecentEntry | null {
  if (!v || typeof v !== 'object') return null
  const r = v as Record<string, unknown>
  if (r.rowVersion !== INDEX_ROW_VERSION) return null
  if (typeof r.id !== 'string' || !r.id || typeof r.seed !== 'string') return null
  if (typeof r.savedAt !== 'number' || !Number.isFinite(r.savedAt)) return null
  if (r.demo !== (r.id === DEMO_SESSION_ID)) return null
  const count = (k: string): number | null => {
    const n = r[k]
    return typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 ? n : null
  }
  const nodes = count('nodes')
  const insights = count('insights')
  const contradictions = count('contradictions')
  const outcomes = count('outcomes')
  const promises = count('promises')
  const chars = count('chars')
  if (
    nodes === null ||
    insights === null ||
    contradictions === null ||
    outcomes === null ||
    promises === null ||
    chars === null
  )
    return null
  if (r.project !== undefined && typeof r.project !== 'string') return null
  if (r.name !== undefined && typeof r.name !== 'string') return null
  const forkedFrom = r.forkedFrom === undefined ? undefined : asForkOrigin(r.forkedFrom)
  if (r.forkedFrom !== undefined && !forkedFrom) return null
  const finishedRunAt = r.finishedRunAt
  if (
    finishedRunAt !== undefined &&
    (typeof finishedRunAt !== 'number' || !Number.isFinite(finishedRunAt))
  )
    return null
  if (r.shared !== undefined && r.shared !== true) return null
  return {
    id: r.id,
    seed: r.seed,
    ...(typeof r.project === 'string' ? { project: r.project } : {}),
    ...(typeof r.name === 'string' ? { name: r.name } : {}),
    ...(forkedFrom ? { forkedFrom } : {}),
    nodes,
    insights,
    contradictions,
    outcomes,
    savedAt: r.savedAt,
    chars,
    demo: r.demo,
    ...(typeof finishedRunAt === 'number' ? { finishedRunAt } : {}),
    ...(r.shared === true ? { shared: true as const } : {}),
    promises,
    rowVersion: INDEX_ROW_VERSION,
  }
}

/**
 * Take the index's own rows for the documents whose bytes they were written from,
 * so a boot summarises only what changed since the last one.
 *
 * The first `readIndex` of a page used to summarise every stored document from
 * the mirror — inflate, checksum, parse and walk the whole of each, receipts and
 * all — to rebuild rows the index already held from the save that wrote those
 * very bytes. With a library of explorations that had seen real use, that was
 * most of the time between a press of reload and the first frame (D345).
 *
 * A row is adopted only when it names the version the mirror holds and the length
 * of the exact text it holds, and when this build's `summarise` wrote it. A save
 * moves the version, so a row describing any other save of the document is passed
 * over; a version is written once (`putIfNewer`), so the bytes behind it are the
 * bytes the row was summarised from; and an earlier build's row is summarised
 * again (`INDEX_ROW_VERSION`). Everything not adopted is decoded, as before.
 */
function adoptIndexRows(): void {
  for (const raw of legacyIndex()) {
    const row = asCurrentIndexRow(raw)
    if (!row) continue
    const stored = readDocText(row.id)
    if (stored === null || stored.length !== row.chars || docVersion(row.id) !== row.savedAt)
      continue
    if (mirrorRows.get(row.id)?.text !== stored) mirrorRows.set(row.id, { text: stored, row })
  }
}

/** Change what this writes or how it counts, and bump `INDEX_ROW_VERSION`. */
function summarise(doc: StoredSession, chars: number): RecentEntry {
  let insights = 0
  let contradictions = 0
  let outcomes = 0
  let promises = 0
  for (const id of doc.session.order) {
    const n = doc.session.nodes[id]
    if (!n) continue
    // Every node, the seed among them: the due list reads every node in `order`,
    // and a date somebody wrote against the seed itself is a promise too.
    if (n.resolvesBy !== undefined) promises++
    if (n.parentId === null) continue
    // `kindChannelOf`, the same function the sidebar tallies with, so the two can
    // never disagree — and so a contradiction a source has disputed keeps being
    // counted as a contradiction rather than vanishing from the recent list.
    const ch = kindChannelOf(n)
    if (ch === 'insight') insights++
    else if (ch === 'tension') contradictions++
    if (n.outcome) outcomes++
  }
  const last = doc.session.runs[doc.session.runs.length - 1]
  return {
    id: doc.session.id,
    seed: doc.session.seed,
    // Copied off the session, where the label lives. Written as an absence when
    // there is none, so the index says exactly what the document says.
    ...(doc.session.project ? { project: doc.session.project } : {}),
    // The same for the name and the fork's origin: an absence when there is none.
    ...(doc.session.name ? { name: doc.session.name } : {}),
    ...(doc.session.forkedFrom ? { forkedFrom: doc.session.forkedFrom } : {}),
    nodes: Math.max(0, doc.session.order.length - 1),
    insights,
    contradictions,
    /** Recorded results: the one thing in a document that another pass cannot
     *  reproduce, and what every Forget door says so about. */
    outcomes,
    savedAt: doc.savedAt,
    chars,
    demo: doc.session.id === DEMO_SESSION_ID,
    // The latest run's end, when it reached one. Written as an absence
    // otherwise, like the label above, so the index says what the document says.
    ...(last?.finishedAt !== undefined ? { finishedRunAt: last.finishedAt } : {}),
    ...(doc.session.shared?.readFirst ? { shared: true as const } : {}),
    promises,
    rowVersion: INDEX_ROW_VERSION,
  }
}

/** Queue a document and expose completion separately from synchronous acceptance.
 * localStorage metadata cannot prevent a committed packed save. */
const LAYOUT_KEYS = new Set(['x', 'y', 'angle', 'radius'])

export function storedSessionJson(doc: StoredSession): string {
  // The commits too, and this is the caller that matters most: the WRITE is
  // where a receipt is actually lost, and a read can only keep what the bytes
  // still hold. See `retainedOps`.
  const ops = retainedOps(
    doc.session.ops,
    doc.session.nodes,
    doc.session.runs,
    doc.session.research?.agenda,
    doc.session.research?.assessments,
    doc.session.research?.experiments,
    doc.session.research?.semanticCommits ?? [],
  ).map((o) =>
    o.thinking && o.thinking.length > DISK_THINKING_CAP
      ? {
          ...o,
          // The ellipsis matters: without it a restored trace simply stops
          // mid-word, and the reader has no way to tell a clip from an abrupt
          // end to the model's own reasoning.
          thinking: `${o.thinking.slice(0, DISK_THINKING_CAP).trimEnd()}…`,
          thinkingClipped: true,
        }
      : o,
  )
  const out: StoredSession = {
    ...doc,
    // A freshly-authored document normally has the same wall and version clock.
    // A restored one carries its translated anchor explicitly, so a logical
    // `savedAt + 1` used by background patches cannot masquerade as clock skew.
    clockAtSave:
      Number.isFinite(doc.clockAtSave) && Number(doc.clockAtSave) > 0
        ? Number(doc.clockAtSave)
        : doc.savedAt,
    session: { ...doc.session, ops },
  }
  // `depth` is deliberately NOT dropped: a folded subtree is not walked by the
  // layout, so its descendants' depth is no longer fully derived.
  return JSON.stringify(out, (k, v) => (LAYOUT_KEYS.has(k) ? undefined : v))
}

/**
 * `keepPlace` is for a write nobody made: the version still rises past the one it
 * read, but by the caller's own stamp rather than to the wall clock. `savedAt` is
 * the library's clock as well as the shelf's version, so a repair stamped "now"
 * filed a document the reader had not touched in days above everything they had
 * (`repairDemo`).
 */
export function writeDoc(
  doc: StoredSession,
  opts: { checkpoint?: boolean; keepPlace?: boolean } = {},
): { ok: boolean; refused?: 'forgotten' | 'storage'; landed?: Promise<boolean> } {
  const id = doc.session.id
  if (forgottenElsewhere(id)) return { ok: false, refused: 'forgotten' }
  const generation = markNow(id)
  if (opts.checkpoint) writeCheckpoint(doc)
  if (durableWriter) return { ok: true, landed: durableWriter(doc, opts.keepPlace) }
  const expected = docVersion(id)
  const text = packText(storedSessionJson(doc))
  // A shelf that failed to read must not be mutated blindly. Retain the input
  // for synchronous local readers without moving its durable metadata. `ok`
  // accepts the working copy; only `landed` can report a committed save.
  if (!shelfOpen()) {
    if (markMoved(id, generation) || forgottenElsewhere(id))
      return { ok: false, refused: 'forgotten' }
    cacheDocText(id, text, doc.savedAt)
    return { ok: true, landed: Promise.resolve(false) }
  }
  const prior = readIndex().find((e) => e.id === id) ?? null
  const row = summarise(doc, text.length)
  // The index is a hint. Quota on localStorage cannot veto an IndexedDB save.
  writeIndex([...readIndex().filter((e) => e.id !== id), row])
  if (markMoved(id, generation) || forgottenElsewhere(id)) {
    dropDocText(id)
    // Only the row this attempt wrote. A replay may already have a newer row.
    writeIndex(
      legacyIndex().filter(
        (e) => !(e.id === id && e.savedAt === row.savedAt && e.chars === row.chars),
      ),
    )
    return { ok: false, refused: 'forgotten' }
  }
  const landed = writeDocText(id, text, doc.savedAt, expected)
    .then(async (kept) => {
      if (!kept || markMoved(id, generation)) {
        restoreRowFromMirror(id)
        return false
      }
      const kv = documentBackend()
      if (kv?.activate && !(await kv.activate(id, doc.savedAt))) return false
      return true
    })
    .catch(() => {
      const rest = readIndex().filter((e) => !(e.id === id && e.savedAt === row.savedAt))
      writeIndex(
        prior && !rest.some((e) => e.id === id) && docVersion(id) !== null
          ? [...rest, prior]
          : rest,
      )
      for (const fn of refusedListeners) fn(id)
      return false
    })
  return { ok: true, landed }
}

let checkpointRecovery: { doc: StoredSession; raw: string; message: string } | null = null
export function persistedCheckpointRecovery(): StoredSession | null {
  return checkpointRecovery && get(K.ckpt) === checkpointRecovery.raw
    ? checkpointRecovery.doc
    : null
}
export function checkpointRecoveryMessage(): string | null {
  return persistedCheckpointRecovery() ? checkpointRecovery!.message : null
}
export function writeCheckpoint(
  doc: StoredSession,
  baseVersion: number | null = docVersion(doc.session.id),
): boolean {
  if (!shelfOpen() || forgottenElsewhere(doc.session.id) || persistedCheckpointRecovery())
    return false
  return put(
    K.ckpt,
    JSON.stringify({
      id: doc.session.id,
      v: doc.savedAt,
      baseVersion,
      text: packText(storedSessionJson(doc)),
    }),
  )
}

const refusedListeners = new Set<(id: string) => void>()

/** Hear the shelf refuse a write. It arrives after `writeDoc` has returned `ok`,
 *  because the shelf answers asynchronously; the autosave subscribes to say so
 *  once per session, the way it always has for a full browser. */
export function onWriteRefused(fn: (id: string) => void): () => void {
  refusedListeners.add(fn)
  return () => {
    refusedListeners.delete(fn)
  }
}

/** A library row summarised from the copy the mirror holds, or null when the
 *  mirror holds nothing readable under that id. */
const mirrorRows = new Map<string, { text: string; row: RecentEntry }>()
function rowFromMirror(id: string): RecentEntry | null {
  const stored = readDocText(id)
  if (stored === null) {
    mirrorRows.delete(id)
    return null
  }
  const cached = mirrorRows.get(id)
  if (cached?.text === stored) return cached.row
  const text = unpackText(stored)
  if (text === null) return null
  const doc = readStoredSessionJson(text)
  if (!doc) return null
  const row = summarise(doc, stored.length)
  mirrorRows.set(id, { text: stored, row })
  return row
}

/** Replace a document's row with one summarised from the mirror — after another
 *  tab's newer save stood over this tab's, so the row describes the save that
 *  actually stands. */
function restoreRowFromMirror(id: string): void {
  const row = rowFromMirror(id)
  if (row) writeIndex([...readIndex().filter((e) => e.id !== id), row])
}

/** The last durable boot target survives a page that can only keep scratch work. */
export function writeLast(id: string): boolean {
  return shelfOpen() && put(K.last, id)
}

export function writePrefs(p: Prefs): void {
  put(K.prefs, JSON.stringify(p))
}

/* ── boot ─────────────────────────────────────────────────────────────── */

/** Every key on this origin under a prefix. The archive tier kept one row per
 *  document, so its manifest is only reachable as a scan. */
function keysWithPrefix(prefix: string): string[] {
  const out: string[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(prefix)) out.push(k)
    }
  } catch {
    /* nothing to scan */
  }
  return out
}

/**
 * Whether a stored document is a worked-example copy an earlier build committed
 * by accident, and nothing a person could have made or touched.
 *
 * WHERE THEY CAME FROM. The demo used to be seeded under a fresh id by
 * `startSession` and re-id'd to `s_demo` a line later. The autosave checkpoints
 * both sides of every id change, and since D84 a lone seed is an investigation
 * worth committing, so the throwaway id was committed too: a 0-node copy of the
 * demo's seed beside the real one in Recent and in the library, and one more on
 * every replay. The demo is built under its own id now; this recognises what the
 * old path left behind.
 *
 * EVERY CLAUSE FAILS TOWARD KEEPING. The copy is exactly what `startSession`
 * wrote: the seed as its only node, unedited; no op, spend, run, bridge or
 * archived check; no project, brief (current or former) or frame; and a research
 * state identical to the empty one it was born with. The root and the research are compared WHOLE
 * against what their constructors make rather than field by field, so a field
 * added to either later cannot hold somebody's words that this check does not
 * know to look at. Anything else is somebody's exploration, however small. The
 * worked example itself never matches, by id.
 */
export function isDemoPhantom(doc: StoredSession): boolean {
  const s = doc.session
  if (s.id === DEMO_SESSION_ID || s.seed !== DEMO_SEED) return false
  if (s.project !== undefined || s.frame !== undefined || s.brief !== undefined) return false
  if (s.formerDirectives !== undefined) return false
  // A name is somebody's words, and a fork's origin says a door made it on purpose.
  if (s.name !== undefined || s.forkedFrom !== undefined) return false
  if (s.ops.length || s.spend.length || s.runs.length || s.bridges.length) return false
  if (s.verificationArchive?.length || s.provenanceIssues?.length) return false
  const root = s.order.length === 1 ? s.nodes[s.order[0]!] : undefined
  if (!root || !s.research) return false
  try {
    // The seed exactly as `startSession` makes it, carrying over only what the
    // layout and the codec stamp on any node — never anything a person types.
    const asSeeded = makeNode({
      id: root.id,
      parentId: null,
      label: DEMO_SEED,
      claim: DEMO_SEED,
      reasoning: '',
      kind: 'seed',
      confidence: 0.5,
      novelty: 0.5,
      tension: 0,
      origin: 'seed',
      depth: root.depth,
      createdAt: root.createdAt,
      createdOrdinal: root.createdOrdinal,
      collapsed: root.collapsed,
      birth: root.birth,
      lastPulse: root.lastPulse,
    })
    const bornWith = createResearchState({ id: s.research.id, now: s.research.createdAt })
    return (
      canonicalSemanticJson({ ...root, x: 0, y: 0, angle: 0, radius: 0 }) ===
        canonicalSemanticJson(asSeeded) &&
      canonicalSemanticJson(s.research) === canonicalSemanticJson(bornWith)
    )
  } catch {
    return false
  }
}

/** `core/conversations.ts` owns this keyspace; it is read here and never written.
 *  A transcript is somebody's words about the document, so it keeps a copy. */
/**
 * Every exploration somebody has talked about on this profile — the device's
 * transcripts, `nv.chat.<id>`, and every account's, `nv.chatu.<account>.<id>`.
 * Literals for the reason `core/conversations.ts` keeps them: this module owns
 * neither keyspace and imports nothing from it. An account's part has its dots
 * escaped there and a session id carries none, so the id is what follows the
 * last dot. A conversation is something a reader attached from outside the
 * document, so it keeps a copy whichever reader had it — the phantom sweep read
 * only the device's shelf, and took the copy an account had been talking about.
 */
function talkedAbout(): Set<string> {
  const ids = new Set<string>()
  for (const key of keysWithPrefix('nv.chat')) {
    if (key.startsWith('nv.chat.')) ids.add(key.slice('nv.chat.'.length))
    else if (key.startsWith('nv.chatu.')) ids.add(key.slice(key.lastIndexOf('.') + 1))
  }
  return ids
}

/**
 * Take down the worked-example copies an earlier build committed by accident
 * (`isDemoPhantom`). Inside hydration, before anything lists the library.
 *
 * The legacy migration's discipline (D84), pointed the other way. The decision
 * is made on the COMMITTED bytes read back from the shelf, not on the mirror. The
 * deletion is a native tombstone committed only while the document still stands
 * at the version that was read, so a save landing in between keeps it, and a
 * stale tab still holding the copy cannot write it back. The mirror, the row and
 * the boot pointer are let go only once a second read finds the document gone.
 * A backend with no conditional tombstone sweeps nothing. What somebody attached
 * from outside the document — a pin, a conversation, the closing-tab checkpoint
 * — keeps it too.
 *
 * Never throws. A copy that could not be taken down stays where it has been all
 * along, and tidying must never be the reason hydration reports a failure.
 */
async function sweepDemoPhantoms(kv: DocBackend): Promise<void> {
  if (!kv.forgetIf) return
  const pinned = new Set(listPinned())
  const talked = talkedAbout()
  let checkpointed: unknown = null
  try {
    checkpointed = JSON.parse(get(K.ckpt) ?? 'null')?.id ?? null
  } catch {
    // Unreadable bytes may name anything; nothing is swept past them.
    return
  }
  for (const id of docIds()) {
    // The row first: it is summarised from the mirror once per boot anyway, so
    // an ordinary library costs no read of the shelf and no second decode here.
    const row = rowFromMirror(id)
    if (!row || row.demo || id === DEMO_SESSION_ID) continue
    if (row.seed !== DEMO_SEED || row.nodes !== 0 || row.project !== undefined) continue
    if (pinned.has(id) || checkpointed === id || talked.has(id)) continue
    try {
      const raw = await kv.get(id)
      if (raw === null) continue
      const held = unwrapDoc(raw)
      const text = unpackText(held.text)
      const doc = text === null ? null : readStoredSessionJson(text)
      if (!doc || doc.session.id !== id || !isDemoPhantom(doc)) continue
      if (!(await kv.forgetIf(id, held.v))) continue
      if ((await kv.get(id)) !== null) continue
      // Other tabs drop their copies on this record, as they do for a Forget.
      markForgotten(id)
      mirrorRows.delete(id)
      dropDocText(id)
      if (get(K.last) === id) drop(K.last)
    } catch {
      /* Kept. See above. */
    }
  }
}

/** Validate and migrate each legacy input before removing its original bytes.
 * Missing index metadata never licenses deleting a committed document. */
export async function hydrateStorage(): Promise<void> {
  try {
    await hydrateDocs()
    adoptIndexRows()
    const kv = documentBackend()
    if (!kv) throw new Error('The document database is unavailable')
    // Native shelf discovery is authoritative. Never delete an unindexed body.
    // The old archive markers still represent deletions and must be applied
    // before their optional manifest is retired.
    for (const key of keysWithPrefix('nv.archrm.')) {
      const id = key.slice('nv.archrm.'.length)
      if (kv.forget) await kv.forget(id)
      else await kv.del(id)
      dropDocText(id)
      drop(key)
    }
    const sweep = get('nv.archrm')
    if (sweep && kv.sweepExcept) {
      await kv.sweepExcept(sweep)
      await hydrateDocs()
      adoptIndexRows()
    }
    // Discover legacy bodies even when their index write was refused.
    const ids = new Set([
      ...legacyIndex().map((row) => row.id),
      ...keysWithPrefix('nv.s.').map((key) => key.slice(5)),
    ])
    for (const id of ids) {
      const raw = get(K.doc(id))
      if (raw === null) continue
      const text = unpackText(raw)
      const doc = text === null ? null : readStoredSessionJson(text)
      if (!doc || doc.session.id !== id || forgottenElsewhere(id)) continue
      const existing = readDocText(id)
      const current = existing === null ? null : readStoredSessionJson(unpackText(existing) ?? '')
      if (current && current.savedAt > doc.savedAt) continue
      const version = Math.max(doc.savedAt, (docVersion(id) ?? -1) + 1)
      if (!(await writeDocText(id, packText(text!), version, docVersion(id))))
        throw new Error('Legacy migration conflicted')
      if (get(K.doc(id)) === raw) drop(K.doc(id))
    }
    const ckRaw = get(K.ckpt)
    checkpointRecovery = null
    if (ckRaw) {
      const ck = JSON.parse(ckRaw) as {
        id?: unknown
        v?: unknown
        text?: unknown
        baseVersion?: unknown
      }
      if (
        typeof ck.id === 'string' &&
        typeof ck.v === 'number' &&
        typeof ck.text === 'string' &&
        !forgottenElsewhere(ck.id)
      ) {
        const text = unpackText(ck.text)
        const doc = text === null ? null : readStoredSessionJson(text)
        if (!doc || doc.session.id !== ck.id)
          throw new Error('The retained checkpoint is unreadable')
        checkpointRecovery = {
          doc,
          raw: ckRaw,
          message:
            'A closing-tab checkpoint has not been safely recovered. Export a recovery copy to inspect the retained work.',
        }
        const currentVersion = docVersion(ck.id)
        const currentRaw = readDocText(ck.id)
        const current =
          currentRaw === null ? null : readStoredSessionJson(unpackText(currentRaw) ?? '')
        const sameWork =
          current &&
          JSON.stringify(current.session) === JSON.stringify(doc.session) &&
          JSON.stringify(current.ui) === JSON.stringify(doc.ui)
        const knownBase =
          ck.baseVersion === null ||
          (typeof ck.baseVersion === 'number' && Number.isFinite(ck.baseVersion))
        if (sameWork) {
          if (get(K.ckpt) === ckRaw) drop(K.ckpt)
          checkpointRecovery = null
        } else if (
          ck.v > (currentVersion ?? -1) &&
          (knownBase ? ck.baseVersion === currentVersion : currentVersion === null)
        ) {
          // Compare the read from the original tab. Borrowing the freshly read
          // version here would let a stale closing tab overwrite newer work.
          if (!(await writeDocText(ck.id, packText(text!), ck.v, currentVersion)))
            throw new Error('Checkpoint recovery conflicted')
          if (get(K.ckpt) === ckRaw) drop(K.ckpt)
          checkpointRecovery = null
        } else {
          checkpointRecovery = {
            doc,
            raw: ckRaw,
            message:
              'A closing-tab checkpoint differs from the saved exploration. The saved work was kept; export a recovery copy to inspect the retained checkpoint.',
          }
        }
      }
    }
    // After the legacy inputs and the checkpoint have landed, so every copy is
    // judged on the shelf's own bytes; before the index is written, so a copy
    // taken down is never listed.
    await sweepDemoPhantoms(kv)
    const rows = docIds().flatMap((id) => {
      const row = rowFromMirror(id)
      return row ? [row] : []
    })
    writeIndex(rows)
    for (const key of keysWithPrefix(ARCHIVE_PREFIX)) {
      if (!key.startsWith('nv.archrm.')) drop(key)
    }
    const active = kv.active ? await kv.active() : null
    const legacyActive = get(K.last)
    if (!active && legacyActive && docVersion(legacyActive) !== null && kv.activate)
      await kv.activate(legacyActive, docVersion(legacyActive)!)
    setStorageHydrationFailure(null)
  } catch (error) {
    setStorageHydrationFailure(error)
  }
}

/**
 * How hard the announced version is looked for, and how long between looks.
 *
 * `writeDoc` puts the index row into localStorage BEFORE `writeDocText` commits
 * to the shelf — it must, because that row write is the synchronous refusal
 * check — so the storage event can reach another tab while the document itself
 * is still in flight. One look finds the old bytes, or none, and no second
 * event ever comes: the row has already been written, and the shelf is not
 * something another tab can subscribe to.
 *
 * ONE rule for both arms, which is the correction: this was added for a
 * document the mirror had never held and left the update arm on a single read,
 * so a save made next door could be missed exactly as a creation was — the
 * newer-copy case has the same race and is the more common one. `wanted` is
 * what the row announced; a look that reaches it stops, and so does a look that
 * finds something newer, because another tab's later save standing over this
 * one is the shelf working as designed rather than a read to retry.
 *
 * Bounded, because a row can also name a version that is never going to land —
 * a write refused after its row went down. Three waits of 150, 300 and 600ms,
 * and then it stops rather than watching a shelf that is not going to answer.
 */
export const NEW_DOC_LOOKS = 4
export const NEW_DOC_PAUSE_MS = 150

async function fetchDocAt(id: string, wanted: number): Promise<void> {
  for (let look = 0; look < NEW_DOC_LOOKS; look++) {
    await refreshDoc(id)
    const held = docVersion(id)
    if (held !== null && held >= wanted) return
    if (look < NEW_DOC_LOOKS - 1) {
      await new Promise((resolve) => setTimeout(resolve, NEW_DOC_PAUSE_MS * 2 ** look))
    }
  }
}

/**
 * "Has anything but THIS document changed since I last looked?"
 *
 * The cache key for every reader that derives something from the whole library
 * except the open exploration. Neither of the two obvious keys works, and both
 * were tried: `rev` moves on a pane toggle through `setViewInset`, and the
 * shelf revision moves on the open document's OWN autosave — so keying on
 * either re-read and re-inflated every stored exploration on a keystroke.
 *
 * `listRecent` reads one small localStorage row per exploration, which is what
 * makes this affordable on every render: the work it avoids is unpacking and
 * parsing every stored document. A row's `savedAt` is exactly the question.
 *
 * Lifted out of `ui/Standing.tsx`, which worked this out first and wrote the
 * argument down. The due list then made the same mistake twice — `rev` first,
 * and the shelf revision after that was fixed — with the answer sitting in a
 * sibling file the whole time. One definition now.
 */
export function shelfKeyExcept(exceptId?: string): string {
  return (
    listRecent()
      .filter((row) => row.id !== exceptId)
      // The MIRROR's version, not the index row's `savedAt`, because the mirror is
      // what a reader of these documents actually gets: `libraryDocs` walks
      // `listRecent` for the ids and `loadSession` for the content.
      //
      // The two differ for exactly one window, and it is the window that matters.
      // A cross-tab write announces the new `savedAt` on the index BEFORE
      // IndexedDB commits, so the first `refreshDoc` can re-announce the old
      // bytes — and a key taken from the announcement then caches rows parsed from
      // the OLD document under the NEW version. When the retry installs what was
      // announced, the subscription renders again and the key has not moved, so
      // the other tab's dates and outcomes stay stale until an unrelated index
      // change. Keyed on what the mirror holds, the retry is what moves it.
      //
      // The id list still comes from the INDEX so an exploration announced but not
      // yet fetched is counted at all; `docVersion` answers null for it and the
      // key moves again when the fetch lands. Both halves are needed: the ids a
      // write can add, and the version a write can swap.
      .map((row) => `${row.id}@${docVersion(row.id) ?? 0}`)
      .sort()
      .join('|')
  )
}

/**
 * The cross-tab half of the shelf. A tab learns that another tab saved through
 * the index row it wrote — `storage` events fire for localStorage keys, and not
 * for IndexedDB — and refreshes the copies it holds that the rows say are older.
 * Called from the autosave's `storage` listener with the event it received.
 */
export function refreshFromIndexEvent(e: { key: string | null; newValue: string | null }): void {
  if (e.key !== K.index || e.newValue === null) return
  let rows: unknown
  try {
    rows = JSON.parse(e.newValue)
  } catch {
    return
  }
  if (!Array.isArray(rows)) return
  for (const r of rows as Array<{ id?: unknown; savedAt?: unknown }>) {
    if (!r || typeof r.id !== 'string' || typeof r.savedAt !== 'number') continue
    const held = docVersion(r.id)
    // Two arms of ONE job: hold what this row announces. It used to ask only
    // "is the copy I hold stale", so an exploration CREATED next door was never
    // fetched at all — `listRecent` showed its row, `loadSession` answered null
    // for it forever, and its claims stayed out of every library reader until a
    // reload ran `hydrateStorage`, with no `useDocs` notification able to repair
    // it because nothing was started to announce. Both arms now go through the
    // same bounded wait, for the reason `fetchDocAt` gives: the race that loses
    // a creation loses an update the same way.
    //
    // Caught, because `refreshDoc` under it now throws where the shelf is
    // unreadable, and an announcement is not worth an unhandled rejection.
    if (held === null || r.savedAt > held) void fetchDocAt(r.id, r.savedAt).catch(() => undefined)
  }
}
